import moment from "moment";
import queryString from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsForClient,
  getSubmission,
  handleAddFieldTableRow,
  handleDeleteFieldTableRow,
  handleEditFieldTableRow,
  handleFieldTableChange,
  handleFormChange,
  handleSaveFieldTableRow,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldValue,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import {
  FieldTable,
  FormCheckbox,
  FormInput,
  FormRadioButton,
  FormSelect,
  FormTextArea,
  PhoneInput,
  ToggleSwitch,
} from "../common/ControlBundle";
import ExpandDiv from "../common/ExpandDiv";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getSchema } from "./joi/validation";

function KYHFPSD() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  const handleRowChange = (e, index, fieldname) => {
    const rowObj = handleFieldTableChange(data, e, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleSaveRow = (index, fieldname) => {
    const rowObj = handleSaveFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleDeleteRow = (index, fieldname) => {
    const rowObj = handleDeleteFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleEditRow = (index, fieldname) => {
    const rowObj = handleEditFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleAddRow = (fieldname, newRow) => {
    const rowObj = handleAddFieldTableRow(data, fieldname, newRow);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setExpandStatus(true);
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [expandStatus, setExpandStatus] = useState(false);
  const [showNarrative, setShowNarrative] = useState("Hide");

  const handleExpandStatusChange = (overrideValue, callback) => {
    if (overrideValue !== undefined) {
      setExpandStatus(overrideValue);
      if (callback) {
        window.setTimeout(callback, 5);
      }
    } else setExpandStatus(!expandStatus);
  };

  const preSignForm = () => {
    const obj = { ...data };

    const familyHistoryDetails = JSON.parse(obj.FamilyHistoryDetails["@value"]);

    if (familyHistoryDetails)
      obj.FamilyHistoryDetails["@value"] = JSON.stringify(
        familyHistoryDetails.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const cPSHistory = JSON.parse(obj.CPSHistory["@value"]);

    if (cPSHistory)
      obj.CPSHistory["@value"] = JSON.stringify(
        cPSHistory.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const legalHistory = JSON.parse(obj.LegalHistory["@value"]);

    if (legalHistory)
      obj.LegalHistory["@value"] = JSON.stringify(
        legalHistory.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const currentMedications = JSON.parse(obj.CurrentMedications["@value"]);

    if (currentMedications)
      obj.CurrentMedications["@value"] = JSON.stringify(
        currentMedications.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const mHTreatmentHistory = JSON.parse(obj.MHTreatmentHistory["@value"]);

    if (mHTreatmentHistory)
      obj.MHTreatmentHistory["@value"] = JSON.stringify(
        mHTreatmentHistory.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const sUHistory = JSON.parse(obj.SUHistory["@value"]);

    if (sUHistory)
      obj.SUHistory["@value"] = JSON.stringify(
        sUHistory.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const sUTreatmentHistory = JSON.parse(obj.SUTreatmentHistory["@value"]);

    if (sUTreatmentHistory)
      obj.SUTreatmentHistory["@value"] = JSON.stringify(
        sUTreatmentHistory.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const traumaHistory = JSON.parse(obj.TraumaHistory["@value"]);

    if (traumaHistory)
      obj.TraumaHistory["@value"] = JSON.stringify(
        traumaHistory.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const diagnosisDetails = JSON.parse(obj.DiagnosisDetails["@value"]);

    if (diagnosisDetails)
      obj.DiagnosisDetails["@value"] = JSON.stringify(
        diagnosisDetails.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const additionalNeeds = JSON.parse(obj.AdditionalNeeds["@value"]);

    if (additionalNeeds)
      obj.AdditionalNeeds["@value"] = JSON.stringify(
        additionalNeeds.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const treatmentRecommendations = JSON.parse(
      obj.TreatmentRecommendations["@value"]
    );

    if (treatmentRecommendations)
      obj.TreatmentRecommendations["@value"] = JSON.stringify(
        treatmentRecommendations.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    setData(obj);
  };

  const handleEducationStatusChange = (e) => {
    handleChange(e);
    if (e.currentTarget.value === "0") {
      const employedInput = {
        currentTarget: {
          type: "radio",
          name: "CurrentlyEmployed",
          value: "0",
        },
      };
      handleChange(employedInput);

      const militaryInput = {
        currentTarget: {
          type: "radio",
          name: "MilitaryHistory",
          value: "0",
        },
      };
      handleChange(militaryInput);
    }
  };

  const handleTraumaSymptomsChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const TraumaticExposureInput = {
        currentTarget: {
          type: "checkbox",
          name: "TraumaticExposure",
          checked: false,
        },
      };
      handleChange(TraumaticExposureInput);

      const AvoidantTraumaInput = {
        currentTarget: {
          type: "checkbox",
          name: "AvoidantTrauma",
          checked: false,
        },
      };
      handleChange(AvoidantTraumaInput);

      const HypervigilanceTraumaInput = {
        currentTarget: {
          type: "checkbox",
          name: "HypervigilanceTrauma",
          checked: false,
        },
      };
      handleChange(HypervigilanceTraumaInput);

      const IntrusiveDreamsInput = {
        currentTarget: {
          type: "checkbox",
          name: "IntrusiveDreams",
          checked: false,
        },
      };
      handleChange(IntrusiveDreamsInput);

      const NegativeThoughtsInput = {
        currentTarget: {
          type: "checkbox",
          name: "NegativeThoughts",
          checked: false,
        },
      };
      handleChange(NegativeThoughtsInput);

      const CocentrationProblemsInput = {
        currentTarget: {
          type: "checkbox",
          name: "CocentrationProblems",
          checked: false,
        },
      };
      handleChange(CocentrationProblemsInput);

      const RecklessBehaviorTraumaInput = {
        currentTarget: {
          type: "checkbox",
          name: "RecklessBehaviorTrauma",
          checked: false,
        },
      };
      handleChange(RecklessBehaviorTraumaInput);

      const SleepDisturbancesTraumaInput = {
        currentTarget: {
          type: "checkbox",
          name: "SleepDisturbancesTrauma",
          checked: false,
        },
      };
      handleChange(SleepDisturbancesTraumaInput);

      const OtherTraumaInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherTrauma",
          checked: false,
        },
      };
      handleChange(OtherTraumaInput);

      const OtherTraumaDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherTraumaDetails",
          value: "",
        },
      };
      handleChange(OtherTraumaDetailsInput);

      const TraumaSymptomsFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "TraumaSymptomsFrequency",
          value: "",
        },
      };
      handleChange(TraumaSymptomsFrequencyInput);

      const TraumaSymptomsIntensityInput = {
        currentTarget: {
          type: "input",
          name: "TraumaSymptomsIntensity",
          value: "",
        },
      };
      handleChange(TraumaSymptomsIntensityInput);

      const TraumaSymptomsDurationInput = {
        currentTarget: {
          type: "input",
          name: "TraumaSymptomsDuration",
          value: "",
        },
      };
      handleChange(TraumaSymptomsDurationInput);

      const TraumaSymptomsOnsetInput = {
        currentTarget: {
          type: "input",
          name: "TraumaSymptomsOnset",
          value: "",
        },
      };
      handleChange(TraumaSymptomsOnsetInput);
    }
  };

  const handleAnxietyDisorderChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const AnxietyDisorderExcessWorryInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyDisorderExcessWorry",
          checked: false,
        },
      };
      handleChange(AnxietyDisorderExcessWorryInput);

      const AnxietyDisorderEasilyFatiguedInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyDisorderEasilyFatigued",
          checked: false,
        },
      };
      handleChange(AnxietyDisorderEasilyFatiguedInput);

      const MuscleTensionInput = {
        currentTarget: {
          type: "checkbox",
          name: "MuscleTension",
          checked: false,
        },
      };
      handleChange(MuscleTensionInput);

      const OverworriedInput = {
        currentTarget: {
          type: "checkbox",
          name: "Overworried",
          checked: false,
        },
      };
      handleChange(OverworriedInput);

      const AnxietyDisorderDifficultyConcentratingInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyDisorderDifficultyConcentrating",
          checked: false,
        },
      };
      handleChange(AnxietyDisorderDifficultyConcentratingInput);

      const SleepDisturbancesAnxietyInput = {
        currentTarget: {
          type: "checkbox",
          name: "SleepDisturbancesAnxiety",
          checked: false,
        },
      };
      handleChange(SleepDisturbancesAnxietyInput);

      const RestlessAnxietyInput = {
        currentTarget: {
          type: "checkbox",
          name: "RestlessAnxiety",
          checked: false,
        },
      };
      handleChange(RestlessAnxietyInput);

      const IrritabilityInput = {
        currentTarget: {
          type: "checkbox",
          name: "Irritability",
          checked: false,
        },
      };
      handleChange(IrritabilityInput);

      const OtherAnxietyInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherAnxiety",
          checked: false,
        },
      };
      handleChange(OtherAnxietyInput);

      const OtherAnxietyDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherAnxietyDetails",
          value: "",
        },
      };
      handleChange(OtherAnxietyDetailsInput);

      const AnxietyFrequencyInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyFrequency",
          checked: false,
        },
      };
      handleChange(AnxietyFrequencyInput);

      const AnxietyIntensityInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyIntensity",
          checked: false,
        },
      };
      handleChange(AnxietyIntensityInput);

      const AnxietyDurationInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyDuration",
          checked: false,
        },
      };
      handleChange(AnxietyDurationInput);
    }
  };

  const handleDepressionSymptomsChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const DepressedMoodInput = {
        currentTarget: {
          type: "checkbox",
          name: "DepressedMood",
          checked: false,
        },
      };
      handleChange(DepressedMoodInput);

      const SleepChangesInput = {
        currentTarget: {
          type: "checkbox",
          name: "SleepChanges",
          checked: false,
        },
      };
      handleChange(SleepChangesInput);

      const GuiltInput = {
        currentTarget: {
          type: "checkbox",
          name: "Guilt",
          checked: false,
        },
      };
      handleChange(GuiltInput);

      const DiminishedInterestInput = {
        currentTarget: {
          type: "checkbox",
          name: "DiminishedInterest",
          checked: false,
        },
      };
      handleChange(DiminishedInterestInput);

      const PurposelessMovementInput = {
        currentTarget: {
          type: "checkbox",
          name: "PurposelessMovement",
          checked: false,
        },
      };
      handleChange(PurposelessMovementInput);

      const WeightChangesInput = {
        currentTarget: {
          type: "checkbox",
          name: "WeightChanges",
          checked: false,
        },
      };
      handleChange(WeightChangesInput);

      const FatigueDepressionInput = {
        currentTarget: {
          type: "checkbox",
          name: "FatigueDepression",
          checked: false,
        },
      };
      handleChange(FatigueDepressionInput);

      const ThoughtsOfDeathInput = {
        currentTarget: {
          type: "checkbox",
          name: "ThoughtsOfDeath",
          checked: false,
        },
      };
      handleChange(ThoughtsOfDeathInput);

      const OtherDepressionInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherDepression",
          checked: false,
        },
      };
      handleChange(OtherDepressionInput);

      const OtherDepressionDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherDepressionDetails",
          value: "",
        },
      };
      handleChange(OtherDepressionDetailsInput);

      const DepressionFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "DepressionFrequency",
          value: "",
        },
      };
      handleChange(DepressionFrequencyInput);

      const DepressionIntensityInput = {
        currentTarget: {
          type: "input",
          name: "DepressionIntensity",
          value: "",
        },
      };
      handleChange(DepressionIntensityInput);

      const DepressionDurationInput = {
        currentTarget: {
          type: "input",
          name: "DepressionDuration",
          value: "",
        },
      };
      handleChange(DepressionDurationInput);
    }
  };

  const handleAttentionHyperactivityChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const AttentionHyperactivityEasilyDistractedInput = {
        currentTarget: {
          type: "checkbox",
          name: "AttentionHyperactivityEasilyDistracted",
          checked: false,
        },
      };
      handleChange(AttentionHyperactivityEasilyDistractedInput);

      const DifficultyFollowThroughInput = {
        currentTarget: {
          type: "checkbox",
          name: "DifficultyFollowThrough",
          checked: false,
        },
      };
      handleChange(DifficultyFollowThroughInput);

      const ForgetfulInput = {
        currentTarget: {
          type: "checkbox",
          name: "Forgetful",
          checked: false,
        },
      };
      handleChange(ForgetfulInput);

      const DifficultyListeningInput = {
        currentTarget: {
          type: "checkbox",
          name: "DifficultyListening",
          checked: false,
        },
      };
      handleChange(DifficultyListeningInput);

      const DisorganizedAttentionInput = {
        currentTarget: {
          type: "checkbox",
          name: "DisorganizedAttention",
          checked: false,
        },
      };
      handleChange(DisorganizedAttentionInput);

      const FidgetyInput = {
        currentTarget: {
          type: "checkbox",
          name: "Fidgety",
          checked: false,
        },
      };
      handleChange(FidgetyInput);

      const LackOfDetailsInput = {
        currentTarget: {
          type: "checkbox",
          name: "LackOfDetails",
          checked: false,
        },
      };
      handleChange(LackOfDetailsInput);

      const LosesThingsInput = {
        currentTarget: {
          type: "checkbox",
          name: "LosesThings",
          checked: false,
        },
      };
      handleChange(LosesThingsInput);

      const InterruptsOthersInput = {
        currentTarget: {
          type: "checkbox",
          name: "InterruptsOthers",
          checked: false,
        },
      };
      handleChange(InterruptsOthersInput);

      const UnableToSitStillInput = {
        currentTarget: {
          type: "checkbox",
          name: "UnableToSitStill",
          checked: false,
        },
      };
      handleChange(UnableToSitStillInput);

      const ExcessiveTalkingInput = {
        currentTarget: {
          type: "checkbox",
          name: "ExcessiveTalking",
          checked: false,
        },
      };
      handleChange(ExcessiveTalkingInput);

      const DifficultyWaitingInput = {
        currentTarget: {
          type: "checkbox",
          name: "DifficultyWaiting",
          checked: false,
        },
      };
      handleChange(DifficultyWaitingInput);

      const OtherAttentionInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherAttention",
          checked: false,
        },
      };
      handleChange(OtherAttentionInput);

      const OtherAttentionDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherAttentionDetails",
          value: "",
        },
      };
      handleChange(OtherAttentionDetailsInput);

      const AttentionFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "AttentionFrequency",
          value: "",
        },
      };
      handleChange(AttentionFrequencyInput);

      const AttentionIntensityInput = {
        currentTarget: {
          type: "input",
          name: "AttentionIntensity",
          value: "",
        },
      };
      handleChange(AttentionIntensityInput);

      const AttentionDurationInput = {
        currentTarget: {
          type: "input",
          name: "AttentionDuration",
          value: "",
        },
      };
      handleChange(AttentionDurationInput);
    }
  };

  const handleSubstanceUseSymptomsChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const PatternOfSubstanceUseInput = {
        currentTarget: {
          type: "checkbox",
          name: "PatternOfSubstanceUse",
          checked: false,
        },
      };
      handleChange(PatternOfSubstanceUseInput);

      const DesiresCutDownUseInput = {
        currentTarget: {
          type: "checkbox",
          name: "DesiresCutDownUse",
          checked: false,
        },
      };
      handleChange(DesiresCutDownUseInput);

      const TimeSpentObtainingInput = {
        currentTarget: {
          type: "checkbox",
          name: "TimeSpentObtaining",
          checked: false,
        },
      };
      handleChange(TimeSpentObtainingInput);

      const CravingsInput = {
        currentTarget: {
          type: "checkbox",
          name: "Cravings",
          checked: false,
        },
      };
      handleChange(CravingsInput);

      const FailureOfResponsibilitiesInput = {
        currentTarget: {
          type: "checkbox",
          name: "FailureOfResponsibilities",
          checked: false,
        },
      };
      handleChange(FailureOfResponsibilitiesInput);

      const NegativeImpactInput = {
        currentTarget: {
          type: "checkbox",
          name: "NegativeImpact",
          checked: false,
        },
      };
      handleChange(NegativeImpactInput);

      const AbandonsActivitiesInput = {
        currentTarget: {
          type: "checkbox",
          name: "AbandonsActivities",
          checked: false,
        },
      };
      handleChange(AbandonsActivitiesInput);

      const ToleranceInput = {
        currentTarget: {
          type: "checkbox",
          name: "Tolerance",
          checked: false,
        },
      };
      handleChange(ToleranceInput);

      const SUWithdrawalInput = {
        currentTarget: {
          type: "checkbox",
          name: "SUWithdrawal",
          checked: false,
        },
      };
      handleChange(SUWithdrawalInput);

      const SUOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "SUOther",
          checked: false,
        },
      };
      handleChange(SUOtherInput);

      const SUOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "SUOtherDetails",
          value: "",
        },
      };
      handleChange(SUOtherDetailsInput);

      const SubstanceUseFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "SubstanceUseFrequency",
          value: "",
        },
      };
      handleChange(SubstanceUseFrequencyInput);

      const SubstanceUseIntensityInput = {
        currentTarget: {
          type: "input",
          name: "SubstanceUseIntensity",
          value: "",
        },
      };
      handleChange(SubstanceUseIntensityInput);

      const SubstanceUseDurationInput = {
        currentTarget: {
          type: "input",
          name: "SubstanceUseDuration",
          value: "",
        },
      };
      handleChange(SubstanceUseDurationInput);
    }
  };

  const handleDMDDChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const TemperInput = {
        currentTarget: {
          type: "checkbox",
          name: "Temper",
          checked: false,
        },
      };
      handleChange(TemperInput);

      const OutsideOutburstsInput = {
        currentTarget: {
          type: "checkbox",
          name: "OutsideOutbursts",
          checked: false,
        },
      };
      handleChange(OutsideOutburstsInput);

      const FrequentOutburstsInput = {
        currentTarget: {
          type: "checkbox",
          name: "FrequentOutbursts",
          checked: false,
        },
      };
      handleChange(FrequentOutburstsInput);

      const DMDDIrritableInput = {
        currentTarget: {
          type: "checkbox",
          name: "DMDDIrritable",
          checked: false,
        },
      };
      handleChange(DMDDIrritableInput);

      const OutburstsAroundOthersInput = {
        currentTarget: {
          type: "checkbox",
          name: "OutburstsAroundOthers",
          checked: false,
        },
      };
      handleChange(OutburstsAroundOthersInput);

      const DMDDOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "DMDDOther",
          checked: false,
        },
      };
      handleChange(DMDDOtherInput);

      const DMDDOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "DMDDOtherDetails",
          value: "",
        },
      };
      handleChange(DMDDOtherDetailsInput);

      const DMDDFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "DMDDFrequency",
          value: "",
        },
      };
      handleChange(DMDDFrequencyInput);

      const DMDDIntensityInput = {
        currentTarget: {
          type: "input",
          name: "DMDDIntensity",
          value: "",
        },
      };
      handleChange(DMDDIntensityInput);

      const DMDDDurationInput = {
        currentTarget: {
          type: "input",
          name: "DMDDDuration",
          value: "",
        },
      };
      handleChange(DMDDDurationInput);
    }
  };

  const handleConductDisorderChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const AggressionInput = {
        currentTarget: {
          type: "checkbox",
          name: "Aggression",
          checked: false,
        },
      };
      handleChange(AggressionInput);

      const DestructionOfPropertyInput = {
        currentTarget: {
          type: "checkbox",
          name: "DestructionOfProperty",
          checked: false,
        },
      };
      handleChange(DestructionOfPropertyInput);

      const TheftInput = {
        currentTarget: {
          type: "checkbox",
          name: "Theft",
          checked: false,
        },
      };
      handleChange(TheftInput);

      const SeriousViolationsInput = {
        currentTarget: {
          type: "checkbox",
          name: "SeriousViolations",
          checked: false,
        },
      };
      handleChange(SeriousViolationsInput);

      const OtherConductDisorderInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherConductDisorder",
          checked: false,
        },
      };
      handleChange(OtherConductDisorderInput);

      const OtherConductDisorderDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherConductDisorderDetails",
          value: "",
        },
      };
      handleChange(OtherConductDisorderDetailsInput);

      const ConductDisorderFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "ConductDisorderFrequency",
          value: "",
        },
      };
      handleChange(ConductDisorderFrequencyInput);

      const ConductDisorderIntensityInput = {
        currentTarget: {
          type: "input",
          name: "ConductDisorderIntensity",
          value: "",
        },
      };
      handleChange(ConductDisorderIntensityInput);

      const ConductDisorderDurationInput = {
        currentTarget: {
          type: "input",
          name: "ConductDisorderDuration",
          value: "",
        },
      };
      handleChange(ConductDisorderDurationInput);
    }
  };

  const handleODDChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const IrritableODDInput = {
        currentTarget: {
          type: "checkbox",
          name: "IrritableODD",
          checked: false,
        },
      };
      handleChange(IrritableODDInput);

      const ArgumentativeInput = {
        currentTarget: {
          type: "checkbox",
          name: "Argumentative",
          checked: false,
        },
      };
      handleChange(ArgumentativeInput);

      const VindictiveInput = {
        currentTarget: {
          type: "checkbox",
          name: "Vindictive",
          checked: false,
        },
      };
      handleChange(VindictiveInput);

      const OtherODDInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherODD",
          checked: false,
        },
      };
      handleChange(OtherODDInput);

      const OtherODDDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherODDDetails",
          value: "",
        },
      };
      handleChange(OtherODDDetailsInput);

      const ODDFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "ODDFrequency",
          value: "",
        },
      };
      handleChange(ODDFrequencyInput);

      const ODDIntensityInput = {
        currentTarget: {
          type: "input",
          name: "ODDIntensity",
          value: "",
        },
      };
      handleChange(ODDIntensityInput);

      const ODDDurationInput = {
        currentTarget: {
          type: "input",
          name: "ODDDuration",
          value: "",
        },
      };
      handleChange(ODDDurationInput);
    }
  };

  const handleOCDChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const OCDPresenceInput = {
        currentTarget: {
          type: "checkbox",
          name: "OCDPresence",
          checked: false,
        },
      };
      handleChange(OCDPresenceInput);

      const ObsessionsInput = {
        currentTarget: {
          type: "checkbox",
          name: "Obsessions",
          checked: false,
        },
      };
      handleChange(ObsessionsInput);

      const TimeConsumingInput = {
        currentTarget: {
          type: "checkbox",
          name: "TimeConsuming",
          checked: false,
        },
      };
      handleChange(TimeConsumingInput);

      const CompulsionsInput = {
        currentTarget: {
          type: "checkbox",
          name: "Compulsions",
          checked: false,
        },
      };
      handleChange(CompulsionsInput);

      const ReduceAnxietyInput = {
        currentTarget: {
          type: "checkbox",
          name: "ReduceAnxiety",
          checked: false,
        },
      };
      handleChange(ReduceAnxietyInput);

      const SuppressUrgesInput = {
        currentTarget: {
          type: "checkbox",
          name: "SuppressUrges",
          checked: false,
        },
      };
      handleChange(SuppressUrgesInput);

      const OCDOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "OCDOther",
          checked: false,
        },
      };
      handleChange(OCDOtherInput);

      const OCDOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OCDOtherDetails",
          value: "",
        },
      };
      handleChange(OCDOtherDetailsInput);

      const OCDFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "OCDFrequency",
          value: "",
        },
      };
      handleChange(OCDFrequencyInput);

      const OCDIntensityInput = {
        currentTarget: {
          type: "input",
          name: "OCDIntensity",
          value: "",
        },
      };
      handleChange(OCDIntensityInput);

      const OCDDurationInput = {
        currentTarget: {
          type: "input",
          name: "OCDDuration",
          value: "",
        },
      };
      handleChange(OCDDurationInput);
    }
  };

  const handlePanicDisorderChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const PoundingHeartInput = {
        currentTarget: {
          type: "checkbox",
          name: "PoundingHeart",
          checked: false,
        },
      };
      handleChange(PoundingHeartInput);

      const PanicDisorderSweatingInput = {
        currentTarget: {
          type: "checkbox",
          name: "PanicDisorderSweating",
          checked: false,
        },
      };
      handleChange(PanicDisorderSweatingInput);

      const TremblingInput = {
        currentTarget: {
          type: "checkbox",
          name: "Trembling",
          checked: false,
        },
      };
      handleChange(TremblingInput);

      const ShortnessOfBreathInput = {
        currentTarget: {
          type: "checkbox",
          name: "ShortnessOfBreath",
          checked: false,
        },
      };
      handleChange(ShortnessOfBreathInput);

      const ChokingInput = {
        currentTarget: {
          type: "checkbox",
          name: "Choking",
          checked: false,
        },
      };
      handleChange(ChokingInput);

      const ChestPainInput = {
        currentTarget: {
          type: "checkbox",
          name: "ChestPain",
          checked: false,
        },
      };
      handleChange(ChestPainInput);

      const PanicDisorderNauseaInput = {
        currentTarget: {
          type: "checkbox",
          name: "PanicDisorderNausea",
          checked: false,
        },
      };
      handleChange(PanicDisorderNauseaInput);

      const DizzyInput = {
        currentTarget: {
          type: "checkbox",
          name: "Dizzy",
          checked: false,
        },
      };
      handleChange(DizzyInput);

      const ChillsInput = {
        currentTarget: {
          type: "checkbox",
          name: "Chills",
          checked: false,
        },
      };
      handleChange(ChillsInput);

      const DerealizationInput = {
        currentTarget: {
          type: "checkbox",
          name: "Derealization",
          checked: false,
        },
      };
      handleChange(DerealizationInput);

      const FearOfCrazyInput = {
        currentTarget: {
          type: "checkbox",
          name: "FearOfCrazy",
          checked: false,
        },
      };
      handleChange(FearOfCrazyInput);

      const FearOfDyingInput = {
        currentTarget: {
          type: "checkbox",
          name: "FearOfDying",
          checked: false,
        },
      };
      handleChange(FearOfDyingInput);

      const PanicDisorderOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "PanicDisorderOther",
          checked: false,
        },
      };
      handleChange(PanicDisorderOtherInput);

      const PanicDisorderOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "PanicDisorderOtherDetails",
          value: "",
        },
      };
      handleChange(PanicDisorderOtherDetailsInput);

      const PanicDisorderFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "PanicDisorderFrequency",
          value: "",
        },
      };
      handleChange(PanicDisorderFrequencyInput);

      const PanicDisorderIntensityInput = {
        currentTarget: {
          type: "input",
          name: "PanicDisorderIntensity",
          value: "",
        },
      };
      handleChange(PanicDisorderIntensityInput);

      const PanicDisorderDurationInput = {
        currentTarget: {
          type: "input",
          name: "PanicDisorderDuration",
          value: "",
        },
      };
      handleChange(PanicDisorderDurationInput);
    }
  };

  const handlePhobiasChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const PhobiasSpecificFearInput = {
        currentTarget: {
          type: "checkbox",
          name: "PhobiasSpecificFear",
          checked: false,
        },
      };
      handleChange(PhobiasSpecificFearInput);

      const ImmediateFearInput = {
        currentTarget: {
          type: "checkbox",
          name: "ImmediateFear",
          checked: false,
        },
      };
      handleChange(ImmediateFearInput);

      const PhobiasAvoidanceInput = {
        currentTarget: {
          type: "checkbox",
          name: "PhobiasAvoidance",
          checked: false,
        },
      };
      handleChange(PhobiasAvoidanceInput);

      const FearExaggeratedInput = {
        currentTarget: {
          type: "checkbox",
          name: "FearExaggerated",
          checked: false,
        },
      };
      handleChange(FearExaggeratedInput);

      const PhobiasOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "PhobiasOther",
          checked: false,
        },
      };
      handleChange(PhobiasOtherInput);

      const PhobiasOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "PhobiasOtherDetails",
          value: "",
        },
      };
      handleChange(PhobiasOtherDetailsInput);

      const PhobiasFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "PhobiasFrequency",
          value: "",
        },
      };
      handleChange(PhobiasFrequencyInput);

      const PhobiasIntensityInput = {
        currentTarget: {
          type: "input",
          name: "PhobiasIntensity",
          value: "",
        },
      };
      handleChange(PhobiasIntensityInput);

      const PhobiasDurationInput = {
        currentTarget: {
          type: "input",
          name: "PhobiasDuration",
          value: "",
        },
      };
      handleChange(PhobiasDurationInput);
    }
  };

  const handleDIChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const DILowIQInput = {
        currentTarget: {
          type: "checkbox",
          name: "DILowIQ",
          checked: false,
        },
      };
      handleChange(DILowIQInput);

      const DISensoryImpairmentsInput = {
        currentTarget: {
          type: "checkbox",
          name: "DISensoryImpairments",
          checked: false,
        },
      };
      handleChange(DISensoryImpairmentsInput);

      const DISpeechDelaysInput = {
        currentTarget: {
          type: "checkbox",
          name: "DISpeechDelays",
          checked: false,
        },
      };
      handleChange(DISpeechDelaysInput);

      const DIMotorCoordinationDelaysInput = {
        currentTarget: {
          type: "checkbox",
          name: "DIMotorCoordinationDelays",
          checked: false,
        },
      };
      handleChange(DIMotorCoordinationDelaysInput);

      const DILearningDisorderInput = {
        currentTarget: {
          type: "checkbox",
          name: "DILearningDisorder",
          checked: false,
        },
      };
      handleChange(DILearningDisorderInput);

      const DISpatialDelaysInput = {
        currentTarget: {
          type: "checkbox",
          name: "DISpatialDelays",
          checked: false,
        },
      };
      handleChange(DISpatialDelaysInput);

      const DevelopmentalImpairmentsOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "DevelopmentalImpairmentsOther",
          checked: false,
        },
      };
      handleChange(DevelopmentalImpairmentsOtherInput);

      const DevelopmentalImpairmentsOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "DevelopmentalImpairmentsOtherDetails",
          value: "",
        },
      };
      handleChange(DevelopmentalImpairmentsOtherDetailsInput);
    }
  };

  // const handleBipolarChange = (e) => {
  //   handleChange(e);

  //   if (!e.currentTarget.checked) {
  //     const ManicEpisodesInput = {
  //       currentTarget: {
  //         type: "checkbox",
  //         name: "ManicEpisodes",
  //         checked: false,
  //       },
  //     };
  //     handleChange(ManicEpisodesInput);

  //     const DepressiveEpisodesInput = {
  //       currentTarget: {
  //         type: "checkbox",
  //         name: "DepressiveEpisodes",
  //         checked: false,
  //       },
  //     };
  //     handleChange(DepressiveEpisodesInput);

  //     const SignificantMoodChangesInput = {
  //       currentTarget: {
  //         type: "checkbox",
  //         name: "SignificantMoodChanges",
  //         checked: false,
  //       },
  //     };
  //     handleChange(SignificantMoodChangesInput);

  //     const BipolarOtherInput = {
  //       currentTarget: {
  //         type: "checkbox",
  //         name: "BipolarOther",
  //         checked: false,
  //       },
  //     };
  //     handleChange(BipolarOtherInput);

  //     const BipolarOtherDetailsInput = {
  //       currentTarget: {
  //         type: "input",
  //         name: "BipolarOtherDetails",
  //         value: "",
  //       },
  //     };
  //     handleChange(BipolarOtherDetailsInput);

  //   const BipolarFrequencyInput = {
  //     currentTarget: {
  //       type: "input",
  //       name: "BipolarFrequency",
  //       value: "",
  //     },
  //   };
  //   handleChange(BipolarFrequencyInput);

  //   const BipolarIntensityInput = {
  //     currentTarget: {
  //       type: "input",
  //       name: "BipolarIntensity",
  //       value: "",
  //     },
  //   };
  //   handleChange(BipolarIntensityInput);

  //   const BipolarDurationInput = {
  //     currentTarget: {
  //       type: "input",
  //       name: "BipolarDuration",
  //       value: "",
  //     },
  //   };
  //   handleChange(BipolarDurationInput);
  //   }

  //   buildClinicalNarrativeSummary();
  // };

  // const handleBorderlineChange = (e) => {
  //   handleChange(e);

  //   if (!e.currentTarget.checked) {
  //     const AvoidAbandonmentInput = {
  //       currentTarget: {
  //         type: "checkbox",
  //         name: "AvoidAbandonment",
  //         checked: false,
  //       },
  //     };
  //     handleChange(AvoidAbandonmentInput);

  //     const UnstableRelationshipsInput = {
  //       currentTarget: {
  //         type: "checkbox",
  //         name: "UnstableRelationships",
  //         checked: false,
  //       },
  //     };
  //     handleChange(UnstableRelationshipsInput);

  //     const IdentityDisturbancesInput = {
  //       currentTarget: {
  //         type: "checkbox",
  //         name: "IdentityDisturbances",
  //         checked: false,
  //       },
  //     };
  //     handleChange(IdentityDisturbancesInput);

  //     const ImpulsivityInput = {
  //       currentTarget: {
  //         type: "checkbox",
  //         name: "Impulsivity",
  //         checked: false,
  //       },
  //     };
  //     handleChange(ImpulsivityInput);

  //     const RecurrentSelfHarmInput = {
  //       currentTarget: {
  //         type: "checkbox",
  //         name: "RecurrentSelfHarm",
  //         checked: false,
  //       },
  //     };
  //     handleChange(RecurrentSelfHarmInput);

  //     const AffectiveInstabilityInput = {
  //       currentTarget: {
  //         type: "checkbox",
  //         name: "AffectiveInstability",
  //         checked: false,
  //       },
  //     };
  //     handleChange(AffectiveInstabilityInput);

  //     const EmptinessInput = {
  //       currentTarget: {
  //         type: "checkbox",
  //         name: "Emptiness",
  //         checked: false,
  //       },
  //     };
  //     handleChange(EmptinessInput);

  //     const PoorAngerManagementInput = {
  //       currentTarget: {
  //         type: "checkbox",
  //         name: "PoorAngerManagement",
  //         checked: false,
  //       },
  //     };
  //     handleChange(PoorAngerManagementInput);

  //     const ParanoidIdeationInput = {
  //       currentTarget: {
  //         type: "checkbox",
  //         name: "ParanoidIdeation",
  //         checked: false,
  //       },
  //     };
  //     handleChange(ParanoidIdeationInput);

  //     const BorderlineOtherInput = {
  //       currentTarget: {
  //         type: "checkbox",
  //         name: "BorderlineOther",
  //         checked: false,
  //       },
  //     };
  //     handleChange(BorderlineOtherInput);

  //     const BorderlineOtherDetailsInput = {
  //       currentTarget: {
  //         type: "input",
  //         name: "BorderlineOtherDetails",
  //         value: "",
  //       },
  //     };
  //     handleChange(BorderlineOtherDetailsInput);

  //   const BorderlineFrequencyInput = {
  //     currentTarget: {
  //       type: "input",
  //       name: "BorderlineFrequency",
  //       value: "",
  //     },
  //   };
  //   handleChange(BorderlineFrequencyInput);

  //   const BorderlineIntensityInput = {
  //     currentTarget: {
  //       type: "input",
  //       name: "BorderlineIntensity",
  //       value: "",
  //     },
  //   };
  //   handleChange(BorderlineIntensityInput);

  //   const BorderlineDurationInput = {
  //     currentTarget: {
  //       type: "input",
  //       name: "BorderlineDuration",
  //       value: "",
  //     },
  //   };

  //   handleChange(BorderlineDurationInput);
  //   }

  //   buildClinicalNarrativeSummary();
  // };

  const handleOtherSymptomsChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const OtherSymptomsDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherSymptomsDetails",
          value: "",
        },
      };
      handleChange(OtherSymptomsDetailsInput);

      const OtherSymptomsFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "OtherSymptomsFrequency",
          value: "",
        },
      };
      handleChange(OtherSymptomsFrequencyInput);

      const OtherSymptomsIntensityInput = {
        currentTarget: {
          type: "input",
          name: "OtherSymptomsIntensity",
          value: "",
        },
      };
      handleChange(OtherSymptomsIntensityInput);

      const OtherSymptomsDurationInput = {
        currentTarget: {
          type: "input",
          name: "OtherSymptomsDuration",
          value: "",
        },
      };
      handleChange(OtherSymptomsDurationInput);
    }
  };

  const handleSubstanceUseNAChange = (e) => {
    handleChange(e);
    const noPastTreatmentInput = {
      currentTarget: {
        type: "checkbox",
        name: "NoPastTreatment",
        checked: true,
      },
    };
    handleChange(noPastTreatmentInput);
  };

  const sexAtBirthOptions = [
    { _id: "Female", name: "Female" },
    { _id: "Male", name: "Male" },
    { _id: "Intersex", name: "Intersex" },
    { _id: "Sex not listed", name: "Sex not listed" },
    { _id: "Prefer not to say", name: "Prefer not to say" },
  ];

  const genderIdentityOptions = [
    { _id: "Female", name: "Female" },
    { _id: "Male", name: "Male" },
    { _id: "Non-binary", name: "Non-binary" },
    { _id: "Agender", name: "Agender" },
    { _id: "Transgender (FtM)", name: "Transgender (FtM)" },
    { _id: "Transgender (MtF)", name: "Transgender (MtF)" },
    { _id: "Identity not listed", name: "Identity not listed" },
    { _id: "Prefer not to say", name: "Prefer not to say" },
  ];

  const pronounOptions = [
    { _id: "She/Her/Hers", name: "She/Her/Hers" },
    { _id: "He/Him/His", name: "He/Him/His" },
    { _id: "They/Them/Their", name: "They/Them/Their" },
    { _id: "Prefer to self-describe", name: "Prefer to self-describe" },
    { _id: "Prefer not to say", name: "Prefer not to say" },
  ];

  const divisionOptions = [
    { _id: "Bowling Green", name: "Bowling Green" },
    { _id: "Lexington", name: "Lexington" },
    { _id: "Louisville", name: "Louisville" },
    { _id: "Northern Kentucky", name: "Northern Kentucky" },
    { _id: "Somerset", name: "Somerset" },
  ];

  const getPSDTypeOptions = (obj) => {
    if (getFieldValue(obj.PSDType) === "Psychiatric Diagnostic Evaluation") {
      return [
        {
          _id: "Psychiatric Diagnostic Evaluation",
          name: "Psychiatric Diagnostic Evaluation (5-day; accompanies 90791)",
        },
        {
          _id: "Biopsychosocial Assessment",
          name: "Biopsychosocial Assessment (30-day)",
        },
        { _id: "Addendum/Annual Update", name: "Addendum/Annual Update" },
      ];
    } else {
      return [
        {
          _id: "Biopsychosocial Assessment",
          name: "Biopsychosocial Assessment (30-day)",
        },
        { _id: "Addendum/Annual Update", name: "Addendum/Annual Update" },
      ];
    }
  };

  const yesNoOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
  ];

  const yesNoNAOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    { _id: "N/A", name: "N/A" },
  ];

  const yesNoUnknownOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    { _id: "Unknown", name: "Unknown" },
  ];

  const yesNoNoneOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    { _id: "None reported", name: "None reported" },
  ];

  const yesNoNoneNAOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    { _id: "None reported", name: "None reported" },
    { _id: "N/A", name: "N/A" },
  ];

  const yesNoNoneUKOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    { _id: "None reported", name: "None reported" },
    { _id: "Unknown", name: "Unknown" },
  ];

  const familyMemberOptions = [
    { _id: "Father", name: "Father" },
    { _id: "Mother", name: "Mother" },
    { _id: "Maternal Grandmother", name: "Maternal Grandmother" },
    { _id: "Maternal Grandfather", name: "Maternal Grandfather" },
    { _id: "Paternal Grandmother", name: "Paternal Grandmother" },
    { _id: "Paternal Grandfather", name: "Paternal Grandfather" },
    { _id: "Maternal Aunt", name: "Maternal Aunt" },
    { _id: "Maternal Uncle", name: "Maternal Uncle" },
    { _id: "Paternal Aunt", name: "Paternal Aunt" },
    { _id: "Paternal Uncle", name: "Paternal Uncle" },
    { _id: "Sister", name: "Sister" },
    { _id: "Brother", name: "Brother" },
    { _id: "Step-mother", name: "Step-mother" },
    { _id: "Step-father", name: "Step-father" },
    { _id: "Cousin", name: "Cousin" },
  ];

  const educationOptions = [
    {
      _id: "Client has not started school",
      name: "Client has not started school",
    },
    {
      _id: "Currently in school",
      name: "Currently in school",
    },
    { _id: "Graduated", name: "Graduated" },
    { _id: "GED", name: "GED" },
    { _id: "Dropout/Did not complete", name: "Dropout/Did not complete" },
  ];

  const sexualOrientationOptions = [
    {
      _id: "Heterosexual",
      name: "Heterosexual",
    },
    {
      _id: "Gay",
      name: "Gay",
    },
    { _id: "Lesbian", name: "Lesbian" },
    { _id: "Bisexual", name: "Bisexual" },
    { _id: "Pansexual", name: "Pansexual" },
    { _id: "Polysexual", name: "Polysexual" },
    { _id: "Asexual", name: "Asexual" },
    { _id: "Queer/Questioning", name: "Queer/Questioning" },
    { _id: "Orientation not listed", name: "Orientation not listed" },
    { _id: "Prefer not to say", name: "Prefer not to say" },
  ];

  const timeframeOptions = [
    { _id: "None/Never", name: "None/Never" },
    { _id: "Current", name: "Current" },
    { _id: "Last 30 Days", name: "Last 30 Days" },
    { _id: "Last 6 Months", name: "Last 6 Months" },
    { _id: "More Than 6 Months Ago", name: "More Than 6 Months Ago" },
  ];

  const safetyPlanStatusOptions = [
    { _id: "Not Needed", name: "Not Needed" },
    { _id: "Active/Effective", name: "Active/Effective" },
    {
      _id: "Ineffective/Updates Completed",
      name: "Ineffective/Updates Completed",
    },
  ];

  const functioningLevelOptions = [
    { _id: "Age Appropriate", name: "Age Appropriate" },
    { _id: "Regressed", name: "Regressed" },
    { _id: "Advanced", name: "Advanced" },
    { _id: "N/A", name: "N/A" },
    { _id: "Unable to Assess", name: "Unable to Assess" },
  ];

  const complianceOptions = [
    {
      _id: "Consistently takes as directed",
      name: "Consistently takes as directed",
    },
    {
      _id: "Inconsistent with directed use",
      name: "Inconsistent with directed use",
    },
    { _id: "Refuses to take", name: "Refuses to take" },
    { _id: "Forgets to take", name: "Forgets to take" },
  ];

  const traumaOptions = [
    { _id: "Physical Abuse", name: "Physical Abuse" },
    { _id: "Sexual Abuse", name: "Sexual Abuse" },
    { _id: "Neglect", name: "Neglect" },
    { _id: "Emotional Abuse", name: "Emotional Abuse" },
    { _id: "Domestic Violence", name: "Domestic Violence" },
    { _id: "Accident", name: "Accident" },
    { _id: "Acts of Violence", name: "Acts of Violence" },
    { _id: "Bullying", name: "Bullying" },
    { _id: "Death/Tragedy", name: "Death/Tragedy" },
    { _id: "Natural Disaster", name: "Natural Disaster" },
    { _id: "Community Violence", name: "Community Violence" },
    { _id: "Medical/Serious Illness", name: "Medical/Serious Illness" },
    { _id: "Abandonment", name: "Abandonment" },
    { _id: "War/Terrorism", name: "War/Terrorism" },
    { _id: "Exploitation/Trafficking", name: "Exploitation/Trafficking" },
    { _id: "Criminal Activity", name: "Criminal Activity" },
    { _id: "Caregiver Disruptions", name: "Caregiver Disruptions" },
    { _id: "Kidnapping", name: "Kidnapping" },
    { _id: "Other", name: "Other" },
  ];

  const statusOptions = [
    { _id: "Victim", name: "Victim" },
    { _id: "Witnessed", name: "Witnessed" },
    { _id: "Suspected", name: "Suspected" },
    { _id: "Threatened", name: "Threatened" },
    { _id: "Perceived", name: "Perceived" },
    { _id: "Repeated Exposure", name: "Repeated Exposure" },
    { _id: "Indirect Exposure", name: "Indirect Exposure" },
  ];

  const riskOptions = [
    { _id: "Low", name: "Low" },
    { _id: "Moderate", name: "Moderate" },
    { _id: "High", name: "High" },
    { _id: "None", name: "None Noted" },
  ];

  const treatmentRecommendationOptions = [
    {
      _id: "Individual Counseling/Therapy",
      name: "Individual Counseling/Therapy",
    },
    { _id: "Family Counseling/Therapy", name: "Family Counseling/Therapy" },
    { _id: "Group Counseling/Therapy", name: "Group Counseling/Therapy" },
    { _id: "Basic Living Skills", name: "Basic Living Skills" },
    { _id: "Mental Health Consultation", name: "Mental Health Consultation" },
    { _id: "Family Support Services", name: "Family Support Services" },
    { _id: "Case Management Services", name: "Case Management Services" },
    { _id: "Psychoeducation", name: "Psychoeducation" },
    { _id: "Rehabilitation Services", name: "Rehabilitation Services" },
    {
      _id: "Psychiatric Evaluation Services",
      name: "Psychiatric Evaluation Services",
    },
    {
      _id: "Behavior Management Training/Services",
      name: "Behavior Management Training/Services",
    },
    { _id: "Animal Assisted Therapy", name: "Animal Assisted Therapy" },
    { _id: "In Home Treatment Serviecs", name: "In Home Treatment Serviecs" },
    {
      _id: "Early Childhood Intervention Services",
      name: "Early Childhood Intervention Services",
    },
    { _id: "Mentoring", name: "Mentoring" },
    {
      _id: "Substance Abuse Specific Treatment",
      name: "Substance Abuse Specific Treatment",
    },
    { _id: "AA/NA/COA", name: "AA/NA/COA" },
    {
      _id: "Comprehensive Community Support Services (CSS)",
      name: "Comprehensive Community Support Services (CSS)",
    },
  ];

  const typeOfCPSOptions = [
    { _id: "Permanent Custody", name: "Permanent Custody" },
    { _id: "Temporary Custody", name: "Temporary Custody" },
    { _id: "Voluntary", name: "Voluntary" },
    { _id: "Court Ordered", name: "Court Ordered" },
    { _id: "Investigation", name: "Investigation" },
    { _id: "Ongoing", name: "Ongoing" },
  ];

  const legalStatusOptions = [
    { _id: "None reported", name: "None reported" },
    { _id: "Probation", name: "Probation" },
    { _id: "Parole", name: "Parole" },
    { _id: "Charges Pending", name: "Charges Pending" },
    { _id: "Dependency Case", name: "Dependency Case" },
    {
      _id: "Charges with no Court Supervision",
      name: "Charges with no Court Supervision",
    },
    { _id: "Convicted", name: "Convicted" },
  ];

  const dispositionOptions = [
    { _id: "None reported", name: "None reported" },
    { _id: "Adjudicated Delinquent", name: "Adjudicated Delinquent" },
    { _id: "Adjudicated", name: "Adjudicated" },
    { _id: "Charges Dropped", name: "Charges Dropped" },
    { _id: "Dependency", name: "Dependency" },
    { _id: "Pending", name: "Pending" },
  ];

  const useOfProtectionOptions = [
    ...yesNoOptions,
    { _id: "Not with Consistency", name: "Not with Consistency" },
    { _id: "N/A", name: "N/A" },
  ];

  const outcomeOptions = [
    { _id: "Successful", name: "Successful" },
    { _id: "Unsuccessful", name: "Unsuccessful" },
    { _id: "Did not complete", name: "Did not complete" },
    { _id: "Other", name: "Other" },
  ];

  const pregnancyOptions = [
    { _id: "Current", name: "Current" },
    { _id: "Past", name: "Past" },
    { _id: "None reported", name: "None reported" },
    { _id: "N/A", name: "N/A" },
  ];

  const awarenessOptions = [
    ...yesNoOptions,
    {
      _id: "Aware but No Recall/Memories",
      name: "Aware but No Recall/Memories",
    },
  ];

  const assessmentNeedOptions = [
    { _id: "Psychiatric", name: "Psychiatric" },
    { _id: "Psychological", name: "Psychological" },
    { _id: "Neurological", name: "Neurological" },
    { _id: "Educational", name: "Educational" },
    { _id: "Medical", name: "Medical" },
    { _id: "Dental", name: "Dental" },
    { _id: "Alcohol/Drug", name: "Alcohol/Drug" },
    { _id: "Vocational", name: "Vocational" },
    { _id: "Occupational", name: "Occupational" },
    { _id: "Visual/Auditory", name: "Visual/Auditory" },
    { _id: "Nutritional", name: "Nutritional" },
    { _id: "ASD Screening", name: "ASD Screening" },
    { _id: "Other", name: "Other" },
  ];

  const typeOfUseOptions = [
    { _id: "Experimentation/Infrequent", name: "Experimentation/Infrequent" },
    { _id: "Regular Use", name: "Regular Use" },
    { _id: "Other", name: "Other" },
  ];

  const typeOfTreatmentOptions = [
    { _id: "Outpatient", name: "Outpatient" },
    { _id: "Intensive Outpatient", name: "Intensive Outpatient" },
    { _id: "Hospital", name: "Hospital" },
    { _id: "Residential", name: "Residential" },
    { _id: "Detox", name: "Detox" },
    { _id: "Court-Ordered Treatment", name: "Court-Ordered Treatment" },
    { _id: "Other", name: "Other" },
  ];

  const buildClinicalNarrativeSummary = () => {
    //   let summary = "";
    //   // Presenting Problem & Strengths
    //   if (
    //     data.PresentingProblem["@value"] &&
    //     data.PresentingProblem["@value"] !== ""
    //   )
    //     summary += `Presenting Problem:\r\n${data.PresentingProblem["@value"]}\r\n\r\n`;
    //   // // Culture/Ethnicity
    //   let cultureSummary = "";
    //   if (data.CulturalGroup["@value"] && data.CulturalGroup["@value"] !== "")
    //     cultureSummary += `Identified cultural group: ${data.CulturalGroup["@value"]}; `;
    //   if (
    //     data.CulturalTraditionsDetails["@value"] &&
    //     data.CulturalTraditionsDetails["@value"] !== ""
    //   )
    //     cultureSummary += `Special cultural traditions/celebrations/rituals: ${data.CulturalTraditionsDetails["@value"]}; `;
    //   if (
    //     data.NegativeCulturalExperiencesDetails["@value"] &&
    //     data.NegativeCulturalExperiencesDetails["@value"] !== ""
    //   )
    //     cultureSummary += `Negative experiences due to customs/ethnicity/race/SES: ${data.NegativeCulturalExperiencesDetails["@value"]}; `;
    //   if (
    //     data.CulturalMedicalBeliefsDetails["@value"] &&
    //     data.CulturalMedicalBeliefsDetails["@value"] !== ""
    //   )
    //     cultureSummary += `Cultural beliefs that influence medical/mental health decisions: ${data.CulturalMedicalBeliefsDetails["@value"]}; `;
    //   if (
    //     data.CulturalDifferencesDetails["@value"] &&
    //     data.CulturalDifferencesDetails["@value"] !== ""
    //   )
    //     cultureSummary += `Differences between youth's and foster/placement family's cultures: ${data.CulturalDifferencesDetails["@value"]}; `;
    //   if (cultureSummary !== "")
    //     summary += `Culture/Ethnicity:\r\n${cultureSummary.trimEnd()}\r\n\r\n`;
    //   // Symptom Checklist
    //   let symptomSummary = "";
    //   if (
    //     data.TraumaSymptomsFrequency["@value"] &&
    //     data.TraumaSymptomsFrequency["@value"] !== "" &&
    //     data.TraumaSymptomsIntensity["@value"] &&
    //     data.TraumaSymptomsIntensity["@value"] !== "" &&
    //     data.TraumaSymptomsDuration["@value"] &&
    //     data.TraumaSymptomsDuration["@value"] !== "" &&
    //     data.TraumaSymptomsOnset["@value"] &&
    //     data.TraumaSymptomsOnset["@value"] !== ""
    //   )
    //     symptomSummary += `Trauma Symptoms: ${data.TraumaSymptomsFrequency["@value"]}, ${data.TraumaSymptomsIntensity["@value"]}, ${data.TraumaSymptomsDuration["@value"]}, ${data.TraumaSymptomsOnset["@value"]}; `;
    //   if (
    //     data.AnxietyDisorderFrequency["@value"] &&
    //     data.AnxietyDisorderFrequency["@value"] !== "" &&
    //     data.AnxietyDisorderIntensity["@value"] &&
    //     data.AnxietyDisorderIntensity["@value"] !== "" &&
    //     data.AnxietyDisorderDuration["@value"] &&
    //     data.AnxietyDisorderDuration["@value"] !== ""
    //   )
    //     symptomSummary += `Anxiety Disorder: ${data.AnxietyDisorderFrequency["@value"]}, ${data.AnxietyDisorderIntensity["@value"]}, ${data.AnxietyDisorderDuration["@value"]}; `;
    //   if (
    //     data.DepressionFrequency["@value"] &&
    //     data.DepressionFrequency["@value"] !== "" &&
    //     data.DepressionIntensity["@value"] &&
    //     data.DepressionIntensity["@value"] !== "" &&
    //     data.DepressionDuration["@value"] &&
    //     data.DepressionDuration["@value"] !== ""
    //   )
    //     symptomSummary += `Depression: ${data.DepressionFrequency["@value"]}, ${data.DepressionIntensity["@value"]}, ${data.DepressionDuration["@value"]}; `;
    //   if (
    //     data.AttentionHyperactivityFrequency["@value"] &&
    //     data.AttentionHyperactivityFrequency["@value"] !== "" &&
    //     data.AttentionHyperactivityIntensity["@value"] &&
    //     data.AttentionHyperactivityIntensity["@value"] !== "" &&
    //     data.AttentionHyperactivityDuration["@value"] &&
    //     data.AttentionHyperactivityDuration["@value"] !== ""
    //   )
    //     symptomSummary += `Attention Hyperactivity: ${data.AttentionHyperactivityFrequency["@value"]}, ${data.AttentionHyperactivityIntensity["@value"]}, ${data.AttentionHyperactivityDuration["@value"]}; `;
    //   if (
    //     data.SubstanceUseFrequency["@value"] &&
    //     data.SubstanceUseFrequency["@value"] !== "" &&
    //     data.SubstanceUseIntensity["@value"] &&
    //     data.SubstanceUseIntensity["@value"] !== "" &&
    //     data.SubstanceUseDuration["@value"] &&
    //     data.SubstanceUseDuration["@value"] !== ""
    //   )
    //     symptomSummary += `Substance Use: ${data.SubstanceUseFrequency["@value"]}, ${data.SubstanceUseIntensity["@value"]}, ${data.SubstanceUseDuration["@value"]}; `;
    //   if (
    //     data.DMDDFrequency["@value"] &&
    //     data.DMDDFrequency["@value"] !== "" &&
    //     data.DMDDIntensity["@value"] &&
    //     data.DMDDIntensity["@value"] !== "" &&
    //     data.DMDDDuration["@value"] &&
    //     data.DMDDDuration["@value"] !== ""
    //   )
    //     symptomSummary += `DMDD: ${data.DMDDFrequency["@value"]}, ${data.DMDDIntensity["@value"]}, ${data.DMDDDuration["@value"]}; `;
    //   if (
    //     data.ConductDisorderFrequency["@value"] &&
    //     data.ConductDisorderFrequency["@value"] !== "" &&
    //     data.ConductDisorderIntensity["@value"] &&
    //     data.ConductDisorderIntensity["@value"] !== "" &&
    //     data.ConductDisorderDuration["@value"] &&
    //     data.ConductDisorderDuration["@value"] !== ""
    //   )
    //     symptomSummary += `Conduct Disorder: ${data.ConductDisorderFrequency["@value"]}, ${data.ConductDisorderIntensity["@value"]}, ${data.ConductDisorderDuration["@value"]}; `;
    //   if (
    //     data.ODDFrequency["@value"] &&
    //     data.ODDFrequency["@value"] !== "" &&
    //     data.ODDIntensity["@value"] &&
    //     data.ODDIntensity["@value"] !== "" &&
    //     data.ODDDuration["@value"] &&
    //     data.ODDDuration["@value"] !== ""
    //   )
    //     symptomSummary += `ODD: ${data.ODDFrequency["@value"]}, ${data.ODDIntensity["@value"]}, ${data.ODDDuration["@value"]}; `;
    //   if (
    //     data.OCDFrequency["@value"] &&
    //     data.OCDFrequency["@value"] !== "" &&
    //     data.OCDIntensity["@value"] &&
    //     data.OCDIntensity["@value"] !== "" &&
    //     data.OCDDuration["@value"] &&
    //     data.OCDDuration["@value"] !== ""
    //   )
    //     symptomSummary += `OCD: ${data.OCDFrequency["@value"]}, ${data.OCDIntensity["@value"]}, ${data.OCDDuration["@value"]}; `;
    //   if (
    //     data.PanicDisorderFrequency["@value"] &&
    //     data.PanicDisorderFrequency["@value"] !== "" &&
    //     data.PanicDisorderIntensity["@value"] &&
    //     data.PanicDisorderIntensity["@value"] !== "" &&
    //     data.PanicDisorderDuration["@value"] &&
    //     data.PanicDisorderDuration["@value"] !== ""
    //   )
    //     symptomSummary += `Panic Disorder: ${data.PanicDisorderFrequency["@value"]}, ${data.PanicDisorderIntensity["@value"]}, ${data.PanicDisorderDuration["@value"]}; `;
    //   if (
    //     data.PhobiasFrequency["@value"] &&
    //     data.PhobiasFrequency["@value"] !== "" &&
    //     data.PhobiasIntensity["@value"] &&
    //     data.PhobiasIntensity["@value"] !== "" &&
    //     data.PhobiasDuration["@value"] &&
    //     data.PhobiasDuration["@value"] !== ""
    //   )
    //     symptomSummary += `Phobias: ${data.PhobiasFrequency["@value"]}, ${data.PhobiasIntensity["@value"]}, ${data.PhobiasDuration["@value"]}; `;
    //   if (
    //     data.OtherSymptomsDetails["@value"] &&
    //     data.OtherSymptomsDetails["@value"] !== "" &&
    //     data.OtherSymptomsFrequency["@value"] &&
    //     data.OtherSymptomsFrequency["@value"] !== "" &&
    //     data.OtherSymptomsIntensity["@value"] &&
    //     data.OtherSymptomsIntensity["@value"] !== "" &&
    //     data.OtherSymptomsDuration["@value"] &&
    //     data.OtherSymptomsDuration["@value"] !== ""
    //   )
    //     symptomSummary += `Other Symptoms: ${data.OtherSymptomsDetails["@value"]}, ${data.OtherSymptomsFrequency["@value"]}, ${data.OtherSymptomsIntensity["@value"]}, ${data.OtherSymptomsDuration["@value"]};`;
    //   if (symptomSummary !== "")
    //     summary += `Symptom Checklist:\r\n${symptomSummary.trimEnd()}\r\n\r\n`;
    //   // Trauma History
    //   let traumaSummary = "";
    //   if (data.TraumaHistory["@value"] && data.TraumaHistory["@value"] !== "") {
    //     const traumaHistory = JSON.parse(data.TraumaHistory["@value"])
    //       .filter((item) => !item.IsEditable)
    //       .map((item) => `${item.Trauma}, ${item.Status}, ${item.Comments}`)
    //       .join(". ");
    //     traumaSummary += `${traumaHistory !== "" ? `${traumaHistory}; ` : ""}`;
    //   }
    //   if (
    //     data.CopingStrategies["@value"] &&
    //     data.CopingStrategies["@value"] !== ""
    //   )
    //     traumaSummary += `Coping strategies: ${data.CopingStrategies["@value"]}; `;
    //   if (
    //     data.ClientPerspectiveOfTrauma["@value"] &&
    //     data.ClientPerspectiveOfTrauma["@value"] !== ""
    //   )
    //     traumaSummary += `Client's perspective of trauma: ${data.ClientPerspectiveOfTrauma["@value"]}; `;
    //   if (
    //     data.CaregiverPerspectiveOfTrauma["@value"] &&
    //     data.CaregiverPerspectiveOfTrauma["@value"] !== ""
    //   )
    //     traumaSummary += `Caregiver's perspective of trauma: ${data.CaregiverPerspectiveOfTrauma["@value"]}; `;
    //   if (traumaSummary !== "")
    //     summary += `Trauma History:\r\n${traumaSummary.trimEnd()}\r\n\r\n`;
    //   // Lethality
    //   let lethalitySummary = "";
    //   if (
    //     data.CurrentSelfHarmRisk["@value"] &&
    //     data.CurrentSelfHarmRisk["@value"] !== ""
    //   ) {
    //     const selfHarmRisk = riskOptions.filter(
    //       (option) => option._id === data.CurrentSelfHarmRisk["@value"]
    //     )[0];
    //     lethalitySummary += `Current Risk of Harm to Self: ${selfHarmRisk.name}; `;
    //     if (
    //       data.SelfHarmComments["@value"] &&
    //       data.SelfHarmComments["@value"] !== ""
    //     )
    //       lethalitySummary += `Comments/Plan: ${data.SelfHarmComments["@value"]}; `;
    //   }
    //   if (
    //     data.CurrentOtherHarmRisk["@value"] &&
    //     data.CurrentOtherHarmRisk["@value"] !== ""
    //   ) {
    //     const othersHarmRisk = riskOptions.filter(
    //       (option) => option._id === data.CurrentOtherHarmRisk["@value"]
    //     )[0];
    //     lethalitySummary += `Current Risk of Harm to Others: ${othersHarmRisk.name}; `;
    //     if (
    //       data.OtherHarmComments["@value"] &&
    //       data.OtherHarmComments["@value"] !== ""
    //     )
    //       lethalitySummary += `Comments/Plan: ${data.OtherHarmComments["@value"]}; `;
    //   }
    //   if (lethalitySummary !== "")
    //     summary += `Lethality:\r\n${lethalitySummary.trimEnd()}\r\n\r\n`;
    //   // Clinical Narrative Summary Comments
    //   if (
    //     data.ClinicalNarrativeSummaryComments["@value"] &&
    //     data.ClinicalNarrativeSummaryComments["@value"] !== ""
    //   )
    //     summary += `Additional Comments:\r\n${data.ClinicalNarrativeSummaryComments["@value"]}`;
    //   summary = summary.trimEnd();
    //   const obj = { ...data };
    //   obj.ClinicalNarrativeSummary["@value"] = summary;
    //   setData(obj);
  };

  const getFamilyMemberConcerns = (item) => {
    const concerns = [];

    if (item.MentalHealth === "true") concerns.push("Mental Health");
    if (item.Medical === "true") concerns.push("Medical");
    if (item.Disability === "true") concerns.push("Disability");
    if (item.SubstanceAbuse === "true") concerns.push("Substance Abuse");
    if (item.Suicidal === "true") concerns.push("Suicidal");
    if (item.Legal === "true") concerns.push("Legal");
    if (item.Trauma === "true") concerns.push("Trauma");
    if (item.Perpetration === "true") concerns.push("Perpetration");
    if (item.PartnerViolence === "true")
      concerns.push("Intimate Partner Violence");
    if (item.Other === "true") concerns.push("Other");

    if (concerns.length > 0) return concerns.join(", ");
    else return "None reported";
  };

  const getTreatmentRecommendationStatus = (item) => {
    let statuses = [];

    if (item.Deferred === "Yes") statuses.push("SAFY will be providing");

    if (item.Referred === "Yes")
      statuses.push("SAFY will be referring to another agency/individual");

    if (item.Declined === "Yes") statuses.push("Declined by Client");

    return statuses.join(", ");
  };

  const customValidate = (errs) => {
    if (
      (!data.ISClient["@value"] || data.ISClient["@value"] === "false") &&
      (!data.ISParent["@value"] || data.ISParent["@value"] === "false") &&
      (!data.ISGuardian["@value"] || data.ISGuardian["@value"] === "false") &&
      (!data.ISCaregiver["@value"] || data.ISCaregiver["@value"] === "false") &&
      (!data.ISPhysician["@value"] || data.ISPhysician["@value"] === "false") &&
      (!data.ISRecords["@value"] || data.ISRecords["@value"] === "false") &&
      (!data.ISServiceProvider["@value"] ||
        data.ISServiceProvider["@value"] === "false") &&
      (!data.ISSchool["@value"] || data.ISSchool["@value"] === "false") &&
      (!data.ISCourt["@value"] || data.ISCourt["@value"] === "false") &&
      (!data.ISCPS["@value"] || data.ISCPS["@value"] === "false") &&
      (!data.ISOther["@value"] || data.ISOther["@value"] === "false")
    ) {
      if (errs)
        errs.InformationSources =
          "At least one Information Source is required.";
      else
        errs = {
          InformationSources: "At least one Information Source is required.",
        };
    }

    if (
      (!data.CurrentConcernsOfDV["@value"] ||
        data.CurrentConcernsOfDV["@value"] === "false") &&
      (!data.PastConcernsOfDV["@value"] ||
        data.PastConcernsOfDV["@value"] === "false") &&
      (!data.UnknownConcernsOfDV["@value"] ||
        data.UnknownConcernsOfDV["@value"] === "false") &&
      (!data.NoReportedConcernsOfDV["@value"] ||
        data.NoReportedConcernsOfDV["@value"] === "false")
    ) {
      if (errs)
        errs.DomesticViolenceConcerns = "At least one value is required.";
      else
        errs = {
          DomesticViolenceConcerns: "At least one value is required.",
        };
    }

    if (
      (!data.CurrentMaritalConflict["@value"] ||
        data.CurrentMaritalConflict["@value"] === "false") &&
      (!data.PastMaritalConflict["@value"] ||
        data.PastMaritalConflict["@value"] === "false") &&
      (!data.UnknownMaritalConflict["@value"] ||
        data.UnknownMaritalConflict["@value"] === "false") &&
      (!data.NoReportedMaritalConflict["@value"] ||
        data.NoReportedMaritalConflict["@value"] === "false")
    ) {
      if (errs) errs.CouplesConflict = "At least one value is required.";
      else
        errs = {
          CouplesConflict: "At least one value is required.",
        };
    }

    if (
      data.PSDType["@value"] !== undefined &&
      data.PSDType["@value"] !== "" &&
      data.PSDType["@value"] !== "Psychiatric Diagnostic Evaluation" &&
      (data.LegalStatus["@value"] === "Probation" ||
        data.LegalStatus["@value"] === "Parole" ||
        data.LegalStatus["@value"] === "Charges Pending" ||
        data.LegalStatus["@value"] === "Dependency Case" ||
        data.LegalStatus["@value"] === "Charges with no Court Supervision" ||
        data.LegalStatus["@value"] === "Convicted") &&
      (data.LegalHistory["@value"] === undefined ||
        data.LegalHistory["@value"] === "")
    ) {
      if (errs) errs.LegalHistory = "Legal History is required.";
      else
        errs = {
          LegalHistory: "Legal History is required.",
        };
    }

    if (data.TraumaNA["@value"] === "false") {
      if (
        (!data.DistressingMemories["@value"] ||
          data.DistressingMemories["@value"] === "false") &&
        (!data.LossOfMemories["@value"] ||
          data.LossOfMemories["@value"] === "false") &&
        (!data.Nightmares["@value"] || data.Nightmares["@value"] === "false") &&
        (!data.Flashbacks["@value"] || data.Flashbacks["@value"] === "false") &&
        (!data.RepetitivePlayOfEvents["@value"] ||
          data.RepetitivePlayOfEvents["@value"] === "false") &&
        (!data.Dissocation["@value"] ||
          data.Dissocation["@value"] === "false") &&
        (!data.BlockOut["@value"] || data.BlockOut["@value"] === "false") &&
        (!data.Hyperarousal["@value"] ||
          data.Hyperarousal["@value"] === "false") &&
        (!data.Avoidant["@value"] || data.Avoidant["@value"] === "false") &&
        (!data.Detachment["@value"] || data.Detachment["@value"] === "false") &&
        (!data.PsychologicalDistress["@value"] ||
          data.PsychologicalDistress["@value"] === "false") &&
        (!data.NegativeCognitions["@value"] ||
          data.NegativeCognitions["@value"] === "false") &&
        (!data.Hypervigilance["@value"] ||
          data.Hypervigilance["@value"] === "false") &&
        (!data.SleepDisturbances["@value"] ||
          data.SleepDisturbances["@value"] === "false") &&
        (!data.DifficultyConcentratingSymptom["@value"] ||
          data.DifficultyConcentratingSymptom["@value"] === "false") &&
        (!data.EmotionalDysregulation["@value"] ||
          data.EmotionalDysregulation["@value"] === "false") &&
        (!data.DiminishedInterests["@value"] ||
          data.DiminishedInterests["@value"] === "false") &&
        (!data.SocialWithdrawalSymptom["@value"] ||
          data.SocialWithdrawalSymptom["@value"] === "false") &&
        (!data.PhysiologicalResponses["@value"] ||
          data.PhysiologicalResponses["@value"] === "false") &&
        (!data.DevelopmentalRegression["@value"] ||
          data.DevelopmentalRegression["@value"] === "false") &&
        (!data.UnhealthyBoundaries["@value"] ||
          data.UnhealthyBoundaries["@value"] === "false") &&
        (!data.NoSymptoms["@value"] || data.NoSymptoms["@value"] === "false")
      ) {
        if (errs)
          errs.TraumaSymptomsResponse =
            "At least one Trauma Symptom/Response is required.";
        else
          errs = {
            TraumaSymptomsResponse:
              "At least one Trauma Symptom/Response is required.",
          };
      }

      if (
        (!data.SocialSupports["@value"] ||
          data.SocialSupports["@value"] === "false") &&
        (!data.InterpersonalSkills["@value"] ||
          data.InterpersonalSkills["@value"] === "false") &&
        (!data.ValidatingExperiences["@value"] ||
          data.ValidatingExperiences["@value"] === "false") &&
        (!data.Adaptability["@value"] ||
          data.Adaptability["@value"] === "false") &&
        (!data.SenseOfMastery["@value"] ||
          data.SenseOfMastery["@value"] === "false") &&
        (!data.PositiveOutlook["@value"] ||
          data.PositiveOutlook["@value"] === "false") &&
        (!data.Resources["@value"] || data.Resources["@value"] === "false") &&
        (!data.SecureAttachments["@value"] ||
          data.SecureAttachments["@value"] === "false") &&
        (!data.CaregiverStability["@value"] ||
          data.CaregiverStability["@value"] === "false") &&
        (!data.PlacementStability["@value"] ||
          data.PlacementStability["@value"] === "false") &&
        (!data.TiesToExtendedFamily["@value"] ||
          data.TiesToExtendedFamily["@value"] === "false") &&
        (!data.StrongCulturalIdentity["@value"] ||
          data.StrongCulturalIdentity["@value"] === "false") &&
        (!data.StrongSpiritualBeliefs["@value"] ||
          data.StrongSpiritualBeliefs["@value"] === "false") &&
        (!data.Pets["@value"] || data.Pets["@value"] === "false") &&
        (!data.Resilience["@value"] || data.Resilience["@value"] === "false") &&
        (!data.NoTraumaProtectiveFactors["@value"] ||
          data.NoTraumaProtectiveFactors["@value"] === "false")
      ) {
        if (errs)
          errs.TraumaProtectiveFactors =
            "At least one Protective Factor is required.";
        else
          errs = {
            TraumaProtectiveFactors:
              "At least one Protective Factor is required.",
          };
      }
    }

    if (
      data.PSDType["@value"] !== undefined &&
      data.PSDType["@value"] !== "" &&
      data.PSDType["@value"] !== "Psychiatric Diagnostic Evaluation" &&
      (!data.AppropriateAppearance["@value"] ||
        data.AppropriateAppearance["@value"] === "false") &&
      (!data.Neat["@value"] || data.Neat["@value"] === "false") &&
      (!data.Disheveled["@value"] || data.Disheveled["@value"] === "false") &&
      (!data.BizarreAppearance["@value"] ||
        data.BizarreAppearance["@value"] === "false") &&
      (!data.OtherAppearance["@value"] ||
        data.OtherAppearance["@value"] === "false")
    ) {
      if (errs) errs.MSAppearance = "At least one value is required.";
      else
        errs = {
          MSAppearance: "At least one value is required.",
        };
    }

    if (
      data.PSDType["@value"] !== undefined &&
      data.PSDType["@value"] !== "" &&
      data.PSDType["@value"] !== "Psychiatric Diagnostic Evaluation" &&
      (!data.Cooperative["@value"] || data.Cooperative["@value"] === "false") &&
      (!data.Guarded["@value"] || data.Guarded["@value"] === "false") &&
      (!data.Hyperactive["@value"] || data.Hyperactive["@value"] === "false") &&
      (!data.Agitated["@value"] || data.Agitated["@value"] === "false") &&
      (!data.Paranoid["@value"] || data.Paranoid["@value"] === "false") &&
      (!data.Tics["@value"] || data.Tics["@value"] === "false") &&
      (!data.Impulsive["@value"] || data.Impulsive["@value"] === "false") &&
      (!data.Aggressive["@value"] || data.Aggressive["@value"] === "false") &&
      (!data.Withdrawn["@value"] || data.Withdrawn["@value"] === "false") &&
      (!data.BizarreBehavior["@value"] ||
        data.BizarreBehavior["@value"] === "false") &&
      (!data.Rigidity["@value"] || data.Rigidity["@value"] === "false") &&
      (!data.OtherBehavior["@value"] ||
        data.OtherBehavior["@value"] === "false")
    ) {
      if (errs) errs.MSBehavior = "At least one value is required.";
      else
        errs = {
          MSBehavior: "At least one value is required.",
        };
    }

    if (
      data.PSDType["@value"] !== undefined &&
      data.PSDType["@value"] !== "" &&
      data.PSDType["@value"] !== "Psychiatric Diagnostic Evaluation" &&
      (!data.Normal["@value"] || data.Normal["@value"] === "false") &&
      (!data.Slurred["@value"] || data.Slurred["@value"] === "false") &&
      (!data.Pressured["@value"] || data.Pressured["@value"] === "false") &&
      (!data.Tangential["@value"] || data.Tangential["@value"] === "false") &&
      (!data.Incoherent["@value"] || data.Incoherent["@value"] === "false") &&
      (!data.Stutters["@value"] || data.Stutters["@value"] === "false") &&
      (!data.OtherSpeech["@value"] || data.OtherSpeech["@value"] === "false")
    ) {
      if (errs) errs.MSSpeech = "At least one value is required.";
      else
        errs = {
          MSSpeech: "At least one value is required.",
        };
    }

    if (
      data.PSDType["@value"] !== undefined &&
      data.PSDType["@value"] !== "" &&
      data.PSDType["@value"] !== "Psychiatric Diagnostic Evaluation" &&
      (!data.Euthymic["@value"] || data.Euthymic["@value"] === "false") &&
      (!data.Anxious["@value"] || data.Anxious["@value"] === "false") &&
      (!data.Angry["@value"] || data.Angry["@value"] === "false") &&
      (!data.Depressed["@value"] || data.Depressed["@value"] === "false") &&
      (!data.Euphoric["@value"] || data.Euphoric["@value"] === "false") &&
      (!data.Irritable["@value"] || data.Irritable["@value"] === "false") &&
      (!data.Fearful["@value"] || data.Fearful["@value"] === "false") &&
      (!data.OtherMood["@value"] || data.OtherMood["@value"] === "false")
    ) {
      if (errs) errs.MSMood = "At least one value is required.";
      else
        errs = {
          MSMood: "At least one value is required.",
        };
    }

    if (
      data.PSDType["@value"] !== undefined &&
      data.PSDType["@value"] !== "" &&
      data.PSDType["@value"] !== "Psychiatric Diagnostic Evaluation" &&
      (!data.FullRange["@value"] || data.FullRange["@value"] === "false") &&
      (!data.Restricted["@value"] || data.Restricted["@value"] === "false") &&
      (!data.Blunted["@value"] || data.Blunted["@value"] === "false") &&
      (!data.Flat["@value"] || data.Flat["@value"] === "false") &&
      (!data.Inappropriate["@value"] ||
        data.Inappropriate["@value"] === "false") &&
      (!data.OtherAffect["@value"] || data.OtherAffect["@value"] === "false")
    ) {
      if (errs) errs.MSAffect = "At least one value is required.";
      else
        errs = {
          MSAffect: "At least one value is required.",
        };
    }

    if (
      data.PSDType["@value"] !== undefined &&
      data.PSDType["@value"] !== "" &&
      data.PSDType["@value"] !== "Psychiatric Diagnostic Evaluation" &&
      (!data.Logical["@value"] || data.Logical["@value"] === "false") &&
      (!data.Preoccupations["@value"] ||
        data.Preoccupations["@value"] === "false") &&
      (!data.Delusions["@value"] || data.Delusions["@value"] === "false") &&
      (!data.Hallucinations["@value"] ||
        data.Hallucinations["@value"] === "false") &&
      (!data.Racing["@value"] || data.Racing["@value"] === "false") &&
      (!data.Disorganized["@value"] ||
        data.Disorganized["@value"] === "false") &&
      (!data.Concrete["@value"] || data.Concrete["@value"] === "false") &&
      (!data.SuicidalIdeation["@value"] ||
        data.SuicidalIdeation["@value"] === "false") &&
      (!data.HomicidalIdeation["@value"] ||
        data.HomicidalIdeation["@value"] === "false") &&
      (!data.OtherPerception["@value"] ||
        data.OtherPerception["@value"] === "false")
    ) {
      if (errs) errs.MSPerception = "At least one value is required.";
      else
        errs = {
          MSPerception: "At least one value is required.",
        };
    }

    if (
      data.PSDType["@value"] !== undefined &&
      data.PSDType["@value"] !== "" &&
      data.PSDType["@value"] !== "Psychiatric Diagnostic Evaluation" &&
      (!data.NoImpairment["@value"] ||
        data.NoImpairment["@value"] === "false") &&
      (!data.ShortAttentionSpan["@value"] ||
        data.ShortAttentionSpan["@value"] === "false") &&
      (!data.DifficultyConcentrating["@value"] ||
        data.DifficultyConcentrating["@value"] === "false") &&
      (!data.Distractibility["@value"] ||
        data.Distractibility["@value"] === "false") &&
      (!data.MemoryImpairment["@value"] ||
        data.MemoryImpairment["@value"] === "false") &&
      (!data.LowIntelligence["@value"] ||
        data.LowIntelligence["@value"] === "false") &&
      (!data.OtherCognition["@value"] ||
        data.OtherCognition["@value"] === "false")
    ) {
      if (errs) errs.MSCognition = "At least one value is required.";
      else
        errs = {
          MSCognition: "At least one value is required.",
        };
    }

    if (
      data.PSDType["@value"] !== undefined &&
      data.PSDType["@value"] !== "" &&
      data.PSDType["@value"] !== "Psychiatric Diagnostic Evaluation" &&
      (!data.AppropriateInsight["@value"] ||
        data.AppropriateInsight["@value"] === "false") &&
      (!data.Fair["@value"] || data.Fair["@value"] === "false") &&
      (!data.Impaired["@value"] || data.Impaired["@value"] === "false") &&
      (!data.OtherInsight["@value"] || data.OtherInsight["@value"] === "false")
    ) {
      if (errs) errs.MSInsight = "At least one value is required.";
      else
        errs = {
          MSInsight: "At least one value is required.",
        };
    }

    if (
      data.TraumaSymptoms["@value"] === "true" &&
      (!data.TraumaticExposure["@value"] ||
        data.TraumaticExposure["@value"] === "false") &&
      (!data.AvoidantTrauma["@value"] ||
        data.AvoidantTrauma["@value"] === "false") &&
      (!data.HypervigilanceTrauma["@value"] ||
        data.HypervigilanceTrauma["@value"] === "false") &&
      (!data.IntrusiveDreams["@value"] ||
        data.IntrusiveDreams["@value"] === "false") &&
      (!data.NegativeThoughts["@value"] ||
        data.NegativeThoughts["@value"] === "false") &&
      (!data.CocentrationProblems["@value"] ||
        data.CocentrationProblems["@value"] === "false") &&
      (!data.RecklessBehaviorTrauma["@value"] ||
        data.RecklessBehaviorTrauma["@value"] === "false") &&
      (!data.SleepDisturbancesTrauma["@value"] ||
        data.SleepDisturbancesTrauma["@value"] === "false") &&
      (!data.OtherTrauma["@value"] || data.OtherTrauma["@value"] === "false")
    ) {
      if (errs)
        errs.TraumaSymptoms = "At least one Trauma Symptom is required.";
      else
        errs = {
          TraumaSymptoms: "At least one Trauma Symptom is required.",
        };
    }

    if (
      data.AnxietyDisorder["@value"] === "true" &&
      (!data.AnxietyDisorderExcessWorry["@value"] ||
        data.AnxietyDisorderExcessWorry["@value"] === "false") &&
      (!data.AnxietyDisorderEasilyFatigued["@value"] ||
        data.AnxietyDisorderEasilyFatigued["@value"] === "false") &&
      (!data.MuscleTension["@value"] ||
        data.MuscleTension["@value"] === "false") &&
      (!data.Overworried["@value"] || data.Overworried["@value"] === "false") &&
      (!data.AnxietyDisorderDifficultyConcentrating["@value"] ||
        data.AnxietyDisorderDifficultyConcentrating["@value"] === "false") &&
      (!data.SleepDisturbancesAnxiety["@value"] ||
        data.SleepDisturbancesAnxiety["@value"] === "false") &&
      (!data.RestlessAnxiety["@value"] ||
        data.RestlessAnxiety["@value"] === "false") &&
      (!data.Irritability["@value"] ||
        data.Irritability["@value"] === "false") &&
      (!data.OtherAnxiety["@value"] || data.OtherAnxiety["@value"] === "false")
    ) {
      if (errs)
        errs.AnxietyDisorder = "At least one Anxiety Symptom is required.";
      else
        errs = {
          AnxietyDisorder: "At least one Anxiety Symptom is required.",
        };
    }

    if (
      data.DepressionSymptoms["@value"] === "true" &&
      (!data.DepressedMood["@value"] ||
        data.DepressedMood["@value"] === "false") &&
      (!data.SleepChanges["@value"] ||
        data.SleepChanges["@value"] === "false") &&
      (!data.Guilt["@value"] || data.Guilt["@value"] === "false") &&
      (!data.DiminishedInterest["@value"] ||
        data.DiminishedInterest["@value"] === "false") &&
      (!data.PurposelessMovement["@value"] ||
        data.PurposelessMovement["@value"] === "false") &&
      (!data.WeightChanges["@value"] ||
        data.WeightChanges["@value"] === "false") &&
      (!data.FatigueDepression["@value"] ||
        data.FatigueDepression["@value"] === "false") &&
      (!data.ThoughtsOfDeath["@value"] ||
        data.ThoughtsOfDeath["@value"] === "false") &&
      (!data.OtherDepression["@value"] ||
        data.OtherDepression["@value"] === "false")
    ) {
      if (errs)
        errs.DepressionSymptoms =
          "At least one Depression Symptom is required.";
      else
        errs = {
          DepressionSymptoms: "At least one Depression Symptom is required.",
        };
    }

    if (
      data.AttentionHyperactivity["@value"] === "true" &&
      (!data.AttentionHyperactivityEasilyDistracted["@value"] ||
        data.AttentionHyperactivityEasilyDistracted["@value"] === "false") &&
      (!data.DifficultyFollowThrough["@value"] ||
        data.DifficultyFollowThrough["@value"] === "false") &&
      (!data.Forgetful["@value"] || data.Forgetful["@value"] === "false") &&
      (!data.DifficultyListening["@value"] ||
        data.DifficultyListening["@value"] === "false") &&
      (!data.DisorganizedAttention["@value"] ||
        data.DisorganizedAttention["@value"] === "false") &&
      (!data.Fidgety["@value"] || data.Fidgety["@value"] === "false") &&
      (!data.LackOfDetails["@value"] ||
        data.LackOfDetails["@value"] === "false") &&
      (!data.LosesThings["@value"] || data.LosesThings["@value"] === "false") &&
      (!data.InterruptsOthers["@value"] ||
        data.InterruptsOthers["@value"] === "false") &&
      (!data.UnableToSitStill["@value"] ||
        data.UnableToSitStill["@value"] === "false") &&
      (!data.ExcessiveTalking["@value"] ||
        data.ExcessiveTalking["@value"] === "false") &&
      (!data.DifficultyWaiting["@value"] ||
        data.DifficultyWaiting["@value"] === "false") &&
      (!data.OtherAttention["@value"] ||
        data.OtherAttention["@value"] === "false")
    ) {
      if (errs)
        errs.AttentionHyperactivity =
          "At least one Attention and Hyperactivity Symptom is required.";
      else
        errs = {
          AttentionHyperactivity:
            "At least one Attention and Hyperactivity Symptom is required.",
        };
    }

    if (
      data.SU["@value"] === "true" &&
      (!data.PatternOfSubstanceUse["@value"] ||
        data.PatternOfSubstanceUse["@value"] === "false") &&
      (!data.DesiresCutDownUse["@value"] ||
        data.DesiresCutDownUse["@value"] === "false") &&
      (!data.TimeSpentObtaining["@value"] ||
        data.TimeSpentObtaining["@value"] === "false") &&
      (!data.Cravings["@value"] || data.Cravings["@value"] === "false") &&
      (!data.FailureOfResponsibilities["@value"] ||
        data.FailureOfResponsibilities["@value"] === "false") &&
      (!data.NegativeImpact["@value"] ||
        data.NegativeImpact["@value"] === "false") &&
      (!data.AbandonsActivities["@value"] ||
        data.AbandonsActivities["@value"] === "false") &&
      (!data.Tolerance["@value"] || data.Tolerance["@value"] === "false") &&
      (!data.SUWithdrawal["@value"] ||
        data.SUWithdrawal["@value"] === "false") &&
      (!data.SUOther["@value"] || data.SUOther["@value"] === "false")
    ) {
      if (errs) errs.SU = "At least one Substance Use Symptom is required.";
      else
        errs = {
          SU: "At least one Substance Use Symptom is required.",
        };
    }

    if (
      data.DMDD["@value"] === "true" &&
      (!data.Temper["@value"] || data.Temper["@value"] === "false") &&
      (!data.OutsideOutbursts["@value"] ||
        data.OutsideOutbursts["@value"] === "false") &&
      (!data.FrequentOutbursts["@value"] ||
        data.FrequentOutbursts["@value"] === "false") &&
      (!data.DMDDIrritable["@value"] ||
        data.DMDDIrritable["@value"] === "false") &&
      (!data.OutburstsAroundOthers["@value"] ||
        data.OutburstsAroundOthers["@value"] === "false") &&
      (!data.DMDDOther["@value"] || data.DMDDOther["@value"] === "false")
    ) {
      if (errs)
        errs.DMDD =
          "At least one Disruptive Mood Dysregulation Disorder Symptom is required.";
      else
        errs = {
          DMDD: "At least one Disruptive Mood Dysregulation Disorder Symptom is required.",
        };
    }

    if (
      data.ConductDisorder["@value"] === "true" &&
      (!data.Aggression["@value"] || data.Aggression["@value"] === "false") &&
      (!data.DestructionOfProperty["@value"] ||
        data.DestructionOfProperty["@value"] === "false") &&
      (!data.Theft["@value"] || data.Theft["@value"] === "false") &&
      (!data.SeriousViolations["@value"] ||
        data.SeriousViolations["@value"] === "false") &&
      (!data.OtherConductDisorder["@value"] ||
        data.OtherConductDisorder["@value"] === "false")
    ) {
      if (errs)
        errs.ConductDisorder =
          "At least one Conduct Disorder Symptom is required.";
      else
        errs = {
          ConductDisorder: "At least one Conduct Disorder Symptom is required.",
        };
    }

    if (
      data.ODD["@value"] === "true" &&
      (!data.IrritableODD["@value"] ||
        data.IrritableODD["@value"] === "false") &&
      (!data.Argumentative["@value"] ||
        data.Argumentative["@value"] === "false") &&
      (!data.Vindictive["@value"] || data.Vindictive["@value"] === "false") &&
      (!data.OtherODD["@value"] || data.OtherODD["@value"] === "false")
    ) {
      if (errs)
        errs.ODD =
          "At least one Oppositional Defiance Disorder Symptom is required.";
      else
        errs = {
          ODD: "At least one Oppositional Defiance Disorder Symptom is required.",
        };
    }

    if (
      data.OCD["@value"] === "true" &&
      (!data.OCDPresence["@value"] || data.OCDPresence["@value"] === "false") &&
      (!data.Obsessions["@value"] || data.Obsessions["@value"] === "false") &&
      (!data.TimeConsuming["@value"] ||
        data.TimeConsuming["@value"] === "false") &&
      (!data.Compulsions["@value"] || data.Compulsions["@value"] === "false") &&
      (!data.ReduceAnxiety["@value"] ||
        data.ReduceAnxiety["@value"] === "false") &&
      (!data.SuppressUrges["@value"] ||
        data.SuppressUrges["@value"] === "false") &&
      (!data.OCDOther["@value"] || data.OCDOther["@value"] === "false")
    ) {
      if (errs)
        errs.OCD =
          "At least one Obsessive-Compulsive Disorder Symptom is required.";
      else
        errs = {
          OCD: "At least one Obsessive-Compulsive Disorder Symptom is required.",
        };
    }

    if (
      data.PanicDisorder["@value"] === "true" &&
      (!data.PoundingHeart["@value"] ||
        data.PoundingHeart["@value"] === "false") &&
      (!data.PanicDisorderSweating["@value"] ||
        data.PanicDisorderSweating["@value"] === "false") &&
      (!data.Trembling["@value"] || data.Trembling["@value"] === "false") &&
      (!data.ShortnessOfBreath["@value"] ||
        data.ShortnessOfBreath["@value"] === "false") &&
      (!data.Choking["@value"] || data.Choking["@value"] === "false") &&
      (!data.ChestPain["@value"] || data.ChestPain["@value"] === "false") &&
      (!data.PanicDisorderNausea["@value"] ||
        data.PanicDisorderNausea["@value"] === "false") &&
      (!data.Dizzy["@value"] || data.Dizzy["@value"] === "false") &&
      (!data.Chills["@value"] || data.Chills["@value"] === "false") &&
      (!data.Derealization["@value"] ||
        data.Derealization["@value"] === "false") &&
      (!data.FearOfCrazy["@value"] || data.FearOfCrazy["@value"] === "false") &&
      (!data.FearOfDying["@value"] || data.FearOfDying["@value"] === "false") &&
      (!data.PanicDisorderOther["@value"] ||
        data.PanicDisorderOther["@value"] === "false")
    ) {
      if (errs)
        errs.PanicDisorder = "At least one Panic Disorder Symptom is required.";
      else
        errs = {
          PanicDisorder: "At least one Panic Disorder Symptom is required.",
        };
    }

    if (
      data.Phobias["@value"] === "true" &&
      (!data.PhobiasSpecificFear["@value"] ||
        data.PhobiasSpecificFear["@value"] === "false") &&
      (!data.ImmediateFear["@value"] ||
        data.ImmediateFear["@value"] === "false") &&
      (!data.PhobiasAvoidance["@value"] ||
        data.PhobiasAvoidance["@value"] === "false") &&
      (!data.FearExaggerated["@value"] ||
        data.FearExaggerated["@value"] === "false") &&
      (!data.PhobiasOther["@value"] || data.PhobiasOther["@value"] === "false")
    ) {
      if (errs) errs.Phobias = "At least one Phobia Symptom is required.";
      else
        errs = {
          Phobias: "At least one Phobia Symptom is required.",
        };
    }

    if (
      data.DI["@value"] === "true" &&
      (!data.DILowIQ["@value"] || data.DILowIQ["@value"] === "false") &&
      (!data.DISensoryImpairments["@value"] ||
        data.DISensoryImpairments["@value"] === "false") &&
      (!data.DISpeechDelays["@value"] ||
        data.DISpeechDelays["@value"] === "false") &&
      (!data.DIMotorCoordinationDelays["@value"] ||
        data.DIMotorCoordinationDelays["@value"] === "false") &&
      (!data.DILearningDisorder["@value"] ||
        data.DILearningDisorder["@value"] === "false") &&
      (!data.DISpatialDelays["@value"] ||
        data.DISpatialDelays["@value"] === "false") &&
      (!data.DevelopmentalImpairmentsOther["@value"] ||
        data.DevelopmentalImpairmentsOther["@value"] === "false")
    ) {
      if (errs) errs.DI = "At least one Developmental Impairment is required.";
      else
        errs = {
          DI: "At least one Developmental Impairment is required.",
        };
    }

    if (
      data.PSDType["@value"] === "Biopsychosocial Assessment" ||
      data.PSDType["@value"] === "Addendum/Annual Update"
    ) {
      if (
        (!data.DisciplinePracticesEffective["@value"] ||
          data.DisciplinePracticesEffective["@value"] === "false") &&
        (!data.DisciplinePracticesInconsistent["@value"] ||
          data.DisciplinePracticesInconsistent["@value"] === "false") &&
        (!data.DisciplinePracticesIneffective["@value"] ||
          data.DisciplinePracticesIneffective["@value"] === "false")
      ) {
        if (errs) errs.DisciplinePractices = "At least one value is required.";
        else
          errs = {
            DisciplinePractices: "At least one value is required.",
          };
      }
    }

    setErrors(errs);
    return errs;
  };

  const sectionsToNavigate = [
    { _id: "PresentingProblem", name: "Presenting Problem & Strengths" },
    { _id: "FamilyHistorySection", name: "Family Environment and History" },
    { _id: "Demographics", name: "Culture/Ethnicity" },
    { _id: "ReligionSpirituality", name: "Religion/Spirituality" },
    { _id: "DevelopmentalHistory", name: "Developmental History" },
    { _id: "Education", name: "Education/Employment" },
    { _id: "Activities", name: "Activities of Daily Living" },
    {
      _id: "Friendships",
      name: "Friendship/Social Peer Support/Relationships",
    },
    { _id: "LegalHistory", name: "Legal History" },
    { _id: "MedicalHistory", name: "Medical History/Medications" },
    {
      _id: "SexualHistory",
      name: "Sexual History to Include Pertinent Sexual Issues/Concerns",
    },
    {
      _id: "MentalHealthTreatmentHistory",
      name: "Mental Health Treatment History",
    },
    { _id: "SubstanceUseHistory", name: "Alcohol/Drug Use History" },
    { _id: "TraumaHistory", name: "Trauma History" },
    { _id: "MentalStatus", name: "Mental Status" },
    { _id: "Lethality", name: "Lethality" },
    { _id: "SymptomChecklist", name: "Symptom Checklist" },
    { _id: "OMHAS", name: "OHIO MENTAL HEALTH & ADDICTION SERVICES" },
    { _id: "ClinicalNarrative", name: "Clinical Narrative Summary" },
    { _id: "Diagnosis", name: "Diagnosis" },
    {
      _id: "TreatmentRecommendations",
      name: "Treatment Recommendations/Needs",
    },
  ];

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={(e) => {
            preSignForm();
            toggleSignatureModal(e);
          }}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          customValidate={customValidate}
          sectionsToNavigate={sectionsToNavigate}
          beforePrintFunction={(callback) => {
            handleExpandStatusChange(true, callback);
          }}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="row">
                <div className="col">
                  <FormSelect
                    name="SexAssignedAtBirth"
                    label="Sex Assigned at Birth:"
                    error={errors.SexAssignedAtBirth}
                    value={data.SexAssignedAtBirth["@value"]}
                    options={sexAtBirthOptions}
                    onChange={handleChange}
                    identical={compare("SexAssignedAtBirth")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {data.SexAssignedAtBirth["@value"] === "Sex not listed" && (
                    <FormInput
                      name="SexAssignedAtBirthDetails"
                      label="Please specify:"
                      error={errors.SexAssignedAtBirthDetails}
                      value={
                        data.SexAssignedAtBirthDetails !== undefined
                          ? data.SexAssignedAtBirthDetails["@value"]
                          : ""
                      }
                      onChange={handleChange}
                      identical={compare("SexAssignedAtBirthDetails")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  )}
                </div>
                <div className="col">
                  <FormSelect
                    name="Pronouns"
                    label="Pronouns:"
                    error={errors.Pronouns}
                    value={data.Pronouns["@value"]}
                    options={pronounOptions}
                    onChange={handleChange}
                    identical={compare("Pronouns")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {data.Pronouns["@value"] === "Prefer to self-describe" && (
                    <FormInput
                      name="PronounsDescription"
                      label="Please describe:"
                      error={errors.PronounsDescription}
                      value={
                        data.PronounsDescription !== undefined
                          ? data.PronounsDescription["@value"]
                          : ""
                      }
                      onChange={handleChange}
                      identical={compare("PronounsDescription")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  )}
                </div>
                <div className="col">
                  <FormSelect
                    name="GenderIdentity"
                    label="Gender Identity:"
                    error={errors.GenderIdentity}
                    value={data.GenderIdentity["@value"]}
                    options={genderIdentityOptions}
                    onChange={handleChange}
                    identical={compare("GenderIdentity")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {data.GenderIdentity["@value"] === "Identity not listed" && (
                    <FormInput
                      name="GenderIdentityDescription"
                      label="Please specify:"
                      error={errors.GenderIdentityDescription}
                      value={
                        data.GenderIdentityDescription !== undefined
                          ? data.GenderIdentityDescription["@value"]
                          : ""
                      }
                      onChange={handleChange}
                      identical={compare("GenderIdentityDescription")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <FormInput
                    name="DateCompleted"
                    label="Date Completed:"
                    type="date"
                    error={errors.DateCompleted}
                    value={data.DateCompleted["@value"]}
                    onChange={handleChange}
                    identical={compare("DateCompleted")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <FormSelect
                    name="Division"
                    label="Division:"
                    error={errors.Division}
                    value={data.Division["@value"]}
                    options={divisionOptions}
                    onChange={handleChange}
                    identical={compare("Division")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <FormRadioButton
                name="PSDType"
                label="Type of Evaluation/Assessment:"
                error={errors.PSDType}
                value={data.PSDType["@value"]}
                options={getPSDTypeOptions(data)}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.PSDType["@value"] === "Addendum/Annual Update" && (
                <>
                  <FormInput
                    name="InitialDADate"
                    label="Date of Initial DA:"
                    type="date"
                    error={errors.InitialDADate}
                    value={data.InitialDADate["@value"]}
                    onChange={handleChange}
                    identical={compare("InitialDADate")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <FormInput
                    name="UpdateSummary"
                    label="Update Summary:"
                    error={errors.UpdateSummary}
                    value={data.UpdateSummary["@value"]}
                    onChange={handleChange}
                    identical={compare("UpdateSummary")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    placeholder="Update on any changes in behaviors and or symptoms/new information from out-side providers on Dx, R/O of Dx, either added or taken off"
                  />
                  <FormInput
                    name="UpdateRecommendations"
                    label="Update Recommendations:"
                    error={errors.UpdateRecommendations}
                    value={data.UpdateRecommendations["@value"]}
                    onChange={handleChange}
                    identical={compare("UpdateRecommendations")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    placeholder="Update recommendations and or referrals/update Care Plan to ensure any changes in Dx is identified on care plan"
                  />
                </>
              )}
              <b>Information Sources:</b>
              <div className="container">
                <div className="row">
                  <div className="col">
                    <FormCheckbox
                      name="ISClient"
                      label="Client"
                      checked={data.ISClient["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISParent"
                      label="Parent/Other"
                      checked={data.ISParent["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISGuardian"
                      label="Guardian"
                      checked={data.ISGuardian["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISCaregiver"
                      label="Non-parent Caregiver"
                      checked={data.ISCaregiver["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormCheckbox
                      name="ISPhysician"
                      label="Physician"
                      checked={data.ISPhysician["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISRecords"
                      label="Records"
                      checked={data.ISRecords["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISServiceProvider"
                      label="Service Provider"
                      checked={data.ISServiceProvider["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISSchool"
                      label="School"
                      checked={data.ISSchool["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormCheckbox
                      name="ISCourt"
                      label="Court"
                      checked={data.ISCourt["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISCPS"
                      label="CPS"
                      checked={data.ISCPS["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISOther"
                      label="Other"
                      checked={data.ISOther["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    {data.ISOther["@value"] === "true" ? (
                      <FormInput
                        name="ISOtherDetails"
                        error={errors.ISOtherDetails}
                        value={data.ISOtherDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("ISOtherDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                  </div>
                </div>
              </div>
              {errors.InformationSources !== undefined ? (
                <div className="row">
                  <div className="col">
                    <div className="alert alert-danger">
                      {errors.InformationSources}
                    </div>
                  </div>
                </div>
              ) : null}
              <div className="clearfix">
                <div className="float-start">
                  <button
                    className="btn btn-primary mb-2"
                    onClick={(e) => {
                      handleExpandStatusChange();
                    }}
                  >
                    {expandStatus === true ? "Collapse All" : "Expand All"}
                  </button>
                </div>
                <div className="float-end d-flex">
                  <div className="me-2">Clinical Narrative Summary:</div>
                  <ToggleSwitch
                    values={["Show", "Hide"]}
                    selected={showNarrative}
                    onChange={setShowNarrative}
                  />
                </div>
              </div>
              <ExpandDiv
                id="PresentingProblem"
                expandStatus={expandStatus}
                className="mt-2"
                title="Strengths and Presenting Problem"
              >
                <FormInput
                  name="ReasonForReferral"
                  label="Reason for Referral:"
                  error={errors.ReasonForReferral}
                  value={data.ReasonForReferral["@value"]}
                  onChange={handleChange}
                  identical={compare("ReasonForReferral")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormInput
                  name="ClientStrengths"
                  label="Client strengths, abilities, and skills (according to client):"
                  error={errors.ClientStrengths}
                  value={data.ClientStrengths["@value"]}
                  onChange={handleChange}
                  identical={compare("ClientStrengths")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormInput
                  name="PresentingProblem"
                  label="Treatment Issues and Presenting Problem (according to client):"
                  error={errors.PresentingProblem}
                  value={data.PresentingProblem["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("PresentingProblem")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormRadioButton
                  name="HumanTrafficking"
                  label="Does the client have a history of or is at risk of human trafficking?"
                  error={errors.HumanTrafficking}
                  value={data.HumanTrafficking["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.HumanTrafficking !== undefined &&
                data.HumanTrafficking["@value"] === "Yes" ? (
                  <>
                    Please complete the{" "}
                    <Link
                      to={`/client/${clientid}?tab=cpass`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Human Trafficking Screener and Assessment
                    </Link>
                    .
                  </>
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="FamilyHistorySection"
                expandStatus={expandStatus}
                className="mt-2"
                title="Family Environment and History"
              >
                {data.PSDType["@value"] !==
                  "Psychiatric Diagnostic Evaluation" && (
                  <>
                    <FormInput
                      name="CurrentPlacement"
                      label="Current Placement (including relevant family/environmental factors or alternative living situations):"
                      error={errors.CurrentPlacement}
                      value={data.CurrentPlacement["@value"]}
                      onChange={handleChange}
                      identical={compare("CurrentPlacement")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormInput
                      name="OtherSupports"
                      label="Other Family Members/Supports for child (not in current placement):"
                      error={errors.OtherSupports}
                      value={data.OtherSupports["@value"]}
                      onChange={handleChange}
                      identical={compare("OtherSupports")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <div className="row pt-2 border-bottom">
                      <div className="col">
                        Cooperation between caregivers on discipline and care:
                      </div>
                      <div className="col">
                        <FormRadioButton
                          name="CaregiverCooperation"
                          value={data.CaregiverCooperation["@value"]}
                          options={[{ _id: "Yes", name: "Yes" }]}
                          onChange={handleChange}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        <FormRadioButton
                          name="CaregiverCooperation"
                          value={data.CaregiverCooperation["@value"]}
                          options={[{ _id: "No", name: "No" }]}
                          onChange={handleChange}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col"></div>
                    </div>
                    {errors.CaregiverCooperation !== undefined ? (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.CaregiverCooperation}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <div className="row pt-2 border-bottom">
                      <div className="col">
                        Difficulties with the supervision/monitoring of child:
                      </div>
                      <div className="col">
                        <FormRadioButton
                          name="SupervisionDifficulties"
                          value={data.SupervisionDifficulties["@value"]}
                          options={[{ _id: "Yes", name: "Yes" }]}
                          onChange={handleChange}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        <FormRadioButton
                          name="SupervisionDifficulties"
                          value={data.SupervisionDifficulties["@value"]}
                          options={[{ _id: "No", name: "No" }]}
                          onChange={handleChange}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col"></div>
                    </div>
                    {errors.SupervisionDifficulties !== undefined ? (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.SupervisionDifficulties}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <div className="row pt-2 border-bottom">
                      <div className="col">Discipline Practices:</div>
                      <div className="col">
                        <FormCheckbox
                          name="DisciplinePracticesEffective"
                          label="Effective"
                          checked={
                            data.DisciplinePracticesEffective["@value"] ===
                            "true"
                          }
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        <FormCheckbox
                          name="DisciplinePracticesInconsistent"
                          label="Inconsistent"
                          checked={
                            data.DisciplinePracticesInconsistent["@value"] ===
                            "true"
                          }
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        <FormCheckbox
                          name="DisciplinePracticesIneffective"
                          label="Ineffective"
                          checked={
                            data.DisciplinePracticesIneffective["@value"] ===
                            "true"
                          }
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                    {errors.DisciplinePractices !== undefined ? (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.DisciplinePractices}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    {data.CaregiverCooperation["@value"] === "No" ||
                    data.SupervisionDifficulties["@value"] === "Yes" ||
                    data.DisciplinePracticesInconsistent["@value"] === "true" ||
                    data.DisciplinePracticesIneffective["@value"] === "true" ? (
                      <FormInput
                        name="CurrentPlacementComments"
                        label="Comments:"
                        error={errors.CurrentPlacementComments}
                        value={data.CurrentPlacementComments["@value"]}
                        onChange={handleChange}
                        identical={compare("CurrentPlacementComments")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                  </>
                )}
                <b>Family History</b>
                <FieldTable
                  name="FamilyHistoryDetails"
                  commentsHandler={handleComments}
                >
                  <div className="container border mb-2">
                    <div className="row border-bottom">
                      <div className="col-2 border-end py-2">Family Member</div>
                      <div className="col border-end py-2">Concern/Issue</div>
                      <div className="col py-2">Explanation/Comments</div>
                    </div>
                    {data.FamilyHistoryDetails["@value"] &&
                      JSON.parse(data.FamilyHistoryDetails["@value"]).map(
                        (item, index) => (
                          <div key={item._id} className="row border-bottom">
                            <div className="col-2 border-end py-2 d-flex">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <i
                                    className="fas fa-save me-2 d-print-none"
                                    onClick={() => {
                                      handleSaveRow(
                                        index,
                                        "FamilyHistoryDetails"
                                      );
                                    }}
                                  ></i>
                                  <i
                                    className="far fa-trash-alt me-2 d-print-none"
                                    onClick={() => {
                                      handleDeleteRow(
                                        index,
                                        "FamilyHistoryDetails"
                                      );
                                    }}
                                  ></i>
                                  <FormSelect
                                    name="FamilyMember"
                                    value={item.FamilyMember}
                                    options={familyMemberOptions}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "FamilyHistoryDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(
                                        index,
                                        "FamilyHistoryDetails"
                                      );
                                    }}
                                  ></i>
                                  {item.FamilyMember}
                                </>
                              )}
                            </div>
                            <div className="col border-end py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <div className="container">
                                  <div className="row">
                                    <div className="col">
                                      <FormCheckbox
                                        name={`MentalHealth-${index}`}
                                        label="Mental Health"
                                        checked={item.MentalHealth === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "FamilyHistoryDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </div>
                                    <div className="col">
                                      <FormCheckbox
                                        name={`Medical-${index}`}
                                        label="Medical"
                                        checked={item.Medical === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "FamilyHistoryDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </div>
                                  </div>
                                  <div className="row">
                                    <div className="col">
                                      <FormCheckbox
                                        name={`Disability-${index}`}
                                        label="Disability"
                                        checked={item.Disability === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "FamilyHistoryDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </div>
                                    <div className="col">
                                      <FormCheckbox
                                        name={`SubstanceAbuse-${index}`}
                                        label="Substance Abuse"
                                        checked={item.SubstanceAbuse === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "FamilyHistoryDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </div>
                                  </div>
                                  <div className="row">
                                    <div className="col">
                                      <FormCheckbox
                                        name={`Suicidal-${index}`}
                                        label="Suicidal"
                                        checked={item.Suicidal === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "FamilyHistoryDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </div>
                                    <div className="col">
                                      <FormCheckbox
                                        name={`Legal-${index}`}
                                        label="Legal"
                                        checked={item.Legal === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "FamilyHistoryDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </div>
                                  </div>
                                  <div className="row">
                                    <div className="col">
                                      <FormCheckbox
                                        name={`Trauma-${index}`}
                                        label="Trauma"
                                        checked={item.Trauma === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "FamilyHistoryDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </div>
                                    <div className="col">
                                      <FormCheckbox
                                        name={`Perpetration-${index}`}
                                        label="Perpetration"
                                        checked={item.Perpetration === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "FamilyHistoryDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </div>
                                  </div>
                                  <div className="row">
                                    <div className="col">
                                      <FormCheckbox
                                        name={`Other-${index}`}
                                        label="Other"
                                        checked={item.Other === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "FamilyHistoryDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </div>
                                    <div className="col">
                                      <FormCheckbox
                                        name={`NoConcerns-${index}`}
                                        label="No Concerns / Issues"
                                        checked={item.NoConcerns === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "FamilyHistoryDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                getFamilyMemberConcerns(item)
                              )}
                            </div>
                            <div className="col py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <FormTextArea
                                  name="Comments"
                                  value={item.Comments}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "FamilyHistoryDetails"
                                    );
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Comments
                              )}
                            </div>
                          </div>
                        )
                      )}
                  </div>
                  {!formDisabled ? (
                    <button
                      className="btn btn-primary mb-2"
                      onClick={() => {
                        handleAddRow("FamilyHistoryDetails", {
                          IsEditable: true,
                        });
                      }}
                    >
                      Add Individual
                    </button>
                  ) : null}
                  {errors.FamilyHistoryDetails !== undefined ? (
                    <div className="alert alert-danger">
                      {errors.FamilyHistoryDetails}
                    </div>
                  ) : null}
                </FieldTable>
                <div className="container">
                  <div className="row pt-2 border-bottom">
                    <div className="col-3">Concerns of Domestic Violence:</div>
                    <div className="col-2">
                      <FormCheckbox
                        name="CurrentConcernsOfDV"
                        label="Current"
                        checked={data.CurrentConcernsOfDV["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-2">
                      <FormCheckbox
                        name="PastConcernsOfDV"
                        label="Past"
                        checked={data.PastConcernsOfDV["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-2">
                      <FormCheckbox
                        name="UnknownConcernsOfDV"
                        label="Unknown"
                        checked={data.UnknownConcernsOfDV["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-3">
                      <FormCheckbox
                        name="NoReportedConcernsOfDV"
                        label="None reported"
                        checked={
                          data.NoReportedConcernsOfDV["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  {errors.DomesticViolenceConcerns !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.DomesticViolenceConcerns}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row pt-2 border-bottom">
                    <div className="col-3">Marital/Couples conflict:</div>
                    <div className="col-2">
                      <FormCheckbox
                        name="CurrentMaritalConflict"
                        label="Current"
                        checked={
                          data.CurrentMaritalConflict["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-2">
                      <FormCheckbox
                        name="PastMaritalConflict"
                        label="Past"
                        checked={data.PastMaritalConflict["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-2">
                      <FormCheckbox
                        name="UnknownMaritalConflict"
                        label="Unknown"
                        checked={
                          data.UnknownMaritalConflict["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-3">
                      <FormCheckbox
                        name="NoReportedMaritalConflict"
                        label="None reported"
                        checked={
                          data.NoReportedMaritalConflict["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  {errors.CouplesConflict !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.CouplesConflict}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  {data.CurrentConcernsOfDV["@value"] === "true" ||
                  data.PastConcernsOfDV["@value"] === "true" ||
                  data.CurrentMaritalConflict["@value"] === "true" ||
                  data.PastMaritalConflict["@value"] === "true" ? (
                    <div className="row pt-2">
                      <div className="col-3">Comments:</div>
                      <div className="col-9">
                        <FormTextArea
                          name="ConflictComments"
                          error={errors.ConflictComments}
                          value={data.ConflictComments["@value"]}
                          onChange={handleChange}
                          identical={compare("ConflictComments")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              </ExpandDiv>
              {data.PSDType["@value"] !==
              "Psychiatric Diagnostic Evaluation" ? (
                <>
                  <ExpandDiv
                    id="Demographics"
                    expandStatus={expandStatus}
                    className="mt-2"
                    title="Culture/Ethnicity"
                  >
                    <div className="container">
                      <div className="row">
                        <div className="col">Identified cultural group:</div>
                        <div className="col">
                          <FormInput
                            name="CulturalGroup"
                            error={errors.CulturalGroup}
                            value={data.CulturalGroup["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("CulturalGroup")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            placeholder="Culture broadly refers to all the ways an individual understands their identity in terms of race, ethnicity, gender, sexual orientation, social class, religion/spirituality, language, and places lived..."
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          Special cultural traditions/celebrations/rituals:
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="CulturalTraditions"
                            error={errors.CulturalTraditions}
                            value={data.CulturalTraditions["@value"]}
                            options={yesNoNoneOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.CulturalTraditions["@value"] === "Yes" ? (
                            <FormInput
                              name="CulturalTraditionsDetails"
                              label="Describe:"
                              error={errors.CulturalTraditionsDetails}
                              value={data.CulturalTraditionsDetails["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("CulturalTraditionsDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          Negative experiences due to
                          customs/ethnicity/race/SES:
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="NegativeCulturalExperiences"
                            error={errors.NegativeCulturalExperiences}
                            value={data.NegativeCulturalExperiences["@value"]}
                            options={yesNoNoneOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.NegativeCulturalExperiences["@value"] ===
                          "Yes" ? (
                            <FormInput
                              name="NegativeCulturalExperiencesDetails"
                              label="Describe:"
                              error={errors.NegativeCulturalExperiencesDetails}
                              value={
                                data.NegativeCulturalExperiencesDetails[
                                  "@value"
                                ]
                              }
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare(
                                "NegativeCulturalExperiencesDetails"
                              )}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          Cultural beliefs that influence medical/mental health
                          decisions:
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="CulturalMedicalBeliefs"
                            error={errors.CulturalMedicalBeliefs}
                            value={data.CulturalMedicalBeliefs["@value"]}
                            options={yesNoNoneOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.CulturalMedicalBeliefs["@value"] === "Yes" ? (
                            <FormInput
                              name="CulturalMedicalBeliefsDetails"
                              label="Describe:"
                              error={errors.CulturalMedicalBeliefsDetails}
                              value={
                                data.CulturalMedicalBeliefsDetails["@value"]
                              }
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare(
                                "CulturalMedicalBeliefsDetails"
                              )}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </ExpandDiv>
                  <ExpandDiv
                    id="ReligionSpirituality"
                    expandStatus={expandStatus}
                    className="mt-2"
                    title="Religion/Spirituality"
                  >
                    <div className="container">
                      <div className="row">
                        <div className="col">
                          Does the youth have an identified religion/belief
                          system/spiritually:
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="Religion"
                            error={errors.Religion}
                            value={data.Religion["@value"]}
                            options={yesNoNoneUKOptions}
                            onChange={handleChange}
                            identical={compare("Religion")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      {data.Religion["@value"] === "Yes" && (
                        <>
                          <div className="row">
                            <div className="col">
                              Role of faith in client's life:
                            </div>
                            <div className="col">
                              <FormInput
                                name="RoleOfFaith"
                                error={errors.RoleOfFaith}
                                value={data.RoleOfFaith["@value"]}
                                onChange={handleChange}
                                identical={compare("RoleOfFaith")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              Special religious traditions/celebrations/rituals:
                            </div>
                            <div className="col">
                              <FormRadioButton
                                name="ReligiousTraditions"
                                error={errors.ReligiousTraditions}
                                value={data.ReligiousTraditions["@value"]}
                                options={yesNoNoneOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              Specific dietary needs or restrictions
                              (religious):
                            </div>
                            <div className="col">
                              <FormRadioButton
                                name="DietaryNeeds"
                                error={errors.DietaryNeeds}
                                value={data.DietaryNeeds["@value"]}
                                options={yesNoNoneOptions}
                                onChange={handleChange}
                                identical={compare("DietaryNeeds")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              Negative experiences due to religious/spiritual
                              beliefs:
                            </div>
                            <div className="col">
                              <FormRadioButton
                                name="NegativeReligiousExperiences"
                                error={errors.NegativeReligiousExperiences}
                                value={
                                  data.NegativeReligiousExperiences["@value"]
                                }
                                options={yesNoNoneOptions}
                                onChange={handleChange}
                                identical={compare(
                                  "NegativeReligiousExperiences"
                                )}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              Spiritual beliefs that influence medical/mental
                              health decisions:
                            </div>
                            <div className="col">
                              <FormRadioButton
                                name="ReligiousMedicalBeliefs"
                                error={errors.ReligiousMedicalBeliefs}
                                value={data.ReligiousMedicalBeliefs["@value"]}
                                options={yesNoNoneOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                        </>
                      )}
                      {data.ReligiousTraditions["@value"] === "Yes" ||
                      data.DietaryNeeds["@value"] === "Yes" ||
                      data.NegativeReligiousExperiences["@value"] === "Yes" ||
                      data.ReligiousMedicalBeliefs["@value"] === "Yes" ? (
                        <div className="row">
                          <div className="col">Comments:</div>
                          <div className="col">
                            <FormInput
                              name="ReligiousComments"
                              error={errors.ReligiousComments}
                              value={data.ReligiousComments["@value"]}
                              onChange={handleChange}
                              identical={compare("ReligiousComments")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </ExpandDiv>
                  <ExpandDiv
                    id="DevelopmentalHistory"
                    expandStatus={expandStatus}
                    className="mt-2"
                    title="Developmental History"
                  >
                    <b>Mother's Pregnancy History:</b>
                    <FormCheckbox
                      name="PHNoProblems"
                      label="No problems reported"
                      divClassName="ms-3"
                      checked={data.PHNoProblems["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <div className="container">
                      {data.PHNoProblems["@value"] === "false" && (
                        <>
                          <div className="row">
                            <div className="col">Prenatal Care:</div>
                            <div className="col">
                              <FormRadioButton
                                name="PHPrenatalCare"
                                error={errors.PHPrenatalCare}
                                value={data.PHPrenatalCare["@value"]}
                                options={yesNoUnknownOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              Exposure to alcohol, tobacco, or other drugs:
                            </div>
                            <div className="col">
                              <FormRadioButton
                                name="PHExposure"
                                error={errors.PHExposure}
                                value={data.PHExposure["@value"]}
                                options={yesNoUnknownOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              History of DV or significant trauma during
                              pregnancy:
                            </div>
                            <div className="col">
                              <FormRadioButton
                                name="PHTrauma"
                                error={errors.PHTrauma}
                                value={data.PHTrauma["@value"]}
                                options={yesNoUnknownOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">Birth complications:</div>
                            <div className="col">
                              <FormRadioButton
                                name="PHComplications"
                                error={errors.PHComplications}
                                value={data.PHComplications["@value"]}
                                options={yesNoUnknownOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">Medical concerns:</div>
                            <div className="col">
                              <FormRadioButton
                                name="PHConcerns"
                                error={errors.PHConcerns}
                                value={data.PHConcerns["@value"]}
                                options={yesNoUnknownOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          {data.PHPrenatalCare["@value"] === "Yes" ||
                          data.PHExposure["@value"] === "Yes" ||
                          data.PHTrauma["@value"] === "Yes" ||
                          data.PHComplications["@value"] === "Yes" ||
                          data.PHConcerns["@value"] === "Yes" ? (
                            <div className="row">
                              <div className="col">Describe:</div>
                              <div className="col">
                                <FormTextArea
                                  name="PHComments"
                                  error={errors.PHComments}
                                  value={data.PHComments["@value"]}
                                  onChange={handleChange}
                                  identical={compare("PHComments")}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                          ) : null}
                        </>
                      )}
                      <br />
                      <div className="row">
                        <div className="col">
                          <b>Developmental delays or special needs:</b>
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="SpecialNeeds"
                            error={errors.SpecialNeeds}
                            value={data.SpecialNeeds["@value"]}
                            options={yesNoUnknownOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <b>Specialized Services</b>{" "}
                          (Speech/Occupational/Physical Therapy):
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="SpecializedServices"
                            error={errors.SpecializedServices}
                            value={data.SpecializedServices["@value"]}
                            options={yesNoUnknownOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>

                      {data.SpecialNeeds["@value"] === "Yes" ||
                      data.SpecializedServices["@value"] === "Yes" ? (
                        <div className="row">
                          <div className="col">Comments:</div>
                          <div className="col">
                            <FormTextArea
                              name="DHComments"
                              error={errors.DHComments}
                              value={data.DHComments["@value"]}
                              onChange={handleChange}
                              identical={compare("DHComments")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </ExpandDiv>
                </>
              ) : null}
              <ExpandDiv
                id="Education"
                expandStatus={expandStatus}
                className="mt-2"
                title="Education/Employment"
              >
                <FormRadioButton
                  name="EducationStatus"
                  error={errors.EducationStatus}
                  value={data.EducationStatus["@value"]}
                  options={educationOptions}
                  onChange={handleEducationStatusChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.EducationStatus["@value"] !==
                "Client has not started school" ? (
                  <>
                    {data.EducationStatus["@value"] ===
                    "Currently in school" ? (
                      <>
                        <b>Education</b>
                      </>
                    ) : null}
                    <div className="container">
                      {data.EducationStatus["@value"] ===
                      "Currently in school" ? (
                        <>
                          <div className="row">
                            <div className="col">School Name:</div>
                            <div className="col">
                              <FormInput
                                name="SchoolName"
                                error={errors.SchoolName}
                                value={data.SchoolName["@value"]}
                                onChange={handleChange}
                                identical={compare("SchoolName")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">Current Grade:</div>
                            <div className="col">
                              <FormInput
                                name="CurrentGrade"
                                error={errors.CurrentGrade}
                                value={data.CurrentGrade["@value"]}
                                onChange={handleChange}
                                identical={compare("CurrentGrade")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">Regular Classroom:</div>
                            <div className="col">
                              <FormRadioButton
                                name="RegularClassroom"
                                error={errors.RegularClassroom}
                                value={data.RegularClassroom["@value"]}
                                options={yesNoOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <>Active 504 Plan:</>
                            </div>
                            <div className="col">
                              <FormRadioButton
                                name="Active504Plan"
                                error={errors.Active504Plan}
                                value={data.Active504Plan["@value"]}
                                options={yesNoOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-3">
                              <>Active IEP:</>
                            </div>
                            <div className="col-3">
                              <FormRadioButton
                                name="ActiveIEP"
                                error={errors.ActiveIEP}
                                value={data.ActiveIEP["@value"]}
                                options={yesNoOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                        </>
                      ) : null}
                      {data.PSDType["@value"] !==
                      "Psychiatric Diagnostic Evaluation" ? (
                        <>
                          <div className="row">
                            <div className="col">
                              Summary of Client's School Experience (e.g.,
                              achievements, discipline):
                            </div>
                            <div className="col">
                              <FormTextArea
                                name="SchoolExperience"
                                error={errors.SchoolExperience}
                                value={data.SchoolExperience["@value"]}
                                onChange={handleChange}
                                identical={compare("SchoolExperience")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <b>Employment</b>
                          <FormCheckbox
                            name="EmploymentNA"
                            label="N/A"
                            divClassName="ms-3"
                            checked={data.EmploymentNA["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                          {data.EmploymentNA["@value"] !== "true" && (
                            <>
                              <FormRadioButton
                                name="CurrentlyEmployed"
                                label="Currently Employed:"
                                error={errors.CurrentlyEmployed}
                                value={data.CurrentlyEmployed["@value"]}
                                options={yesNoNAOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                              {data.CurrentlyEmployed["@value"] === "Yes" && (
                                <>
                                  <FormInput
                                    name="EmployerAndTitle"
                                    label="Name of employer &amp; title"
                                    error={errors.EmployerAndTitle}
                                    value={data.EmployerAndTitle["@value"]}
                                    onChange={handleChange}
                                    identical={compare("EmployerAndTitle")}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                </>
                              )}
                              {data.CurrentlyEmployed["@value"] !== "N/A" && (
                                <FormInput
                                  name="EmploymentInterests"
                                  label="Employment Interests/Skills/Concerns:"
                                  error={errors.EmploymentInterests}
                                  value={data.EmploymentInterests["@value"]}
                                  onChange={handleChange}
                                  identical={compare("EmploymentInterests")}
                                  disabled={formDisabled}
                                />
                              )}
                              <FormRadioButton
                                name="MilitaryHistory"
                                label="Military History:"
                                error={errors.MilitaryHistory}
                                value={data.MilitaryHistory["@value"]}
                                options={yesNoNAOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                              {data.MilitaryHistory["@value"] === "Yes" ? (
                                <FormTextArea
                                  name="MilitaryComments"
                                  label="Comments:"
                                  error={errors.MilitaryComments}
                                  value={data.MilitaryComments["@value"]}
                                  onChange={handleChange}
                                  identical={compare("MilitaryComments")}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              ) : null}
                            </>
                          )}
                        </>
                      ) : null}
                    </div>
                  </>
                ) : null}
              </ExpandDiv>
              {data.PSDType["@value"] !==
              "Psychiatric Diagnostic Evaluation" ? (
                <>
                  <ExpandDiv
                    id="Activities"
                    expandStatus={expandStatus}
                    className="mt-2"
                    title="Activities of Daily Living"
                  >
                    <FormCheckbox
                      name="ADLNA"
                      label="N/A (Client is at a developmental age (under age 5) requiring caregiver to provide ADLs on their behalf)"
                      divClassName="ms-3"
                      checked={data.ADLNA["@value"] === "true"}
                      commentsHandler={handleComments}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                    {data.ADLNA["@value"] !== "true" ? (
                      <>
                        <button
                          className="btn btn-link"
                          onClick={() => {
                            const obj = { ...data };

                            obj.MoneyManagement["@value"] = "Age Appropriate";
                            obj.FoodManagement["@value"] = "Age Appropriate";
                            obj.Hygiene["@value"] = "Age Appropriate";
                            obj.Health["@value"] = "Age Appropriate";
                            obj.Housekeeping["@value"] = "Age Appropriate";
                            obj.Transportation["@value"] = "Age Appropriate";
                            obj.SafetySkills["@value"] = "Age Appropriate";
                            obj.ResourceKnowledge["@value"] = "Age Appropriate";
                            obj.TimeManagement["@value"] = "Age Appropriate";
                            obj.Interaction["@value"] = "Age Appropriate";

                            setData(obj);
                          }}
                        >
                          Apply "Age Appropriate" to all
                        </button>
                        <br />
                        Does the client require additional support in the daily
                        functioning and daily activity skills listed below?
                        <div className="container">
                          <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mb-2">
                            <div className="col">
                              <b>Skill</b>
                            </div>
                            <div className="col">
                              <b>Functioning Level</b>
                            </div>
                            <div className="col">
                              <b>Explanation</b>
                            </div>
                          </div>
                          <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                            <div className="col">Money management:</div>
                            <div className="col">
                              <FormSelect
                                name="MoneyManagement"
                                error={errors.MoneyManagement}
                                value={data.MoneyManagement["@value"]}
                                options={functioningLevelOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              {data.MoneyManagement["@value"] === "Regressed" ||
                              data.MoneyManagement["@value"] === "Advanced" ? (
                                <FormInput
                                  name="MoneyManagementDetails"
                                  error={errors.MoneyManagementDetails}
                                  value={data.MoneyManagementDetails["@value"]}
                                  onChange={handleChange}
                                  identical={compare("MoneyManagementDetails")}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              ) : null}
                            </div>
                          </div>
                          <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                            <div className="col">Food management:</div>
                            <div className="col">
                              <FormSelect
                                name="FoodManagement"
                                error={errors.FoodManagement}
                                value={data.FoodManagement["@value"]}
                                options={functioningLevelOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              {data.FoodManagement["@value"] === "Regressed" ||
                              data.FoodManagement["@value"] === "Advanced" ? (
                                <FormInput
                                  name="FoodManagementDetails"
                                  error={errors.FoodManagementDetails}
                                  value={data.FoodManagementDetails["@value"]}
                                  onChange={handleChange}
                                  identical={compare("FoodManagementDetails")}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              ) : null}
                            </div>
                          </div>
                          <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                            <div className="col">
                              Personal appearance and hygiene:
                            </div>
                            <div className="col">
                              <FormSelect
                                name="Hygiene"
                                error={errors.Hygiene}
                                value={data.Hygiene["@value"]}
                                options={functioningLevelOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              {data.Hygiene["@value"] === "Regressed" ||
                              data.Hygiene["@value"] === "Advanced" ? (
                                <FormInput
                                  name="HygieneDetails"
                                  error={errors.HygieneDetails}
                                  value={data.HygieneDetails["@value"]}
                                  onChange={handleChange}
                                  identical={compare("HygieneDetails")}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              ) : null}
                            </div>
                          </div>
                          <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                            <div className="col">
                              Health awareness/Personal Health:
                            </div>
                            <div className="col">
                              <FormSelect
                                name="Health"
                                error={errors.Health}
                                value={data.Health["@value"]}
                                options={functioningLevelOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              {data.Health["@value"] === "Regressed" ||
                              data.Health["@value"] === "Advanced" ? (
                                <FormInput
                                  name="HealthDetails"
                                  error={errors.HealthDetails}
                                  value={data.HealthDetails["@value"]}
                                  onChange={handleChange}
                                  identical={compare("HealthDetails")}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              ) : null}
                            </div>
                          </div>
                          <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                            <div className="col">Housekeeping:</div>
                            <div className="col">
                              <FormSelect
                                name="Housekeeping"
                                error={errors.Housekeeping}
                                value={data.Housekeeping["@value"]}
                                options={functioningLevelOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              {data.Housekeeping["@value"] === "Regressed" ||
                              data.Housekeeping["@value"] === "Advanced" ? (
                                <FormInput
                                  name="HousekeepingDetails"
                                  error={errors.HousekeepingDetails}
                                  value={data.HousekeepingDetails["@value"]}
                                  onChange={handleChange}
                                  identical={compare("HousekeepingDetails")}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              ) : null}
                            </div>
                          </div>
                          <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                            <div className="col">Transportation:</div>
                            <div className="col">
                              <FormSelect
                                name="Transportation"
                                error={errors.Transportation}
                                value={data.Transportation["@value"]}
                                options={functioningLevelOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              {data.Transportation["@value"] === "Regressed" ||
                              data.Transportation["@value"] === "Advanced" ? (
                                <FormInput
                                  name="TransportationDetails"
                                  error={errors.TransportationDetails}
                                  value={data.TransportationDetails["@value"]}
                                  onChange={handleChange}
                                  identical={compare("TransportationDetails")}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              ) : null}
                            </div>
                          </div>
                          <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                            <div className="col">
                              Emergency and safety skills:
                            </div>
                            <div className="col">
                              <FormSelect
                                name="SafetySkills"
                                error={errors.SafetySkills}
                                value={data.SafetySkills["@value"]}
                                options={functioningLevelOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              {data.SafetySkills["@value"] === "Regressed" ||
                              data.SafetySkills["@value"] === "Advanced" ? (
                                <FormInput
                                  name="SafetySkillsDetails"
                                  error={errors.SafetySkillsDetails}
                                  value={data.SafetySkillsDetails["@value"]}
                                  onChange={handleChange}
                                  identical={compare("SafetySkillsDetails")}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              ) : null}
                            </div>
                          </div>
                          <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                            <div className="col">
                              Knowledge of community resources:
                            </div>
                            <div className="col">
                              <FormSelect
                                name="ResourceKnowledge"
                                error={errors.ResourceKnowledge}
                                value={data.ResourceKnowledge["@value"]}
                                options={functioningLevelOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              {data.ResourceKnowledge["@value"] ===
                                "Regressed" ||
                              data.ResourceKnowledge["@value"] ===
                                "Advanced" ? (
                                <FormInput
                                  name="ResourceKnowledgeDetails"
                                  error={errors.ResourceKnowledgeDetails}
                                  value={
                                    data.ResourceKnowledgeDetails["@value"]
                                  }
                                  onChange={handleChange}
                                  identical={compare(
                                    "ResourceKnowledgeDetails"
                                  )}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              ) : null}
                            </div>
                          </div>
                          <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                            <div className="col">Time management:</div>
                            <div className="col">
                              <FormSelect
                                name="TimeManagement"
                                error={errors.TimeManagement}
                                value={data.TimeManagement["@value"]}
                                options={functioningLevelOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              {data.TimeManagement["@value"] === "Regressed" ||
                              data.TimeManagement["@value"] === "Advanced" ? (
                                <FormInput
                                  name="TimeManagementDetails"
                                  error={errors.TimeManagementDetails}
                                  value={data.TimeManagementDetails["@value"]}
                                  onChange={handleChange}
                                  identical={compare("TimeManagementDetails")}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              ) : null}
                            </div>
                          </div>
                          <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                            <div className="col">Interactions with Others:</div>
                            <div className="col">
                              <FormSelect
                                name="Interaction"
                                error={errors.Interaction}
                                value={data.Interaction["@value"]}
                                options={functioningLevelOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              {data.Interaction["@value"] === "Regressed" ||
                              data.Interaction["@value"] === "Advanced" ? (
                                <FormInput
                                  name="InteractionDetails"
                                  error={errors.InteractionDetails}
                                  value={data.InteractionDetails["@value"]}
                                  onChange={handleChange}
                                  identical={compare("InteractionDetails")}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </>
                    ) : null}
                  </ExpandDiv>
                  <ExpandDiv
                    id="Friendships"
                    expandStatus={expandStatus}
                    className="mt-2"
                    title="Friendship/ Social Peer Support/ Relationships"
                  >
                    <button
                      className="btn btn-link"
                      onClick={() => {
                        const obj = { ...data };

                        obj.CoopSkills["@value"] = "Age Appropriate";
                        obj.CommunicationSkills["@value"] = "Age Appropriate";
                        obj.EmotionalUnderstanding["@value"] =
                          "Age Appropriate";
                        obj.ProblemSolving["@value"] = "Age Appropriate";
                        obj.ConflictResolution["@value"] = "Age Appropriate";
                        obj.DecisionMakingSkills["@value"] = "Age Appropriate";

                        setData(obj);
                      }}
                    >
                      Apply "Age Appropriate" to all
                    </button>
                    <div className="container">
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom">
                        <div className="col">
                          <b>Skill</b>
                        </div>
                        <div className="col">
                          <b>Functioning Level</b>
                        </div>
                        <div className="col">
                          <b>Explain</b>
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-3">
                        <div className="col">Cooperative play skills:</div>
                        <div className="col">
                          <FormSelect
                            name="CoopSkills"
                            error={errors.CoopSkills}
                            value={data.CoopSkills["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.CoopSkills["@value"] === "Regressed" ||
                          data.CoopSkills["@value"] === "Advanced" ? (
                            <FormInput
                              name="CoopSkillsDetails"
                              error={errors.CoopSkillsDetails}
                              value={data.CoopSkillsDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("CoopSkillsDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-3">
                        <div className="col">
                          Communication and interaction skills:
                        </div>
                        <div className="col">
                          <FormSelect
                            name="CommunicationSkills"
                            error={errors.CommunicationSkills}
                            value={data.CommunicationSkills["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.CommunicationSkills["@value"] === "Regressed" ||
                          data.CommunicationSkills["@value"] === "Advanced" ? (
                            <FormInput
                              name="CommunicationSkillsDetails"
                              error={errors.CommunicationSkillsDetails}
                              value={data.CommunicationSkillsDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("CommunicationSkillsDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-3">
                        <div className="col">
                          Emotional understanding and self-regulation skills:
                        </div>
                        <div className="col">
                          <FormSelect
                            name="EmotionalUnderstanding"
                            error={errors.EmotionalUnderstanding}
                            value={data.EmotionalUnderstanding["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.EmotionalUnderstanding["@value"] ===
                            "Regressed" ||
                          data.EmotionalUnderstanding["@value"] ===
                            "Advanced" ? (
                            <FormInput
                              name="EmotionalUnderstandingDetails"
                              error={errors.EmotionalUnderstandingDetails}
                              value={
                                data.EmotionalUnderstandingDetails["@value"]
                              }
                              onChange={handleChange}
                              identical={compare(
                                "EmotionalUnderstandingDetails"
                              )}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-3">
                        <div className="col">
                          Social problem-solving skills:
                        </div>
                        <div className="col">
                          <FormSelect
                            name="ProblemSolving"
                            error={errors.ProblemSolving}
                            value={data.ProblemSolving["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.ProblemSolving["@value"] === "Regressed" ||
                          data.ProblemSolving["@value"] === "Advanced" ? (
                            <FormInput
                              name="ProblemSolvingDetails"
                              error={errors.ProblemSolvingDetails}
                              value={data.ProblemSolvingDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("ProblemSolvingDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-3">
                        <div className="col">Conflict resolution skills:</div>
                        <div className="col">
                          <FormSelect
                            name="ConflictResolution"
                            error={errors.ConflictResolution}
                            value={data.ConflictResolution["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.ConflictResolution["@value"] === "Regressed" ||
                          data.ConflictResolution["@value"] === "Advanced" ? (
                            <FormInput
                              name="ConflictResolutionDetails"
                              error={errors.ConflictResolutionDetails}
                              value={data.ConflictResolutionDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("ConflictResolutionDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-3">
                        <div className="col">Decision making skills:</div>
                        <div className="col">
                          <FormSelect
                            name="DecisionMakingSkills"
                            error={errors.DecisionMakingSkills}
                            value={data.DecisionMakingSkills["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.DecisionMakingSkills["@value"] ===
                            "Regressed" ||
                          data.DecisionMakingSkills["@value"] === "Advanced" ? (
                            <FormInput
                              name="DecisionMakingSkillsDetails"
                              error={errors.DecisionMakingSkillsDetails}
                              value={data.DecisionMakingSkillsDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("DecisionMakingSkillsDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <FormRadioButton
                      name="Bullied"
                      label="History of being bullied:"
                      error={errors.Bullied}
                      value={data.Bullied["@value"]}
                      options={yesNoNoneNAOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormRadioButton
                      name="Bully"
                      label="History of bullying others:"
                      error={errors.Bully}
                      value={data.Bully["@value"]}
                      options={yesNoNoneNAOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormRadioButton
                      name="AcceptedByPeers"
                      label="Accepted by peer group:"
                      error={errors.AcceptedByPeers}
                      value={data.AcceptedByPeers["@value"]}
                      options={yesNoNoneNAOptions}
                      onChange={handleChange}
                      comments={parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                    <FormRadioButton
                      name="NegativeAssociations"
                      label="Negative peer group associations:"
                      error={errors.NegativeAssociations}
                      value={data.NegativeAssociations["@value"]}
                      options={yesNoNoneNAOptions}
                      onChange={handleChange}
                      comments={parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                    {data.Bullied["@value"] === "Yes" ||
                    data.Bully["@value"] === "Yes" ||
                    data.AcceptedByPeers["@value"] === "No" ||
                    data.NegativeAssociations["@value"] === "Yes" ? (
                      <FormInput
                        name="RelationshipComments"
                        label="Comments:"
                        error={errors.RelationshipComments}
                        value={data.RelationshipComments["@value"]}
                        onChange={handleChange}
                        identical={compare("RelationshipComments")}
                        comments={parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                    ) : null}
                  </ExpandDiv>
                </>
              ) : null}
              <ExpandDiv
                id="LegalHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Legal History"
              >
                <FormRadioButton
                  name="LegalInvolvement"
                  label="Current involvement with the Legal System:"
                  error={errors.LegalInvolvement}
                  value={data.LegalInvolvement["@value"]}
                  options={yesNoNoneOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.LegalInvolvement["@value"] !== "No" &&
                  data.LegalInvolvement["@value"] !== "None reported" && (
                    <>
                      <FormRadioButton
                        name="CPSInvolvement"
                        label="Current Children's Protective Services (CPS) Involvement with Family:"
                        error={errors.CPSInvolvement}
                        value={data.CPSInvolvement["@value"]}
                        options={yesNoNoneOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.PSDType["@value"] !==
                        "Psychiatric Diagnostic Evaluation" && (
                        <>
                          {data.CPSInvolvement["@value"] === "Yes" && (
                            <>
                              <FormSelect
                                name="TypeOfCPSInvolvement"
                                label="Type of Involvement:"
                                error={errors.TypeOfCPSInvolvement}
                                value={data.TypeOfCPSInvolvement["@value"]}
                                options={typeOfCPSOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                              CPS History
                              <FieldTable
                                name="CPSHistory"
                                commentsHandler={handleComments}
                              >
                                <div className="container border-top border-start border-end mb-2">
                                  <div className="row border-bottom">
                                    <div className="col border-end py-2">
                                      Dates
                                    </div>
                                    <div className="col py-2">Comments</div>
                                  </div>
                                  {data.CPSHistory["@value"]
                                    ? JSON.parse(data.CPSHistory["@value"]).map(
                                        (item, index) => (
                                          <div
                                            key={item._id}
                                            className="row border-bottom"
                                          >
                                            <div className="col border-end py-2 d-flex">
                                              {item.IsEditable === undefined ||
                                              item.IsEditable ? (
                                                <>
                                                  <i
                                                    className="fas fa-save me-2 d-print-none"
                                                    onClick={() => {
                                                      handleSaveRow(
                                                        index,
                                                        "CPSHistory"
                                                      );
                                                    }}
                                                  ></i>
                                                  <i
                                                    className="far fa-trash-alt me-2 d-print-none"
                                                    onClick={() => {
                                                      handleDeleteRow(
                                                        index,
                                                        "CPSHistory"
                                                      );
                                                    }}
                                                  ></i>
                                                  <FormInput
                                                    name="Dates"
                                                    value={item.Dates}
                                                    onChange={(e) => {
                                                      handleRowChange(
                                                        e,
                                                        index,
                                                        "CPSHistory"
                                                      );
                                                    }}
                                                    disabled={formDisabled}
                                                  />
                                                </>
                                              ) : (
                                                <>
                                                  <i
                                                    className="fas fa-pen me-2 d-print-none"
                                                    onClick={() => {
                                                      handleEditRow(
                                                        index,
                                                        "CPSHistory"
                                                      );
                                                    }}
                                                  ></i>
                                                  {item.Dates}
                                                </>
                                              )}
                                            </div>
                                            <div className="col py-2">
                                              {item.IsEditable === undefined ||
                                              item.IsEditable ? (
                                                <FormInput
                                                  name="Comments"
                                                  value={item.Comments}
                                                  onChange={(e) => {
                                                    handleRowChange(
                                                      e,
                                                      index,
                                                      "CPSHistory"
                                                    );
                                                  }}
                                                  disabled={formDisabled}
                                                />
                                              ) : (
                                                item.Comments
                                              )}
                                            </div>
                                          </div>
                                        )
                                      )
                                    : null}
                                </div>
                                {!formDisabled ? (
                                  <>
                                    <button
                                      className="btn btn-primary mb-2"
                                      onClick={() => {
                                        handleAddRow("CPSHistory", {
                                          IsEditable: true,
                                        });
                                      }}
                                    >
                                      Add History
                                    </button>
                                  </>
                                ) : null}
                                {errors.CPSHistory !== undefined && (
                                  <div className="row">
                                    <div className="col">
                                      <div className="alert alert-danger">
                                        {errors.CPSHistory}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </FieldTable>
                              <div className="row">
                                <div className="col">
                                  <FormRadioButton
                                    name="CPSCaseworker"
                                    label="CPS Caseworker:"
                                    error={errors.CPSCaseworker}
                                    value={data.CPSCaseworker["@value"]}
                                    options={yesNoOptions}
                                    onChange={handleChange}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                </div>
                                {data.CPSCaseworker["@value"] === "Yes" && (
                                  <div className="col">
                                    <FormInput
                                      name="CPSCaseworkerName"
                                      label="Name:"
                                      error={errors.CPSCaseworkerName}
                                      value={data.CPSCaseworkerName["@value"]}
                                      onChange={handleChange}
                                      identical={compare("CPSCaseworkerName")}
                                      commentsHandler={handleComments}
                                      disabled={formDisabled}
                                    />
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                          <div className="row">
                            <div className="col">
                              <FormRadioButton
                                name="ProbationOfficer"
                                label="Probation/Parole Officer:"
                                error={errors.ProbationOfficer}
                                value={data.ProbationOfficer["@value"]}
                                options={yesNoOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            {data.ProbationOfficer["@value"] === "Yes" && (
                              <div className="col">
                                <FormInput
                                  name="ProbationOfficerName"
                                  label="Name:"
                                  error={errors.ProbationOfficerName}
                                  value={data.ProbationOfficerName["@value"]}
                                  onChange={handleChange}
                                  identical={compare("ProbationOfficerName")}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            )}
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormRadioButton
                                name="GALCASA"
                                label="Guardian ad Litem (GAL) or Court Appointed Special Advocate (CASA):"
                                error={errors.GALCASA}
                                value={data.GALCASA["@value"]}
                                options={yesNoUnknownOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            {data.GALCASA["@value"] === "Yes" && (
                              <div className="col">
                                <FormInput
                                  name="GALCASAName"
                                  label="Name:"
                                  error={errors.GALCASAName}
                                  value={data.GALCASAName["@value"]}
                                  onChange={handleChange}
                                  identical={compare("GALCASAName")}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            )}
                          </div>
                          <FormSelect
                            name="LegalStatus"
                            label="Current Legal Status:"
                            error={errors.LegalStatus}
                            value={data.LegalStatus["@value"]}
                            options={legalStatusOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.LegalStatus["@value"] !== "None reported" && (
                            <FieldTable
                              name="LegalHistory"
                              commentsHandler={handleComments}
                            >
                              <div className="container border-top border-start border-end mb-2">
                                <div className="row border-bottom">
                                  <div className="col border-end py-2">
                                    Legal Charges
                                  </div>
                                  <div className="col border-end py-2">
                                    Disposition
                                  </div>
                                  <div className="col py-2">Comments</div>
                                </div>
                                {data.LegalHistory["@value"]
                                  ? JSON.parse(data.LegalHistory["@value"]).map(
                                      (item, index) => (
                                        <div
                                          key={item._id}
                                          className="row border-bottom"
                                        >
                                          <div className="col border-end py-2 d-flex">
                                            {item.IsEditable === undefined ||
                                            item.IsEditable ? (
                                              <>
                                                <i
                                                  className="fas fa-save me-2 d-print-none"
                                                  onClick={() => {
                                                    handleSaveRow(
                                                      index,
                                                      "LegalHistory"
                                                    );
                                                  }}
                                                ></i>
                                                <i
                                                  className="far fa-trash-alt me-2 d-print-none"
                                                  onClick={() => {
                                                    handleDeleteRow(
                                                      index,
                                                      "LegalHistory"
                                                    );
                                                  }}
                                                ></i>
                                                <FormInput
                                                  name="Charges"
                                                  value={item.Charges}
                                                  onChange={(e) => {
                                                    handleRowChange(
                                                      e,
                                                      index,
                                                      "LegalHistory"
                                                    );
                                                  }}
                                                  disabled={formDisabled}
                                                />
                                              </>
                                            ) : (
                                              <>
                                                <i
                                                  className="fas fa-pen me-2 d-print-none"
                                                  onClick={() => {
                                                    handleEditRow(
                                                      index,
                                                      "LegalHistory"
                                                    );
                                                  }}
                                                ></i>
                                                {item.Charges}
                                              </>
                                            )}
                                          </div>
                                          <div className="col border-end py-2">
                                            {item.IsEditable === undefined ||
                                            item.IsEditable ? (
                                              <FormSelect
                                                name="Disposition"
                                                value={item.Disposition}
                                                options={dispositionOptions}
                                                onChange={(e) => {
                                                  handleRowChange(
                                                    e,
                                                    index,
                                                    "LegalHistory"
                                                  );
                                                }}
                                                disabled={formDisabled}
                                              />
                                            ) : (
                                              item.Disposition
                                            )}
                                          </div>
                                          <div className="col py-2">
                                            {item.IsEditable === undefined ||
                                            item.IsEditable ? (
                                              <FormInput
                                                name="Comments"
                                                value={item.Comments}
                                                onChange={(e) => {
                                                  handleRowChange(
                                                    e,
                                                    index,
                                                    "LegalHistory"
                                                  );
                                                }}
                                                disabled={formDisabled}
                                              />
                                            ) : (
                                              item.Comments
                                            )}
                                          </div>
                                        </div>
                                      )
                                    )
                                  : null}
                              </div>
                              {!formDisabled ? (
                                <>
                                  <button
                                    className="btn btn-primary mb-2"
                                    onClick={() => {
                                      handleAddRow("LegalHistory", {
                                        IsEditable: true,
                                      });
                                    }}
                                  >
                                    Add Charge
                                  </button>
                                </>
                              ) : null}
                              {errors.LegalHistory !== undefined && (
                                <div className="row">
                                  <div className="col">
                                    <div className="alert alert-danger">
                                      {errors.LegalHistory}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </FieldTable>
                          )}
                        </>
                      )}
                      <FormInput
                        name="OtherLegalDetails"
                        label="Other Court/Custody Related Issues:"
                        error={errors.OtherLegalDetails}
                        value={data.OtherLegalDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("OtherLegalDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </>
                  )}
              </ExpandDiv>
              <ExpandDiv
                id="MedicalHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Medications"
              >
                {data.PSDType["@value"] !==
                  "Psychiatric Diagnostic Evaluation" && (
                  <>
                    <b>Medical</b>
                    <div className="container">
                      <div className="row">
                        <div className="col">
                          <FormInput
                            name="PCP"
                            label="Primary Care Physician:"
                            error={errors.PCP}
                            value={data.PCP["@value"]}
                            onChange={handleChange}
                            identical={compare("PCP")}
                            comments={parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <div className="row">
                            <div className="col">
                              <PhoneInput
                                name="PCPPhone"
                                label="Phone:"
                                error={errors.PCPPhone}
                                value={data.PCPPhone["@value"]}
                                onChange={handleChange}
                                identical={compare("PCPPhone")}
                                comments={parsedComments}
                                addComment={() => setShowComment(true)}
                                editComment={toggleCommentModal}
                                deleteComment={handleDeleteComment}
                                acknowledgeComment={handleAcknowledgeComment}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="PCPUnknown"
                                label="None reported"
                                checked={
                                  data.PCPUnknown &&
                                  data.PCPUnknown["@value"] === "true"
                                }
                                onChange={handleChange}
                                comments={parsedComments}
                                addComment={() => setShowComment(true)}
                                editComment={toggleCommentModal}
                                deleteComment={handleDeleteComment}
                                acknowledgeComment={handleAcknowledgeComment}
                                disabled={formDisabled}
                                divStyle={{ marginTop: "30px" }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormInput
                            name="LastMedicalExamDate"
                            label="Date of last medical exam:"
                            error={errors.LastMedicalExamDate}
                            value={data.LastMedicalExamDate["@value"]}
                            onChange={handleChange}
                            identical={compare("LastMedicalExamDate")}
                            comments={parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormInput
                            name="LastDentalExamDate"
                            label="Date of last dental exam:"
                            error={errors.LastDentalExamDate}
                            value={data.LastDentalExamDate["@value"]}
                            onChange={handleChange}
                            identical={compare("LastDentalExamDate")}
                            comments={parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="Allergies"
                            label="Allergies"
                            divClassName="ms-3"
                            checked={data.Allergies["@value"] === "true"}
                            onChange={handleChange}
                            comments={parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.Allergies["@value"] === "true" ? (
                            <FormInput
                              name="AllergiesDetails"
                              label="List:"
                              error={errors.AllergiesDetails}
                              value={data.AllergiesDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("AllergiesDetails")}
                              comments={parsedComments}
                              addComment={() => setShowComment(true)}
                              editComment={toggleCommentModal}
                              deleteComment={handleDeleteComment}
                              acknowledgeComment={handleAcknowledgeComment}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="MedicalConditions"
                            label="Medical conditions/concerns"
                            divClassName="ms-3"
                            checked={
                              data.MedicalConditions["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.MedicalConditions["@value"] === "true" ? (
                            <FormInput
                              name="MedicalConditionsDetails"
                              label="List:"
                              error={errors.MedicalConditionsDetails}
                              value={data.MedicalConditionsDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("MedicalConditionsDetails")}
                              comments={parsedComments}
                              addComment={() => setShowComment(true)}
                              editComment={toggleCommentModal}
                              deleteComment={handleDeleteComment}
                              acknowledgeComment={handleAcknowledgeComment}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="NutritionalConcerns"
                            label="Nutritional concerns"
                            divClassName="ms-3"
                            checked={
                              data.NutritionalConcerns["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.NutritionalConcerns["@value"] === "true" ? (
                            <FormInput
                              name="NutritionalConcernsDetails"
                              label="List:"
                              error={errors.NutritionalConcernsDetails}
                              value={data.NutritionalConcernsDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("NutritionalConcernsDetails")}
                              comments={parsedComments}
                              addComment={() => setShowComment(true)}
                              editComment={toggleCommentModal}
                              deleteComment={handleDeleteComment}
                              acknowledgeComment={handleAcknowledgeComment}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="DentalConcerns"
                            label="Dental concerns"
                            divClassName="ms-3"
                            checked={data.DentalConcerns["@value"] === "true"}
                            onChange={handleChange}
                            comments={parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.DentalConcerns["@value"] === "true" ? (
                            <FormInput
                              name="DentalConcernsDetails"
                              label="List:"
                              error={errors.DentalConcernsDetails}
                              value={data.DentalConcernsDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("DentalConcernsDetails")}
                              comments={parsedComments}
                              addComment={() => setShowComment(true)}
                              editComment={toggleCommentModal}
                              deleteComment={handleDeleteComment}
                              acknowledgeComment={handleAcknowledgeComment}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <FormRadioButton
                      name="Undiagnosed"
                      className="mt-2"
                      label="Client has symptoms that suggest an undiagnosed medical illness that may be causing or contributing to mental health symptoms:"
                      error={errors.Undiagnosed}
                      value={data.Undiagnosed["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      comments={parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                    {data.Undiagnosed["@value"] === "Yes" ? (
                      <FormInput
                        name="UndiagnosedDetails"
                        label="Comments:"
                        error={errors.UndiagnosedDetails}
                        value={data.UndiagnosedDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("UndiagnosedDetails")}
                        comments={parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                    ) : null}
                    <br />
                  </>
                )}
                <b>Current Medications</b>
                <FormCheckbox
                  name="NoMedications"
                  label="Not currently on medications"
                  divClassName="ms-3"
                  checked={data.NoMedications["@value"] === "true"}
                  comments={parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.NoMedications["@value"] !== "true" && (
                  <>
                    <FieldTable
                      name="CurrentMedications"
                      commentsHandler={handleComments}
                    >
                      <div className="container border-top border-start border-end mb-2">
                        <div className="row border-bottom">
                          <div className="col border-end py-2">Medication</div>
                          <div className="col border-end py-2">Dosage/Use</div>
                          <div className="col border-end py-2">
                            Prescribed By/Date
                          </div>
                          <div className="col py-2">Purpose</div>
                        </div>
                        {data.CurrentMedications["@value"]
                          ? JSON.parse(data.CurrentMedications["@value"]).map(
                              (item, index) => (
                                <div
                                  key={item._id}
                                  className="row border-bottom"
                                >
                                  <div className="col border-end py-2 d-flex">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <i
                                          className="fas fa-save me-2 d-print-none"
                                          onClick={() => {
                                            handleSaveRow(
                                              index,
                                              "CurrentMedications"
                                            );
                                          }}
                                        ></i>
                                        <i
                                          className="far fa-trash-alt me-2 d-print-none"
                                          onClick={() => {
                                            handleDeleteRow(
                                              index,
                                              "CurrentMedications"
                                            );
                                          }}
                                        ></i>
                                        <FormInput
                                          name="Medication"
                                          value={item.Medication}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CurrentMedications"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <i
                                          className="fas fa-pen me-2 d-print-none"
                                          onClick={() => {
                                            handleEditRow(
                                              index,
                                              "CurrentMedications"
                                            );
                                          }}
                                        ></i>
                                        {item.Medication}
                                      </>
                                    )}
                                  </div>
                                  <div className="col border-end py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormInput
                                        name="Dosage"
                                        value={item.Dosage}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "CurrentMedications"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Dosage
                                    )}
                                  </div>
                                  <div className="col border-end py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormInput
                                        name="PrescribedBy"
                                        value={item.PrescribedBy}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "CurrentMedications"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.PrescribedBy
                                    )}
                                  </div>
                                  <div className="col py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormInput
                                        name="Purpose"
                                        value={item.Purpose}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "CurrentMedications"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Purpose
                                    )}
                                  </div>
                                </div>
                              )
                            )
                          : null}
                      </div>
                      {!formDisabled ? (
                        <>
                          <button
                            className="btn btn-primary mb-2"
                            onClick={() => {
                              handleAddRow("CurrentMedications", {
                                IsEditable: true,
                              });
                            }}
                          >
                            Add Medication
                          </button>
                        </>
                      ) : null}
                      {errors.CurrentMedications !== undefined && (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.CurrentMedications}
                            </div>
                          </div>
                        </div>
                      )}
                    </FieldTable>
                    <FormSelect
                      name="MedicationCompliance"
                      label="Compliance with medication:"
                      error={errors.MedicationCompliance}
                      value={data.MedicationCompliance["@value"]}
                      options={complianceOptions}
                      onChange={handleChange}
                      comments={parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                    <FormRadioButton
                      name="SideEffects"
                      label="Side effects from medications:"
                      error={errors.SideEffects}
                      value={data.SideEffects["@value"]}
                      options={yesNoNoneNAOptions}
                      onChange={handleChange}
                      comments={parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                    {data.SideEffects["@value"] === "Yes" ? (
                      <FormInput
                        name="SideEffectsDetails"
                        label="Describe:"
                        error={errors.SideEffectsDetails}
                        value={data.SideEffectsDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("SideEffectsDetails")}
                        comments={parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                    ) : null}
                  </>
                )}
                <FormRadioButton
                  name="PsychotropicMeds"
                  label="Past psychotropic medications:"
                  error={errors.PsychotropicMeds}
                  value={data.PsychotropicMeds["@value"]}
                  options={yesNoNoneNAOptions}
                  onChange={handleChange}
                  comments={parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                  disabled={formDisabled}
                />
                {data.PsychotropicMeds["@value"] === "Yes" ? (
                  <FormInput
                    name="PsychotropicMedsDetails"
                    label="List:"
                    error={errors.PsychotropicMedsDetails}
                    value={data.PsychotropicMedsDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("PsychotropicMedsDetails")}
                    comments={parsedComments}
                    addComment={() => setShowComment(true)}
                    editComment={toggleCommentModal}
                    deleteComment={handleDeleteComment}
                    acknowledgeComment={handleAcknowledgeComment}
                    disabled={formDisabled}
                  />
                ) : null}
              </ExpandDiv>
              {data.PSDType["@value"] !==
              "Psychiatric Diagnostic Evaluation" ? (
                <ExpandDiv
                  id="SexualHistory"
                  expandStatus={expandStatus}
                  className="mt-2"
                  title="Sexual History to Include Pertinent Sexual Issues/Concerns"
                >
                  <FormCheckbox
                    name="SexualHistoryNA"
                    label="N/A - Youth is under 5 with no history of sexual abuse or sexually inappropriate behavior"
                    checked={data.SexualHistoryNA["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                  {data.SexualHistoryNA["@value"] !== "true" ? (
                    <>
                      <div className="container">
                        <div className="row">
                          <div className="col">
                            <FormSelect
                              name="SexualOrientation"
                              label="Sexual Orientation:"
                              error={errors.SexualOrientation}
                              value={data.SexualOrientation["@value"]}
                              options={sexualOrientationOptions}
                              onChange={handleChange}
                              identical={compare("SexualOrientation")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            {data.SexualOrientation["@value"] ===
                              "Orientation not listed" ||
                            data.SexualOrientation["@value"] ===
                              "Unable to assess" ? (
                              <FormInput
                                name="SexualOrientationDetails"
                                label={`Please ${
                                  data.SexualOrientation["@value"] ===
                                  "Orientation not listed"
                                    ? "specify"
                                    : "describe"
                                }:`}
                                error={errors.SexualOrientationDetails}
                                value={data.SexualOrientationDetails["@value"]}
                                onChange={handleChange}
                                identical={compare("SexualOrientationDetails")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            ) : null}
                          </div>
                        </div>
                        <FormRadioButton
                          name="SexualRelationshipsEducation"
                          label="Education provided on healthy sexual behavior/relationships:"
                          error={errors.SexualRelationshipsEducation}
                          value={data.SexualRelationshipsEducation["@value"]}
                          options={yesNoNoneNAOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormRadioButton
                          name="SexuallyActive"
                          label="Sexually Active:"
                          error={errors.SexuallyActive}
                          value={data.SexuallyActive["@value"]}
                          options={yesNoNAOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {data.SexuallyActive["@value"] === "Yes" && (
                          <div className="ms-3">
                            <FormRadioButton
                              name="CaregiverAware"
                              label="Caregiver aware of sexual history:"
                              error={errors.CaregiverAware}
                              value={data.CaregiverAware["@value"]}
                              options={yesNoOptions}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            <FormRadioButton
                              name="UseProtection"
                              label="Use of protection:"
                              error={errors.UseProtection}
                              value={data.UseProtection["@value"]}
                              options={useOfProtectionOptions}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            <FormRadioButton
                              name="AwareOfRisks"
                              label="Awareness of sexual risks:"
                              error={errors.AwareOfRisks}
                              value={data.AwareOfRisks["@value"]}
                              options={yesNoNAOptions}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            <FormRadioButton
                              name="Pregnancy"
                              label="Pregnancy:"
                              error={errors.Pregnancy}
                              value={data.Pregnancy["@value"]}
                              options={pregnancyOptions}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            <FormRadioButton
                              name="Children"
                              label="Children:"
                              error={errors.Children}
                              value={data.Children["@value"]}
                              options={yesNoNoneNAOptions}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            <FormInput
                              name="SexuallyActiveComments"
                              label="Comments:"
                              error={errors.SexuallyActiveComments}
                              value={data.SexuallyActiveComments["@value"]}
                              onChange={handleChange}
                              identical={compare("SexuallyActiveComments")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        )}
                        <FormRadioButton
                          name="SexualAbuseVictim"
                          label="Victim of sexual abuse:"
                          error={errors.SexualAbuseVictim}
                          value={data.SexualAbuseVictim["@value"]}
                          options={yesNoNoneOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormRadioButton
                          name="SexualBehavior"
                          label="Age inappropriate sexual behavior:"
                          error={errors.SexualBehavior}
                          value={data.SexualBehavior["@value"]}
                          options={yesNoNoneOptions}
                          onChange={handleChange}
                          identical={compare("SexualBehavior")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormRadioButton
                          name="SexuallyActingOut"
                          label="Sexually acting out/reactive behavior:"
                          error={errors.SexuallyActingOut}
                          value={data.SexuallyActingOut["@value"]}
                          options={yesNoNoneOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormRadioButton
                          name="SexuallyAbusive"
                          label="Sexually abusive/offending behavior:"
                          error={errors.SexuallyAbusive}
                          value={data.SexuallyAbusive["@value"]}
                          options={yesNoNoneOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {data.SexualAbuseVictim["@value"] === "Yes" ||
                        data.SexualBehavior["@value"] === "Yes" ||
                        data.SexuallyActingOut["@value"] === "Yes" ||
                        data.SexuallyAbusive["@value"] === "Yes" ? (
                          <FormTextArea
                            name="SexualHistoryComments"
                            label="Comments:"
                            error={errors.SexualHistoryComments}
                            value={data.SexualHistoryComments["@value"]}
                            onChange={handleChange}
                            identical={compare("SexualHistoryComments")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </>
                  ) : null}
                </ExpandDiv>
              ) : null}
              <ExpandDiv
                id="MentalHealthTreatmentHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Mental Health Treatment History"
              >
                <FormCheckbox
                  name="MHTreatmentNA"
                  label="N/A - no history of mental health treatment"
                  divClassName="ms-3"
                  checked={data.MHTreatmentNA["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.MHTreatmentNA["@value"] !== "true" && (
                  <>
                    <FormRadioButton
                      name="PastMentalHealthTreatment"
                      label="Past mental health counseling/treatment:"
                      error={errors.PastMentalHealthTreatment}
                      value={data.PastMentalHealthTreatment["@value"]}
                      options={yesNoNoneOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />

                    <FormRadioButton
                      name="PastGroupHomePlacement"
                      label="Past placement in a group home/residential treatment facility:"
                      error={errors.PastGroupHomePlacement}
                      value={data.PastGroupHomePlacement["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormRadioButton
                      name="PastHospitalPlacement"
                      label="Past placement in a psychiatric hospital/unit:"
                      error={errors.PastHospitalPlacement}
                      value={data.PastHospitalPlacement["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormRadioButton
                      name="InvoluntaryTreatment"
                      label="History of involuntary/court-ordered treatment:"
                      error={errors.InvoluntaryTreatment}
                      value={data.InvoluntaryTreatment["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormInput
                      name="PastMentalHealthDiagnosis"
                      label="Previous mental health diagnosis:"
                      error={errors.PastMentalHealthDiagnosis}
                      value={data.PastMentalHealthDiagnosis["@value"]}
                      onChange={handleChange}
                      identical={compare("PastMentalHealthDiagnosis")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.PastMentalHealthTreatment["@value"] === "Yes" ||
                    data.PastGroupHomePlacement["@value"] === "Yes" ||
                    data.PastHospitalPlacement["@value"] === "Yes" ||
                    data.InvoluntaryTreatment["@value"] === "Yes" ? (
                      <FormTextArea
                        name="MHTreatmentComments"
                        label="Comments:"
                        error={errors.MHTreatmentComments}
                        value={data.MHTreatmentComments["@value"]}
                        onChange={handleChange}
                        identical={compare("MHTreatmentComments")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    <FieldTable
                      name="MHTreatmentHistory"
                      commentsHandler={handleComments}
                    >
                      <div className="container border-top border-start border-end mb-2">
                        <div className="row border-bottom">
                          <div className="col border-end py-2">
                            Agency/Facility
                          </div>
                          <div className="col border-end py-2">Dates</div>
                          <div className="col border-end py-2">Reason</div>
                          <div className="col py-2">Outcome</div>
                        </div>
                        {data.MHTreatmentHistory["@value"]
                          ? JSON.parse(data.MHTreatmentHistory["@value"]).map(
                              (item, index) => (
                                <div
                                  key={item._id}
                                  className="row border-bottom"
                                >
                                  <div className="col border-end py-2 d-flex">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <i
                                          className="fas fa-save me-2 d-print-none"
                                          onClick={() => {
                                            handleSaveRow(
                                              index,
                                              "MHTreatmentHistory"
                                            );
                                          }}
                                        ></i>
                                        <i
                                          className="far fa-trash-alt me-2 d-print-none"
                                          onClick={() => {
                                            handleDeleteRow(
                                              index,
                                              "MHTreatmentHistory"
                                            );
                                          }}
                                        ></i>
                                        <FormInput
                                          name="Agency"
                                          value={item.Agency}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "MHTreatmentHistory"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <i
                                          className="fas fa-pen me-2 d-print-none"
                                          onClick={() => {
                                            handleEditRow(
                                              index,
                                              "MHTreatmentHistory"
                                            );
                                          }}
                                        ></i>
                                        {item.Agency}
                                      </>
                                    )}
                                  </div>
                                  <div className="col border-end py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormInput
                                        name="Dates"
                                        value={item.Dates}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "MHTreatmentHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Dates
                                    )}
                                  </div>
                                  <div className="col border-end py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormInput
                                        name="Reason"
                                        value={item.Reason}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "MHTreatmentHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Reason
                                    )}
                                  </div>
                                  <div className="col py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormSelect
                                        name="Outcome"
                                        value={item.Outcome}
                                        options={outcomeOptions}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "MHTreatmentHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Outcome
                                    )}
                                  </div>
                                </div>
                              )
                            )
                          : null}
                      </div>
                      {!formDisabled ? (
                        <>
                          <button
                            className="btn btn-primary mb-2"
                            onClick={() => {
                              handleAddRow("MHTreatmentHistory", {
                                IsEditable: true,
                              });
                            }}
                          >
                            Add Agency/Facility
                          </button>
                        </>
                      ) : null}
                      {errors.MHTreatmentHistory !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.MHTreatmentHistory}
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </FieldTable>
                  </>
                )}
                <FormTextArea
                  name="MHClientPerspectiveOfTreatment"
                  label="Client's perspective of treatment/counseling:"
                  error={errors.MHClientPerspectiveOfTreatment}
                  value={data.MHClientPerspectiveOfTreatment["@value"]}
                  onChange={handleChange}
                  identical={compare("MHClientPerspectiveOfTreatment")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormTextArea
                  name="MHCaregiverPerspectiveOfTreatment"
                  label="Caregiver's perspective of treatment/counseling:"
                  error={errors.MHCaregiverPerspectiveOfTreatment}
                  value={data.MHCaregiverPerspectiveOfTreatment["@value"]}
                  onChange={handleChange}
                  identical={compare("MHCaregiverPerspectiveOfTreatment")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </ExpandDiv>
              <ExpandDiv
                id="SubstanceUseHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Alcohol/Drug Use History"
              >
                <FormCheckbox
                  name="SUAge"
                  label="Client is 12 or older"
                  checked={data.SUAge["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="ms-3"
                />
                <FormCheckbox
                  name="SubstanceUseNA"
                  label="No current or past use/experimentation/abuse of alcohol and/or drugs"
                  checked={data.SubstanceUseNA["@value"] === "true"}
                  onChange={handleSubstanceUseNAChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="ms-3"
                />
                {data.SubstanceUseNA["@value"] !== "true" ? (
                  <>
                    <b>Client:</b>
                    <div className="ms-3">
                      <FormCheckbox
                        name="SUClientDenies"
                        label="Denies current or past use/experimentation/abuse of alcohol and/or drugs"
                        checked={data.SUClientDenies["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                      <FormCheckbox
                        name="SUClientReportsCurrent"
                        label={
                          <>
                            Reports{" "}
                            <b>
                              <u>current</u>
                            </b>{" "}
                            use/experimentation/abuse of alcohol and/or drugs
                          </>
                        }
                        checked={
                          data.SUClientReportsCurrent["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                      <FormCheckbox
                        name="SUClientReportsPast"
                        label={
                          <>
                            Reports{" "}
                            <b>
                              <u>past</u>
                            </b>{" "}
                            use/experimentation/abuse of alcohol and/or drugs
                          </>
                        }
                        checked={data.SUClientReportsPast["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                      <FormCheckbox
                        name="SUClientPeers"
                        label="Associates with peers who use alcohol and/or drugs"
                        checked={data.SUClientPeers["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <br />
                    <b>Caregiver:</b>
                    <br />
                    <div className="ms-3">
                      <FormCheckbox
                        name="SUCaregiverSuspects"
                        label="Suspects or has concerns about potential use by client"
                        checked={data.SUCaregiverSuspects["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                      <FormCheckbox
                        name="SUCaregiverEvidence"
                        label="Has found evidence of substance related paraphernalia belonging to client"
                        checked={data.SUCaregiverEvidence["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    {data.SUClientDenies["@value"] === "true" ||
                    data.SUClientReportsCurrent["@value"] === "true" ||
                    data.SUClientReportsPast["@value"] === "true" ||
                    data.SUClientPeers["@value"] === "true" ||
                    data.SUCaregiverSuspects["@value"] === "true" ||
                    data.SUCaregiverEvidence["@value"] === "true" ? (
                      <>
                        <FormTextArea
                          name="SUComments"
                          label="Comments:"
                          error={errors.SUComments}
                          value={data.SUComments["@value"]}
                          onChange={handleChange}
                          identical={compare("SUComments")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {data.SUAge["@value"] !== "true" ? (
                          <>
                            <b>
                              Based on your answers in this section, please
                              complete the{" "}
                              <Link
                                to={`/client/${clientid}?tab=cpass`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Full Alcohol and Drug Use Assessment Tool
                              </Link>{" "}
                              (CRAFFT).
                            </b>
                            <br />
                            <br />
                          </>
                        ) : null}
                      </>
                    ) : null}
                  </>
                ) : null}
                {data.SUAge["@value"] === "true" && (
                  <>
                    <b>
                      Based on your answers in this section, please complete the{" "}
                      <Link
                        to={`/client/${clientid}?tab=cpass`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Full Alcohol and Drug Use Assessment Tool
                      </Link>{" "}
                      (CRAFFT).
                    </b>
                    <br />
                    <br />
                  </>
                )}
                {data.SubstanceUseNA["@value"] !== "true" ? (
                  <>
                    {data.PSDType["@value"] !==
                      "Psychiatric Diagnostic Evaluation" && (
                      <>
                        <b>Substance Use History</b>
                        <FieldTable
                          name="SUHistory"
                          commentsHandler={handleComments}
                        >
                          <div className="container border-top border-start border-end mb-2">
                            <div className="row border-bottom">
                              <div className="col border-end py-2">
                                Substance
                              </div>
                              <div className="col border-end py-2">
                                Type of Use
                              </div>
                              <div className="col-1 border-end py-2">
                                Age of First Use
                              </div>
                              <div className="col-1 border-end py-2">
                                Age of Last Use
                              </div>
                              <div className="col py-2">
                                Describe any problems/issues related to use
                              </div>
                            </div>
                            {data.SUHistory["@value"]
                              ? JSON.parse(data.SUHistory["@value"]).map(
                                  (item, index) => (
                                    <div
                                      key={item._id}
                                      className="row border-bottom"
                                    >
                                      <div className="col border-end py-2 d-flex">
                                        {item.IsEditable === undefined ||
                                        item.IsEditable ? (
                                          <>
                                            <i
                                              className="fas fa-save me-2 d-print-none"
                                              onClick={() => {
                                                handleSaveRow(
                                                  index,
                                                  "SUHistory"
                                                );
                                              }}
                                            ></i>
                                            <i
                                              className="far fa-trash-alt me-2 d-print-none"
                                              onClick={() => {
                                                handleDeleteRow(
                                                  index,
                                                  "SUHistory"
                                                );
                                              }}
                                            ></i>
                                            <FormInput
                                              name="Substance"
                                              value={item.Substance}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "SUHistory"
                                                );
                                              }}
                                              disabled={formDisabled}
                                            />
                                          </>
                                        ) : (
                                          <>
                                            <i
                                              className="fas fa-pen me-2 d-print-none"
                                              onClick={() => {
                                                handleEditRow(
                                                  index,
                                                  "SUHistory"
                                                );
                                              }}
                                            ></i>
                                            {item.Substance}
                                          </>
                                        )}
                                      </div>
                                      <div className="col border-end py-2">
                                        {item.IsEditable === undefined ||
                                        item.IsEditable ? (
                                          <FormSelect
                                            name="TypeOfUse"
                                            value={item.TypeOfUse}
                                            options={typeOfUseOptions}
                                            onChange={(e) => {
                                              handleRowChange(
                                                e,
                                                index,
                                                "SUHistory"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        ) : (
                                          item.TypeOfUse
                                        )}
                                      </div>
                                      <div className="col-1 border-end py-2">
                                        {item.IsEditable === undefined ||
                                        item.IsEditable ? (
                                          <FormInput
                                            name="FirstUse"
                                            value={item.FirstUse}
                                            onChange={(e) => {
                                              handleRowChange(
                                                e,
                                                index,
                                                "SUHistory"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        ) : (
                                          item.FirstUse
                                        )}
                                      </div>
                                      <div className="col-1 border-end py-2">
                                        {item.IsEditable === undefined ||
                                        item.IsEditable ? (
                                          <FormInput
                                            name="LastUse"
                                            value={item.LastUse}
                                            onChange={(e) => {
                                              handleRowChange(
                                                e,
                                                index,
                                                "SUHistory"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        ) : (
                                          item.LastUse
                                        )}
                                      </div>
                                      <div className="col py-2">
                                        {item.IsEditable === undefined ||
                                        item.IsEditable ? (
                                          <FormInput
                                            name="Problems"
                                            value={item.Problems}
                                            onChange={(e) => {
                                              handleRowChange(
                                                e,
                                                index,
                                                "SUHistory"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        ) : (
                                          item.Problems
                                        )}
                                      </div>
                                    </div>
                                  )
                                )
                              : null}
                          </div>
                          {!formDisabled ? (
                            <>
                              <button
                                className="btn btn-primary mb-2"
                                onClick={() => {
                                  handleAddRow("SUHistory", {
                                    IsEditable: true,
                                  });
                                }}
                              >
                                Add Substance
                              </button>
                            </>
                          ) : null}
                          {errors.SUHistory !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.SUHistory}
                                </div>
                              </div>
                            </div>
                          ) : null}
                        </FieldTable>
                        <FormInput
                          name="ClientPerceptionOfSubstanceUse"
                          label="Client's perception of substance use:"
                          error={errors.ClientPerceptionOfSubstanceUse}
                          value={data.ClientPerceptionOfSubstanceUse["@value"]}
                          onChange={handleChange}
                          identical={compare("ClientPerceptionOfSubstanceUse")}
                          disabled={formDisabled}
                        />
                        <FormInput
                          name="CaregiverPerceptionOfSubstanceUse"
                          label="Caregiver's perception of substance use:"
                          error={errors.CaregiverPerceptionOfSubstanceUse}
                          value={
                            data.CaregiverPerceptionOfSubstanceUse["@value"]
                          }
                          onChange={handleChange}
                          identical={compare(
                            "CaregiverPerceptionOfSubstanceUse"
                          )}
                          disabled={formDisabled}
                        />
                        <br />
                        <b>Alcohol/Drug Treatment History</b>
                        <FormCheckbox
                          name="NoPastTreatment"
                          label="No history of alcohol/drug treatment"
                          checked={data.NoPastTreatment["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-3"
                        />
                        {data.NoPastTreatment["@value"] !== "true" && (
                          <>
                            <FieldTable
                              name="SUTreatmentHistory"
                              commentsHandler={handleComments}
                            >
                              <div className="container border-top border-start border-end mb-2">
                                <div className="row border-bottom">
                                  <div className="col border-end py-2">
                                    Agency/Facility
                                  </div>
                                  <div className="col border-end py-2">
                                    Type of Treatment
                                  </div>
                                  <div className="col border-end py-2">
                                    Dates
                                  </div>
                                  <div className="col border-end py-2">
                                    Reason
                                  </div>
                                  <div className="col py-2">Outcome</div>
                                </div>
                                {data.SUTreatmentHistory["@value"]
                                  ? JSON.parse(
                                      data.SUTreatmentHistory["@value"]
                                    ).map((item, index) => (
                                      <div
                                        key={item._id}
                                        className="row border-bottom"
                                      >
                                        <div className="col border-end py-2 d-flex">
                                          {item.IsEditable === undefined ||
                                          item.IsEditable ? (
                                            <>
                                              <i
                                                className="fas fa-save me-2 d-print-none"
                                                onClick={() => {
                                                  handleSaveRow(
                                                    index,
                                                    "SUTreatmentHistory"
                                                  );
                                                }}
                                              ></i>
                                              <i
                                                className="far fa-trash-alt me-2 d-print-none"
                                                onClick={() => {
                                                  handleDeleteRow(
                                                    index,
                                                    "SUTreatmentHistory"
                                                  );
                                                }}
                                              ></i>
                                              <FormInput
                                                name="Agency"
                                                value={item.Agency}
                                                onChange={(e) => {
                                                  handleRowChange(
                                                    e,
                                                    index,
                                                    "SUTreatmentHistory"
                                                  );
                                                }}
                                                disabled={formDisabled}
                                              />
                                            </>
                                          ) : (
                                            <>
                                              <i
                                                className="fas fa-pen me-2 d-print-none"
                                                onClick={() => {
                                                  handleEditRow(
                                                    index,
                                                    "SUTreatmentHistory"
                                                  );
                                                }}
                                              ></i>
                                              {item.Agency}
                                            </>
                                          )}
                                        </div>
                                        <div className="col border-end py-2">
                                          {item.IsEditable === undefined ||
                                          item.IsEditable ? (
                                            <FormSelect
                                              name="TypeOfTreatment"
                                              value={item.TypeOfTreatment}
                                              options={typeOfTreatmentOptions}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "SUTreatmentHistory"
                                                );
                                              }}
                                              disabled={formDisabled}
                                            />
                                          ) : (
                                            item.TypeOfTreatment
                                          )}
                                        </div>
                                        <div className="col border-end py-2">
                                          {item.IsEditable === undefined ||
                                          item.IsEditable ? (
                                            <FormInput
                                              name="Dates"
                                              value={item.Dates}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "SUTreatmentHistory"
                                                );
                                              }}
                                              disabled={formDisabled}
                                            />
                                          ) : (
                                            item.Dates
                                          )}
                                        </div>
                                        <div className="col border-end py-2">
                                          {item.IsEditable === undefined ||
                                          item.IsEditable ? (
                                            <FormInput
                                              name="Reason"
                                              value={item.Reason}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "SUTreatmentHistory"
                                                );
                                              }}
                                              disabled={formDisabled}
                                            />
                                          ) : (
                                            item.Reason
                                          )}
                                        </div>
                                        <div className="col py-2">
                                          {item.IsEditable === undefined ||
                                          item.IsEditable ? (
                                            <FormSelect
                                              name="Outcome"
                                              value={item.Outcome}
                                              options={outcomeOptions}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "SUTreatmentHistory"
                                                );
                                              }}
                                              disabled={formDisabled}
                                            />
                                          ) : (
                                            item.Outcome
                                          )}
                                        </div>
                                      </div>
                                    ))
                                  : null}
                              </div>
                              {!formDisabled ? (
                                <>
                                  <button
                                    className="btn btn-primary mb-2"
                                    onClick={() => {
                                      handleAddRow("SUTreatmentHistory", {
                                        IsEditable: true,
                                      });
                                    }}
                                  >
                                    Add Treatment History
                                  </button>
                                </>
                              ) : null}
                              {errors.SUTreatmentHistory !== undefined ? (
                                <div className="row">
                                  <div className="col">
                                    <div className="alert alert-danger">
                                      {errors.SUTreatmentHistory}
                                    </div>
                                  </div>
                                </div>
                              ) : null}
                            </FieldTable>
                            <FormInput
                              name="SUClientPerspectiveOfTreatment"
                              label="Client's perspective of treatment:"
                              error={errors.SUClientPerspectiveOfTreatment}
                              value={
                                data.SUClientPerspectiveOfTreatment["@value"]
                              }
                              onChange={handleChange}
                              identical={compare(
                                "SUClientPerspectiveOfTreatment"
                              )}
                              disabled={formDisabled}
                            />
                            <FormInput
                              name="SUCaregiverPerspectiveOfTreatment"
                              label="Caregiver's perspective of treatment:"
                              error={errors.SUCaregiverPerspectiveOfTreatment}
                              value={
                                data.SUCaregiverPerspectiveOfTreatment["@value"]
                              }
                              onChange={handleChange}
                              identical={compare(
                                "SUCaregiverPerspectiveOfTreatment"
                              )}
                              disabled={formDisabled}
                            />
                          </>
                        )}
                      </>
                    )}
                  </>
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="TraumaHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Trauma History"
              >
                <FormCheckbox
                  name="TraumaNA"
                  label="No history of abuse or trauma"
                  checked={data.TraumaNA["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                  divClassName="ms-3"
                />
                {data.TraumaNA["@value"] !== "true" ? (
                  <>
                    <br />
                    <FieldTable
                      name="TraumaHistory"
                      commentsHandler={handleComments}
                    >
                      <div className="container border-top border-start border-end mb-2">
                        <div className="row border-bottom">
                          <div className="col border-end py-2">Trauma</div>
                          <div className="col border-end py-2">Status</div>
                          <div className="col py-2">Comments</div>
                        </div>
                        {data.TraumaHistory["@value"]
                          ? JSON.parse(data.TraumaHistory["@value"]).map(
                              (item, index) => (
                                <div
                                  key={item._id}
                                  className="row border-bottom"
                                >
                                  <div className="col border-end py-2 d-flex">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <i
                                          className="fas fa-save me-2 d-print-none"
                                          onClick={() => {
                                            handleSaveRow(
                                              index,
                                              "TraumaHistory"
                                            );
                                            buildClinicalNarrativeSummary();
                                          }}
                                        ></i>
                                        <i
                                          className="far fa-trash-alt me-2 d-print-none"
                                          onClick={() => {
                                            handleDeleteRow(
                                              index,
                                              "TraumaHistory"
                                            );
                                            buildClinicalNarrativeSummary();
                                          }}
                                        ></i>
                                        <FormSelect
                                          name="Trauma"
                                          value={item.Trauma}
                                          options={traumaOptions}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "TraumaHistory"
                                            );
                                            buildClinicalNarrativeSummary();
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <i
                                          className="fas fa-pen me-2 d-print-none"
                                          onClick={() => {
                                            handleEditRow(
                                              index,
                                              "TraumaHistory"
                                            );
                                            buildClinicalNarrativeSummary();
                                          }}
                                        ></i>
                                        {item.Trauma}
                                      </>
                                    )}
                                  </div>
                                  <div className="col border-end py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormSelect
                                        name="Status"
                                        value={item.Status}
                                        options={statusOptions}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "TraumaHistory"
                                          );
                                          buildClinicalNarrativeSummary();
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Status
                                    )}
                                  </div>
                                  <div className="col py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormInput
                                        name="Comments"
                                        value={item.Comments}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "TraumaHistory"
                                          );
                                          buildClinicalNarrativeSummary();
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Comments
                                    )}
                                  </div>
                                </div>
                              )
                            )
                          : null}
                      </div>
                      {!formDisabled ? (
                        <>
                          <button
                            className="btn btn-primary mb-2"
                            onClick={() => {
                              handleAddRow("TraumaHistory", {
                                IsEditable: true,
                              });
                            }}
                          >
                            Add Trauma History
                          </button>
                        </>
                      ) : null}
                      {errors.TraumaHistory !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.TraumaHistory}
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </FieldTable>
                    <br />
                    <b>Trauma Symptoms/Responses:</b>
                    <div className="container">
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="DistressingMemories"
                            label="Distressing memories"
                            divClassName="ms-3"
                            checked={
                              data.DistressingMemories["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="BlockOut"
                            label="Blocking out events"
                            divClassName="ms-3"
                            checked={data.BlockOut["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="Hypervigilance"
                            label="Hypervigilance"
                            divClassName="ms-3"
                            checked={data.Hypervigilance["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="PhysiologicalResponses"
                            label="Physiological responses"
                            divClassName="ms-3"
                            checked={
                              data.PhysiologicalResponses["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="LossOfMemories"
                            label="Loss of memories"
                            divClassName="ms-3"
                            checked={data.LossOfMemories["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="Hyperarousal"
                            label="Hyperarousal"
                            divClassName="ms-3"
                            checked={data.Hyperarousal["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="SleepDisturbances"
                            label="Sleep disturbances"
                            divClassName="ms-3"
                            checked={
                              data.SleepDisturbances["@value"] === "true"
                            }
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="DevelopmentalRegression"
                            label="Developmental regression"
                            divClassName="ms-3"
                            checked={
                              data.DevelopmentalRegression["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="Nightmares"
                            label="Frightening dreams"
                            divClassName="ms-3"
                            checked={data.Nightmares["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="Avoidant"
                            label="Avoidant behaviors"
                            divClassName="ms-3"
                            checked={data.Avoidant["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="DifficultyConcentratingSymptom"
                            label="Difficulty concentrating"
                            divClassName="ms-3"
                            checked={
                              data.DifficultyConcentratingSymptom["@value"] ===
                              "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="UnhealthyBoundaries"
                            label="Unhealthy boundaries"
                            divClassName="ms-3"
                            checked={
                              data.UnhealthyBoundaries["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="Flashbacks"
                            label="Flashbacks"
                            divClassName="ms-3"
                            checked={data.Flashbacks["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="Detachment"
                            label="Detachment"
                            divClassName="ms-3"
                            checked={data.Detachment["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="EmotionalDysregulation"
                            label="Emotional dysregulation"
                            divClassName="ms-3"
                            checked={
                              data.EmotionalDysregulation["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="RepetitivePlayOfEvents"
                            label="Repetitive play of events"
                            divClassName="ms-3"
                            checked={
                              data.RepetitivePlayOfEvents["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="PsychologicalDistress"
                            label="Psychological distress"
                            divClassName="ms-3"
                            checked={
                              data.PsychologicalDistress["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="DiminishedInterests"
                            label="Diminished interests"
                            divClassName="ms-3"
                            checked={
                              data.DiminishedInterests["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="Dissocation"
                            label="Dissocation"
                            divClassName="ms-3"
                            checked={data.Dissocation["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="NegativeCognitions"
                            label="Negative cognitions"
                            divClassName="ms-3"
                            checked={
                              data.NegativeCognitions["@value"] === "true"
                            }
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-3">
                          <FormCheckbox
                            name="SocialWithdrawalSymptom"
                            label="Social withdrawal"
                            divClassName="ms-3"
                            checked={
                              data.SocialWithdrawalSymptom["@value"] === "true"
                            }
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-3">
                          <FormCheckbox
                            name="NoSymptoms"
                            label="None reported"
                            divClassName="ms-3"
                            checked={
                              data.NoSymptoms &&
                              data.NoSymptoms["@value"] === "true"
                            }
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                    {errors.TraumaSymptomsResponse !== undefined ? (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.TraumaSymptomsResponse}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <br />
                    <b>Protective Factors:</b>
                    <div className="container">
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="SocialSupports"
                            label="Social supports"
                            divClassName="ms-3"
                            checked={data.SocialSupports["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="SenseOfMastery"
                            label="Sense of mastery"
                            divClassName="ms-3"
                            checked={data.SenseOfMastery["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="CaregiverStability"
                            label="Caregiver stability"
                            divClassName="ms-3"
                            checked={
                              data.CaregiverStability["@value"] === "true"
                            }
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="StrongSpiritualBeliefs"
                            label="Strong spiritual beliefs"
                            divClassName="ms-3"
                            checked={
                              data.StrongSpiritualBeliefs["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="InterpersonalSkills"
                            label="Interpersonal skills"
                            divClassName="ms-3"
                            checked={
                              data.InterpersonalSkills["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="PositiveOutlook"
                            label="Positive outlook"
                            divClassName="ms-3"
                            checked={data.PositiveOutlook["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="PlacementStability"
                            label="Placement stability"
                            divClassName="ms-3"
                            checked={
                              data.PlacementStability["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="Pets"
                            label="Pets"
                            divClassName="ms-3"
                            checked={data.Pets["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="ValidatingExperiences"
                            label="Validating experiences"
                            divClassName="ms-3"
                            checked={
                              data.ValidatingExperiences["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="Resources"
                            label="Resources"
                            divClassName="ms-3"
                            checked={data.Resources["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="TiesToExtendedFamily"
                            label="Ties to extended family"
                            divClassName="ms-3"
                            checked={
                              data.TiesToExtendedFamily["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="Adaptability"
                            label="Adaptability"
                            divClassName="ms-3"
                            checked={data.Adaptability["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="SecureAttachments"
                            label="Secure attachments"
                            divClassName="ms-3"
                            checked={
                              data.SecureAttachments["@value"] === "true"
                            }
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="StrongCulturalIdentity"
                            label="Strong cultural identity"
                            divClassName="ms-3"
                            checked={
                              data.StrongCulturalIdentity["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="Resilience"
                            label="Resilience"
                            divClassName="ms-3"
                            checked={
                              data.Resilience &&
                              data.Resilience["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="NoTraumaProtectiveFactors"
                            label="None reported"
                            divClassName="ms-3"
                            checked={
                              data.NoTraumaProtectiveFactors &&
                              data.NoTraumaProtectiveFactors["@value"] ===
                                "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                    {errors.TraumaProtectiveFactors !== undefined ? (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.TraumaProtectiveFactors}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <FormTextArea
                      name="CopingStrategies"
                      label="Coping strategies:"
                      error={errors.CopingStrategies}
                      value={data.CopingStrategies["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("CopingStrategies")}
                      disabled={formDisabled}
                    />
                    <FormRadioButton
                      name="ClientAwareOfTrauma"
                      label="Client is aware of traumatic events/reports:"
                      error={errors.ClientAwareOfTrauma}
                      value={data.ClientAwareOfTrauma["@value"]}
                      options={awarenessOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormTextArea
                      name="ClientPerspectiveOfTrauma"
                      label="Client's perspective of trauma:"
                      error={errors.ClientPerspectiveOfTrauma}
                      value={data.ClientPerspectiveOfTrauma["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("ClientPerspectiveOfTrauma")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormTextArea
                      name="CaregiverPerspectiveOfTrauma"
                      label="Caregiver's perspective of trauma:"
                      error={errors.CaregiverPerspectiveOfTrauma}
                      value={data.CaregiverPerspectiveOfTrauma["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("CaregiverPerspectiveOfTrauma")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </>
                ) : null}
              </ExpandDiv>
              {data.PSDType["@value"] !==
              "Psychiatric Diagnostic Evaluation" ? (
                <ExpandDiv
                  id="MentalStatus"
                  expandStatus={expandStatus}
                  className="mt-2"
                  title="Mental Status"
                >
                  <div className="container">
                    <div className="row border-bottom">
                      <div className="col-3">Appearance</div>
                      <div className="col-9 d-flex flex-wrap">
                        <FormCheckbox
                          name="AppropriateAppearance"
                          label="Appropriate"
                          divClassName="ms-3"
                          checked={
                            data.AppropriateAppearance["@value"] === "true"
                          }
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Neat"
                          label="Neat"
                          divClassName="ms-3"
                          checked={data.Neat["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Disheveled"
                          label="Disheveled"
                          divClassName="ms-3"
                          checked={data.Disheveled["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="BizarreAppearance"
                          label="Bizarre"
                          divClassName="ms-3"
                          checked={data.BizarreAppearance["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="OtherAppearance"
                          label="Other"
                          divClassName="ms-3"
                          checked={data.OtherAppearance["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        {data.OtherAppearance["@value"] === "true" ? (
                          <FormInput
                            name="OtherAppearanceDetails"
                            label="Comments:"
                            error={errors.OtherAppearanceDetails}
                            value={data.OtherAppearanceDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("OtherAppearanceDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    {errors.MSAppearance !== undefined ? (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.MSAppearance}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <div className="row mt-2 border-bottom">
                      <div className="col-3">Behavior</div>
                      <div className="col-9 d-flex flex-wrap">
                        <FormCheckbox
                          name="Cooperative"
                          label="Cooperative"
                          divClassName="ms-3"
                          checked={data.Cooperative["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Guarded"
                          label="Guarded"
                          divClassName="ms-3"
                          checked={data.Guarded["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Hyperactive"
                          label="Hyperactive"
                          divClassName="ms-3"
                          checked={data.Hyperactive["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Agitated"
                          label="Agitated"
                          divClassName="ms-3"
                          checked={data.Agitated["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Paranoid"
                          label="Paranoid"
                          divClassName="ms-3"
                          checked={data.Paranoid["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Tics"
                          label="Tics"
                          divClassName="ms-3"
                          checked={data.Tics["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Impulsive"
                          label="Impulsive"
                          divClassName="ms-3"
                          checked={data.Impulsive["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Aggressive"
                          label="Aggressive"
                          divClassName="ms-3"
                          checked={data.Aggressive["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Withdrawn"
                          label="Withdrawn"
                          divClassName="ms-3"
                          checked={data.Withdrawn["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="BizarreBehavior"
                          label="Bizarre"
                          divClassName="ms-3"
                          checked={data.BizarreBehavior["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Rigidity"
                          label="Rigidity"
                          divClassName="ms-3"
                          checked={data.Rigidity["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="OtherBehavior"
                          label="Other"
                          divClassName="ms-3"
                          checked={data.OtherBehavior["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        {data.OtherBehavior["@value"] === "true" ? (
                          <FormInput
                            name="OtherBehaviorDetails"
                            label="Comments:"
                            error={errors.OtherBehaviorDetails}
                            value={data.OtherBehaviorDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("OtherBehaviorDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    {errors.MSBehavior !== undefined ? (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.MSBehavior}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <div className="row mt-2 border-bottom">
                      <div className="col-3">Speech</div>
                      <div className="col-9 d-flex flex-wrap">
                        <FormCheckbox
                          name="Normal"
                          label="Normal"
                          divClassName="ms-3"
                          checked={data.Normal["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Slurred"
                          label="Slurred"
                          divClassName="ms-3"
                          checked={data.Slurred["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Pressured"
                          label="Pressured"
                          divClassName="ms-3"
                          checked={data.Pressured["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Tangential"
                          label="Tangential"
                          divClassName="ms-3"
                          checked={data.Tangential["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Incoherent"
                          label="Incoherent"
                          divClassName="ms-3"
                          checked={data.Incoherent["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Stutters"
                          label="Stutters"
                          divClassName="ms-3"
                          checked={data.Stutters["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="OtherSpeech"
                          label="Other"
                          divClassName="ms-3"
                          checked={data.OtherSpeech["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        {data.OtherSpeech["@value"] === "true" ? (
                          <FormInput
                            name="OtherSpeechDetails"
                            label="Comments:"
                            error={errors.OtherSpeechDetails}
                            value={data.OtherSpeechDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("OtherSpeechDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    {errors.MSSpeech !== undefined ? (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.MSSpeech}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <div className="row mt-2 border-bottom">
                      <div className="col-3">Mood</div>
                      <div className="col-9 d-flex flex-wrap">
                        <FormCheckbox
                          name="Euthymic"
                          label="Euthymic"
                          divClassName="ms-3"
                          checked={data.Euthymic["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Anxious"
                          label="Anxious"
                          divClassName="ms-3"
                          checked={data.Anxious["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Angry"
                          label="Angry"
                          divClassName="ms-3"
                          checked={data.Angry["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Depressed"
                          label="Depressed"
                          divClassName="ms-3"
                          checked={data.Depressed["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Euphoric"
                          label="Euphoric"
                          divClassName="ms-3"
                          checked={data.Euphoric["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Irritable"
                          label="Irritable"
                          divClassName="ms-3"
                          checked={data.Irritable["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Fearful"
                          label="Fearful"
                          divClassName="ms-3"
                          checked={data.Fearful["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="OtherMood"
                          label="Other"
                          divClassName="ms-3"
                          checked={data.OtherMood["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        {data.OtherMood["@value"] === "true" ? (
                          <FormInput
                            name="OtherMoodDetails"
                            label="Comments:"
                            error={errors.OtherMoodDetails}
                            value={data.OtherMoodDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("OtherMoodDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    {errors.MSMood !== undefined ? (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.MSMood}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <div className="row mt-2 border-bottom">
                      <div className="col-3">Affect</div>
                      <div className="col-9 d-flex flex-wrap">
                        <FormCheckbox
                          name="FullRange"
                          label="Full range"
                          divClassName="ms-3"
                          checked={data.FullRange["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Restricted"
                          label="Restricted"
                          divClassName="ms-3"
                          checked={data.Restricted["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Blunted"
                          label="Blunted"
                          divClassName="ms-3"
                          checked={data.Blunted["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Flat"
                          label="Flat"
                          divClassName="ms-3"
                          checked={data.Flat["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Inappropriate"
                          label="Inappropriate"
                          divClassName="ms-3"
                          checked={data.Inappropriate["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="OtherAffect"
                          label="Other"
                          divClassName="ms-3"
                          checked={data.OtherAffect["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        {data.OtherAffect["@value"] === "true" ? (
                          <FormInput
                            name="OtherAffectDetails"
                            label="Comments:"
                            error={errors.OtherAffectDetails}
                            value={data.OtherAffectDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("OtherAffectDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    {errors.MSAffect !== undefined ? (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.MSAffect}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <div className="row mt-2 border-bottom">
                      <div className="col-3">Thought Process/Perception</div>
                      <div className="col-9 d-flex flex-wrap">
                        <FormCheckbox
                          name="Logical"
                          label="Logical"
                          divClassName="ms-3"
                          checked={data.Logical["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Preoccupations"
                          label="Preoccupations"
                          divClassName="ms-3"
                          checked={data.Preoccupations["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Delusions"
                          label="Delusions"
                          divClassName="ms-3"
                          checked={data.Delusions["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Hallucinations"
                          label="Hallucinations"
                          divClassName="ms-3"
                          checked={data.Hallucinations["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Racing"
                          label="Racing"
                          divClassName="ms-3"
                          checked={data.Racing["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Disorganized"
                          label="Disorganized"
                          divClassName="ms-3"
                          checked={data.Disorganized["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Concrete"
                          label="Concrete"
                          divClassName="ms-3"
                          checked={data.Concrete["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="SuicidalIdeation"
                          label="Suicidal Ideation"
                          divClassName="ms-3"
                          checked={data.SuicidalIdeation["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="HomicidalIdeation"
                          label="Homicidal Ideation"
                          divClassName="ms-3"
                          checked={data.HomicidalIdeation["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="OtherPerception"
                          label="Other"
                          divClassName="ms-3"
                          checked={data.OtherPerception["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        {data.OtherPerception["@value"] === "true" ? (
                          <FormInput
                            name="OtherPerceptionDetails"
                            label="Comments:"
                            error={errors.OtherPerceptionDetails}
                            value={data.OtherPerceptionDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("OtherPerceptionDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    {errors.MSPerception !== undefined ? (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.MSPerception}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <div className="row mt-2 border-bottom">
                      <div className="col-3">
                        Cognition/Intellectual Functioning
                      </div>
                      <div className="col-9 d-flex flex-wrap">
                        <FormCheckbox
                          name="NoImpairment"
                          label="No Impairment"
                          divClassName="ms-3"
                          checked={data.NoImpairment["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="ShortAttentionSpan"
                          label="Short Attention Span"
                          divClassName="ms-3"
                          checked={data.ShortAttentionSpan["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="DifficultyConcentrating"
                          label="Difficulty Concentrating"
                          divClassName="ms-3"
                          checked={
                            data.DifficultyConcentrating["@value"] === "true"
                          }
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Distractibility"
                          label="Distractibility"
                          divClassName="ms-3"
                          checked={data.Distractibility["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="MemoryImpairment"
                          label="Memory Impairment"
                          divClassName="ms-3"
                          checked={data.MemoryImpairment["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="LowIntelligence"
                          label="Low Intelligence"
                          divClassName="ms-3"
                          checked={data.LowIntelligence["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="OtherCognition"
                          label="Other"
                          divClassName="ms-3"
                          checked={data.OtherCognition["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        {data.OtherCognition["@value"] === "true" ? (
                          <FormInput
                            name="OtherCognitionDetails"
                            label="Comments:"
                            error={errors.OtherCognitionDetails}
                            value={data.OtherCognitionDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("OtherCognitionDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    {errors.MSCognition !== undefined ? (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.MSCognition}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <div className="row mt-2 border-bottom">
                      <div className="col-3">Insight/Judgment</div>
                      <div className="col-9 d-flex flex-wrap">
                        <FormCheckbox
                          name="AppropriateInsight"
                          label="Appropriate"
                          divClassName="ms-3"
                          checked={data.AppropriateInsight["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Fair"
                          label="Fair"
                          divClassName="ms-3"
                          checked={data.Fair["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="Impaired"
                          label="Impaired"
                          divClassName="ms-3"
                          checked={data.Impaired["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        <FormCheckbox
                          name="OtherInsight"
                          label="Other"
                          divClassName="ms-3"
                          checked={data.OtherInsight["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divStyle={{ width: "200px" }}
                        />
                        {data.OtherInsight["@value"] === "true" ? (
                          <FormInput
                            name="OtherInsightDetails"
                            label="Comments:"
                            error={errors.OtherInsightDetails}
                            value={data.OtherInsightDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("OtherInsightDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    {errors.MSInsight !== undefined ? (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.MSInsight}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <div className="row mt-2">
                      <div className="col-3">Comments/Observations</div>
                      <div className="col-9">
                        <FormTextArea
                          name="MentalStatusComments"
                          error={errors.MentalStatusComments}
                          value={data.MentalStatusComments["@value"]}
                          onChange={handleChange}
                          identical={compare("MentalStatusComments")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                  </div>
                </ExpandDiv>
              ) : null}
              <ExpandDiv
                id="Lethality"
                expandStatus={expandStatus}
                className="mt-2"
                title="Lethality"
              >
                <FormCheckbox
                  name="LethalityNA"
                  label="N/A - youth is 5 or under with no history of harm to self or others"
                  divClassName="ms-3"
                  checked={data.LethalityNA["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.LethalityNA["@value"] !== "true" && (
                  <>
                    <b>Suicide/Harm to Self</b>
                    <div className="container border-top border-start border-end mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Self-injurious or Self-abusive Behavior
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="SelfAbusiveBehavior"
                            error={errors.SelfAbusiveBehavior}
                            value={data.SelfAbusiveBehavior["@value"]}
                            options={timeframeOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Suicidal threats/gestures
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="SuicidalThreats"
                            error={errors.SuicidalThreats}
                            value={data.SuicidalThreats["@value"]}
                            options={timeframeOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Suicide Attempt
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="SuicideAttempt"
                            error={errors.SuicideAttempt}
                            value={data.SuicideAttempt["@value"]}
                            options={timeframeOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Parent/Caregiver concerns of suicide/self-harm
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="CaregiverConcernsOfSelfHarm"
                            error={errors.CaregiverConcernsOfSelfHarm}
                            value={data.CaregiverConcernsOfSelfHarm["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Other expressed concerns of suicide/self-harm
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="OtherConcernsOfSelfHarm"
                            error={errors.OtherConcernsOfSelfHarm}
                            value={data.OtherConcernsOfSelfHarm["@value"]}
                            options={yesNoNoneOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Current Risk of Harm to Self
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="CurrentSelfHarmRisk"
                            error={errors.CurrentSelfHarmRisk}
                            value={data.CurrentSelfHarmRisk["@value"]}
                            options={riskOptions}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                    {data.SelfAbusiveBehavior["@value"] !== "None/Never" ||
                    data.SuicidalThreats["@value"] !== "None/Never" ||
                    data.SuicideAttempt["@value"] !== "None/Never" ||
                    data.CaregiverConcernsOfSelfHarm["@value"] !== "No" ||
                    (data.OtherConcernsOfSelfHarm["@value"] !== "No" &&
                      data.OtherConcernsOfSelfHarm["@value"] !==
                        "None reported") ||
                    data.CurrentSelfHarmRisk["@value"] !== "None Noted" ? (
                      <FormTextArea
                        name="SelfHarmComments"
                        label="Please provide any additional information:"
                        error={errors.SelfHarmComments}
                        value={data.SelfHarmComments["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("SelfHarmComments")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    <br />
                    <b>Harm to Others/Endangerment</b>
                    <div className="container border-top border-start border-end mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Threats to harm/endanger others
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="ThreatsToHarmOthers"
                            error={errors.ThreatsToHarmOthers}
                            value={data.ThreatsToHarmOthers["@value"]}
                            options={timeframeOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Thoughts of harming/endangering others
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="ThoughtsOfHarm"
                            error={errors.ThoughtsOfHarm}
                            value={data.ThoughtsOfHarm["@value"]}
                            options={timeframeOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Acts of harm/endangering others
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="ActsOfHarm"
                            error={errors.ActsOfHarm}
                            value={data.ActsOfHarm["@value"]}
                            options={timeframeOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Acts of harm/endangering animals
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="ActsOfHarmAnimals"
                            error={errors.ActsOfHarmAnimals}
                            value={data.ActsOfHarmAnimals["@value"]}
                            options={timeframeOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Acts involving weapons
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="ActsInvolvingWeapons"
                            error={errors.ActsInvolvingWeapons}
                            value={data.ActsInvolvingWeapons["@value"]}
                            options={timeframeOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Parent/Caregiver concerns of harm/endangering others
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="CaregiverConcernOfOtherHarm"
                            error={errors.CaregiverConcernOfOtherHarm}
                            value={data.CaregiverConcernOfOtherHarm["@value"]}
                            options={yesNoNoneOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Other expressed concerns of harm/endangering others
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="OtherConcernsOfOtherHarm"
                            error={errors.OtherConcernsOfOtherHarm}
                            value={data.OtherConcernsOfOtherHarm["@value"]}
                            options={yesNoNoneOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Current Risk of Harm to Others
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="CurrentOtherHarmRisk"
                            error={errors.CurrentOtherHarmRisk}
                            value={data.CurrentOtherHarmRisk["@value"]}
                            options={riskOptions}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                    {data.ThreatsToHarmOthers["@value"] !== "None/Never" ||
                    data.ThoughtsOfHarm["@value"] !== "None/Never" ||
                    data.ActsOfHarm["@value"] !== "None/Never" ||
                    data.ActsOfHarmAnimals["@value"] !== "None/Never" ||
                    data.ActsInvolvingWeapons["@value"] !== "None/Never" ||
                    data.CaregiverConcernOfOtherHarm["@value"] !== "No" ||
                    (data.OtherConcernsOfOtherHarm["@value"] !== "No" &&
                      data.OtherConcernsOfOtherHarm["@value"] !==
                        "None reported") ||
                    data.CurrentOtherHarmRisk["@value"] !== "None Noted" ? (
                      <FormTextArea
                        name="OtherHarmComments"
                        label="Please provide any additional information:"
                        error={errors.OtherHarmComments}
                        value={data.OtherHarmComments["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("OtherHarmComments")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    {data.SelfAbusiveBehavior["@value"] === "Current" ||
                    data.SelfAbusiveBehavior["@value"] === "Last 30 Days" ||
                    data.SelfAbusiveBehavior["@value"] === "Last 6 Months" ||
                    data.SuicidalThreats["@value"] === "Current" ||
                    data.SuicidalThreats["@value"] === "Last 30 Days" ||
                    data.SuicidalThreats["@value"] === "Last 6 Months" ||
                    data.SuicideAttempt["@value"] === "Current" ||
                    data.SuicideAttempt["@value"] === "Last 30 Days" ||
                    data.SuicideAttempt["@value"] === "Last 6 Months" ? (
                      <div>
                        Based on the above answers, it is required to complete a{" "}
                        <Link
                          to={`/client/${clientid}?tab=cpass`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          CSSRS
                        </Link>
                        .
                      </div>
                    ) : null}

                    {data.SelfAbusiveBehavior["@value"] === "Current" ||
                    data.SelfAbusiveBehavior["@value"] === "Last 30 Days" ||
                    data.SelfAbusiveBehavior["@value"] === "Last 6 Months" ||
                    data.SuicidalThreats["@value"] === "Current" ||
                    data.SuicidalThreats["@value"] === "Last 30 Days" ||
                    data.SuicidalThreats["@value"] === "Last 6 Months" ||
                    data.SuicideAttempt["@value"] === "Current" ||
                    data.SuicideAttempt["@value"] === "Last 30 Days" ||
                    data.SuicideAttempt["@value"] === "Last 6 Months" ||
                    data.ThreatsToHarmOthers["@value"] === "Current" ||
                    data.ThreatsToHarmOthers["@value"] === "Last 30 Days" ||
                    data.ThreatsToHarmOthers["@value"] === "Last 6 Months" ||
                    data.ThoughtsOfHarm["@value"] === "Current" ||
                    data.ThoughtsOfHarm["@value"] === "Last 30 Days" ||
                    data.ThoughtsOfHarm["@value"] === "Last 6 Months" ||
                    data.ActsOfHarm["@value"] === "Current" ||
                    data.ActsOfHarm["@value"] === "Last 30 Days" ||
                    data.ActsOfHarm["@value"] === "Last 6 Months" ||
                    data.ActsOfHarmAnimals["@value"] === "Current" ||
                    data.ActsOfHarmAnimals["@value"] === "Last 30 Days" ||
                    data.ActsOfHarmAnimals["@value"] === "Last 6 Months" ||
                    data.ActsInvolvingWeapons["@value"] === "Current" ||
                    data.ActsInvolvingWeapons["@value"] === "Last 30 Days" ||
                    data.ActsInvolvingWeapons["@value"] === "Last 6 Months" ? (
                      <>
                        Based on the responses to these questions, please
                        complete a{" "}
                        <Link
                          to={`/client/${clientid}?tab=forms`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Safety Plan
                        </Link>
                        .
                      </>
                    ) : null}
                    <FormRadioButton
                      name="SafetyPlanStatus"
                      label="Safety plan status:"
                      error={errors.SafetyPlanStatus}
                      value={data.SafetyPlanStatus["@value"]}
                      options={safetyPlanStatusOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormTextArea
                      name="LethalityComments"
                      label="Comments/Plan:"
                      error={errors.LethalityComments}
                      value={data.LethalityComments["@value"]}
                      onChange={handleChange}
                      identical={compare("LethalityComments")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </>
                )}
              </ExpandDiv>
              <ExpandDiv
                id="SymptomChecklist"
                expandStatus={expandStatus}
                className="mt-2"
                title="Symptom Checklist"
              >
                Mark if symptoms of diagnostic category are present. Describe
                symptom <b>frequency, intensity and duration</b> if present to
                assist in formulating diagnosis.
                <br />
                <div className="border rounded mb-2 px-2 pt-2 mt-2">
                  <FormCheckbox
                    name="TraumaSymptoms"
                    label="Symptoms of Trauma and Other Stressor-Related Disorders"
                    divClassName="ms-3 fw-bold"
                    checked={data.TraumaSymptoms["@value"] === "true"}
                    onChange={handleTraumaSymptomsChange}
                    disabled={formDisabled}
                  />
                  {data.TraumaSymptoms["@value"] === "true" && (
                    <>
                      <div className="container">
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="TraumaticExposure"
                              label="Exposure to a traumatic event"
                              divClassName="ms-3"
                              checked={
                                data.TraumaticExposure["@value"] === "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="AvoidantTrauma"
                              label="Persistent avoidant behavior"
                              divClassName="ms-3"
                              checked={data.AvoidantTrauma["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="HypervigilanceTrauma"
                              label="Hypervigilance"
                              divClassName="ms-3"
                              checked={
                                data.HypervigilanceTrauma["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="IntrusiveDreams"
                              label="Intrusive dreams/memories"
                              divClassName="ms-3"
                              checked={
                                data.IntrusiveDreams["@value"] === "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="NegativeThoughts"
                              label="Negative thoughts and mood"
                              divClassName="ms-3"
                              checked={
                                data.NegativeThoughts["@value"] === "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="CocentrationProblems"
                              label="Problems with concentration"
                              divClassName="ms-3"
                              checked={
                                data.CocentrationProblems["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="RecklessBehaviorTrauma"
                              label="Irritable or reckless behavior"
                              divClassName="ms-3"
                              checked={
                                data.RecklessBehaviorTrauma["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="SleepDisturbancesTrauma"
                              label="Sleep disturbances"
                              divClassName="ms-3"
                              checked={
                                data.SleepDisturbancesTrauma["@value"] ===
                                "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="OtherTrauma"
                              label="Other:"
                              divClassName="ms-3"
                              checked={data.OtherTrauma["@value"] === "true"}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            {data.OtherTrauma["@value"] === "true" ? (
                              <FormInput
                                name="OtherTraumaDetails"
                                error={errors.OtherTraumaDetails}
                                value={data.OtherTraumaDetails["@value"]}
                                onChange={handleChange}
                                identical={compare("OtherTraumaDetails")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            ) : null}
                          </div>
                        </div>
                      </div>
                      {errors.TraumaSymptoms !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.TraumaSymptoms}
                            </div>
                          </div>
                        </div>
                      ) : null}
                      <FormInput
                        name="TraumaSymptomsFrequency"
                        label="Frequency:"
                        error={errors.TraumaSymptomsFrequency}
                        value={data.TraumaSymptomsFrequency["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("TraumaSymptomsFrequency")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="TraumaSymptomsIntensity"
                        label="Intensity:"
                        error={errors.TraumaSymptomsIntensity}
                        value={data.TraumaSymptomsIntensity["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("TraumaSymptomsIntensity")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="TraumaSymptomsDuration"
                        label="Duration:"
                        error={errors.TraumaSymptomsDuration}
                        value={data.TraumaSymptomsDuration["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("TraumaSymptomsDuration")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="TraumaSymptomsOnset"
                        label="Onset:"
                        error={errors.TraumaSymptomsOnset}
                        value={data.TraumaSymptomsOnset["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("TraumaSymptomsOnset")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      Diagnoses to Consider: Post-traumatic Stress Disorder
                      (PTSD), Acute Stress Disorder, Adjustment Disorder, Other
                      Specified Trauma-and Stressor-Related Disorder
                    </>
                  )}
                </div>
                <div className="border rounded mb-2 px-2 pt-2">
                  <FormCheckbox
                    name="AnxietyDisorder"
                    label="Symptoms of Anxiety"
                    divClassName="ms-3 fw-bold"
                    checked={data.AnxietyDisorder["@value"] === "true"}
                    onChange={handleAnxietyDisorderChange}
                    disabled={formDisabled}
                  />
                  {data.AnxietyDisorder["@value"] === "true" && (
                    <>
                      <div className="container">
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="AnxietyDisorderExcessWorry"
                              label="Excess worry"
                              divClassName="ms-3"
                              checked={
                                data.AnxietyDisorderExcessWorry["@value"] ===
                                "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="AnxietyDisorderEasilyFatigued"
                              label="Easily fatigued"
                              divClassName="ms-3"
                              checked={
                                data.AnxietyDisorderEasilyFatigued["@value"] ===
                                "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="MuscleTension"
                              label="Muscle tension"
                              divClassName="ms-3"
                              checked={data.MuscleTension["@value"] === "true"}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="Overworried"
                              label="Difficulty controlling worry"
                              divClassName="ms-3"
                              checked={data.Overworried["@value"] === "true"}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="AnxietyDisorderDifficultyConcentrating"
                              label="Difficulty concentrating"
                              divClassName="ms-3"
                              checked={
                                data.AnxietyDisorderDifficultyConcentrating[
                                  "@value"
                                ] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="SleepDisturbancesAnxiety"
                              label="Sleep difficulties"
                              divClassName="ms-3"
                              checked={
                                data.SleepDisturbancesAnxiety["@value"] ===
                                "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="RestlessAnxiety"
                              label="Restless"
                              divClassName="ms-3"
                              checked={
                                data.RestlessAnxiety["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="Irritability"
                              label="Irritability"
                              divClassName="ms-3"
                              checked={data.Irritability["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="OtherAnxiety"
                              label="Other:"
                              divClassName="ms-3"
                              checked={data.OtherAnxiety["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                            {data.OtherAnxiety["@value"] === "true" ? (
                              <FormInput
                                name="OtherAnxietyDetails"
                                error={errors.OtherAnxietyDetails}
                                value={data.OtherAnxietyDetails["@value"]}
                                onChange={handleChange}
                                identical={compare("OtherAnxietyDetails")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            ) : null}
                          </div>
                        </div>
                      </div>
                      {errors.AnxietyDisorder !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.AnxietyDisorder}
                            </div>
                          </div>
                        </div>
                      ) : null}
                      <FormInput
                        name="AnxietyDisorderFrequency"
                        label="Frequency:"
                        error={errors.AnxietyDisorderFrequency}
                        value={data.AnxietyDisorderFrequency["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("AnxietyDisorderFrequency")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="AnxietyDisorderIntensity"
                        label="Intensity:"
                        error={errors.AnxietyDisorderIntensity}
                        value={data.AnxietyDisorderIntensity["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("AnxietyDisorderIntensity")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="AnxietyDisorderDuration"
                        label="Duration:"
                        error={errors.AnxietyDisorderDuration}
                        value={data.AnxietyDisorderDuration["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("AnxietyDisorderDuration")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      Diagnosis to consider: Generalized Anxiety Disorder
                    </>
                  )}
                </div>
                <div className="border rounded mb-2 px-2 pt-2">
                  <FormCheckbox
                    name="DepressionSymptoms"
                    label="Symptoms of Depression"
                    divClassName="ms-3 fw-bold"
                    checked={data.DepressionSymptoms["@value"] === "true"}
                    onChange={handleDepressionSymptomsChange}
                    disabled={formDisabled}
                  />
                  {data.DepressionSymptoms["@value"] === "true" && (
                    <>
                      <div className="container">
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="DepressedMood"
                              label="Depressed mood most of the day, nearly every day"
                              divClassName="ms-3"
                              checked={data.DepressedMood["@value"] === "true"}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="SleepChanges"
                              label="Insomnia or hypersomnia"
                              divClassName="ms-3"
                              checked={data.SleepChanges["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="Guilt"
                              label="Feelings of worthlessness/guilt"
                              divClassName="ms-3"
                              checked={data.Guilt["@value"] === "true"}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="DiminishedInterest"
                              label="Diminished interest or pleasure in most daily activities"
                              divClassName="ms-3"
                              checked={
                                data.DiminishedInterest["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="PurposelessMovement"
                              label="Purposeless movement"
                              divClassName="ms-3"
                              checked={
                                data.PurposelessMovement["@value"] === "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="DifficultyConcentratingDepression"
                              label="Inability to think/concentrate"
                              divClassName="ms-3"
                              checked={
                                data.DifficultyConcentratingDepression !==
                                  undefined &&
                                data.DifficultyConcentratingDepression[
                                  "@value"
                                ] === "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="WeightChanges"
                              label="Significant weight loss or weight gain"
                              divClassName="ms-3"
                              checked={data.WeightChanges["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="FatigueDepression"
                              label="Fatigue"
                              divClassName="ms-3"
                              checked={
                                data.FatigueDepression["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="ThoughtsOfDeath"
                              label="Recurrent thoughts of death"
                              divClassName="ms-3"
                              checked={
                                data.ThoughtsOfDeath["@value"] === "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-4">
                            <FormCheckbox
                              name="OtherDepression"
                              label="Other:"
                              divClassName="ms-3"
                              checked={
                                data.OtherDepression["@value"] === "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            {data.OtherDepression["@value"] === "true" ? (
                              <FormInput
                                name="OtherDepressionDetails"
                                error={errors.OtherDepressionDetails}
                                value={data.OtherDepressionDetails["@value"]}
                                onChange={handleChange}
                                identical={compare("OtherDepressionDetails")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            ) : null}
                          </div>
                        </div>
                      </div>
                      {errors.DepressionSymptoms !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.DepressionSymptoms}
                            </div>
                          </div>
                        </div>
                      ) : null}
                      <FormInput
                        name="DepressionFrequency"
                        label="Frequency:"
                        error={errors.DepressionFrequency}
                        value={data.DepressionFrequency["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("DepressionFrequency")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="DepressionIntensity"
                        label="Intensity:"
                        error={errors.DepressionIntensity}
                        value={data.DepressionIntensity["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("DepressionIntensity")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="DepressionDuration"
                        label="Duration:"
                        error={errors.DepressionDuration}
                        value={data.DepressionDuration["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("DepressionDuration")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      Diagnosis to consider: Major Depressive Disorder, Other
                      Specified Depressive Disorder, Unspecified Depressive
                      Disorder
                    </>
                  )}
                </div>
                <div className="border rounded mb-2 px-2 pt-2">
                  <FormCheckbox
                    name="AttentionHyperactivity"
                    label="Symptoms of Attention and Hyperactivity"
                    divClassName="ms-3 fw-bold"
                    checked={data.AttentionHyperactivity["@value"] === "true"}
                    onChange={handleAttentionHyperactivityChange}
                    disabled={formDisabled}
                  />
                  {data.AttentionHyperactivity["@value"] === "true" && (
                    <>
                      <div className="container">
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="AttentionHyperactivityEasilyDistracted"
                              label="Easily distracted"
                              divClassName="ms-3"
                              checked={
                                data.AttentionHyperactivityEasilyDistracted !==
                                  undefined &&
                                data.AttentionHyperactivityEasilyDistracted[
                                  "@value"
                                ] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="DifficultyFollowThrough"
                              label="Difficulty following-through"
                              divClassName="ms-3"
                              checked={
                                data.DifficultyFollowThrough["@value"] ===
                                "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="Forgetful"
                              label="Forgetful"
                              divClassName="ms-3"
                              checked={data.Forgetful["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="DifficultyListening"
                              label="Difficulty listening"
                              divClassName="ms-3"
                              checked={
                                data.DifficultyListening["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="DisorganizedAttention"
                              label="Disorganized in tasks and activities"
                              divClassName="ms-3"
                              checked={
                                data.DisorganizedAttention["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="Fidgety"
                              label="Fidgety"
                              divClassName="ms-3"
                              checked={data.Fidgety["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="LackOfDetails"
                              label="Lack of attention to detail"
                              divClassName="ms-3"
                              checked={data.LackOfDetails["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="LosesThings"
                              label="Loses things"
                              divClassName="ms-3"
                              checked={data.LosesThings["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="InterruptsOthers"
                              label="Interrupts others"
                              divClassName="ms-3"
                              checked={
                                data.InterruptsOthers["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="UnableToSitStill"
                              label="Unable to sit still"
                              divClassName="ms-3"
                              checked={
                                data.UnableToSitStill["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="ExcessiveTalking"
                              label="Excessive talking"
                              divClassName="ms-3"
                              checked={
                                data.ExcessiveTalking["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="DifficultyWaiting"
                              label="Difficulty waiting for their turn"
                              divClassName="ms-3"
                              checked={
                                data.DifficultyWaiting["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-4">
                            <FormCheckbox
                              name="OtherAttention"
                              label="Other:"
                              divClassName="ms-3"
                              checked={data.OtherAttention["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                            {data.OtherAttention["@value"] === "true" ? (
                              <FormInput
                                name="OtherAttentionDetails"
                                error={errors.OtherAttentionDetails}
                                value={data.OtherAttentionDetails["@value"]}
                                onChange={handleChange}
                                identical={compare("OtherAttentionDetails")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            ) : null}
                          </div>
                        </div>
                      </div>
                      {errors.AttentionHyperactivity !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.AttentionHyperactivity}
                            </div>
                          </div>
                        </div>
                      ) : null}
                      <FormInput
                        name="AttentionHyperactivityFrequency"
                        label="Frequency:"
                        error={errors.AttentionHyperactivityFrequency}
                        value={data.AttentionHyperactivityFrequency["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("AttentionHyperactivityFrequency")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="AttentionHyperactivityIntensity"
                        label="Intensity:"
                        error={errors.AttentionHyperactivityIntensity}
                        value={data.AttentionHyperactivityIntensity["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("AttentionHyperactivityIntensity")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="AttentionHyperactivityDuration"
                        label="Duration:"
                        error={errors.AttentionHyperactivityDuration}
                        value={data.AttentionHyperactivityDuration["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("AttentionHyperactivityDuration")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      Diagnosis to consider: Attention-Deficit/Hyperactivity
                      Disorder
                    </>
                  )}
                </div>
                <div className="border rounded mb-2 px-2 pt-2">
                  <FormCheckbox
                    name="SU"
                    label="Symptoms of Substance Use Disorders"
                    divClassName="ms-3 fw-bold"
                    checked={data.SU["@value"] === "true"}
                    onChange={handleSubstanceUseSymptomsChange}
                    disabled={formDisabled}
                  />
                  {data.SU["@value"] === "true" && (
                    <>
                      <div className="container">
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="PatternOfSubstanceUse"
                              label="Takes substances in large amounts and/or over long periods of time"
                              divClassName="ms-3"
                              checked={
                                data.PatternOfSubstanceUse["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="DesiresCutDownUse"
                              label="Persistent desire to cut down on substance use"
                              divClassName="ms-3"
                              checked={
                                data.DesiresCutDownUse["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="TimeSpentObtaining"
                              label="Spends a significant amount of time obtaining substance(s)"
                              divClassName="ms-3"
                              checked={
                                data.TimeSpentObtaining["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="Cravings"
                              label="Cravings for substances"
                              divClassName="ms-3"
                              checked={data.Cravings["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="FailureOfResponsibilities"
                              label="Failure to fulfill major roles at work, school, or home"
                              divClassName="ms-3"
                              checked={
                                data.FailureOfResponsibilities["@value"] ===
                                "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="NegativeImpact"
                              label="Use consistently negatively impacts relationships"
                              divClassName="ms-3"
                              checked={data.NegativeImpact["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="AbandonsActivities"
                              label="Giving up/reducing previously enjoyed activities"
                              divClassName="ms-3"
                              checked={
                                data.AbandonsActivities["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="Tolerance"
                              label="Tolerance"
                              divClassName="ms-3"
                              checked={data.Tolerance["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="SUWithdrawal"
                              label="Withdrawal"
                              divClassName="ms-3"
                              checked={data.SUWithdrawal["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-4">
                            <FormCheckbox
                              name="SUOther"
                              label="Other:"
                              divClassName="ms-3"
                              checked={data.SUOther["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                            {data.SUOther["@value"] === "true" ? (
                              <FormInput
                                name="SUOtherDetails"
                                error={errors.SUOtherDetails}
                                value={data.SUOtherDetails["@value"]}
                                onChange={handleChange}
                                identical={compare("SUOtherDetails")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            ) : null}
                          </div>
                        </div>
                      </div>
                      {errors.SU !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.SU}
                            </div>
                          </div>
                        </div>
                      ) : null}
                      <FormInput
                        name="SubstanceUseFrequency"
                        label="Frequency:"
                        error={errors.SubstanceUseFrequency}
                        value={data.SubstanceUseFrequency["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("SubstanceUseFrequency")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="SubstanceUseIntensity"
                        label="Intensity:"
                        error={errors.SubstanceUseIntensity}
                        value={data.SubstanceUseIntensity["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("SubstanceUseIntensity")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="SubstanceUseDuration"
                        label="Duration:"
                        error={errors.SubstanceUseDuration}
                        value={data.SubstanceUseDuration["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("SubstanceUseDuration")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </>
                  )}
                </div>
                <div className="border rounded mb-2 px-2 pt-2">
                  <FormCheckbox
                    name="DMDD"
                    label="Symptoms of Disruptive Mood Dysregulation Disorder"
                    divClassName="ms-3 fw-bold"
                    checked={data.DMDD["@value"] === "true"}
                    onChange={handleDMDDChange}
                    disabled={formDisabled}
                  />
                  {data.DMDD["@value"] === "true" && (
                    <>
                      <div className="container">
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="Temper"
                              label="Severe and recurrent temper outbursts (verbally and/or behaviorally)"
                              divClassName="ms-3"
                              checked={data.Temper["@value"] === "true"}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="OutsideOutbursts"
                              label="Outbursts are outside of developmental level"
                              divClassName="ms-3"
                              checked={
                                data.OutsideOutbursts["@value"] === "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="FrequentOutbursts"
                              label="Outbursts occur three or more times per week"
                              divClassName="ms-3"
                              checked={
                                data.FrequentOutbursts["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="DMDDIrritable"
                              label="Irritable/angry mood most of the day"
                              divClassName="ms-3"
                              checked={data.DMDDIrritable["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="OutburstsAroundOthers"
                              label="Symptoms occur in home, school and/or with peers"
                              divClassName="ms-3"
                              checked={
                                data.OutburstsAroundOthers["@value"] === "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="DMDDOther"
                              label="Other:"
                              divClassName="ms-3"
                              checked={data.DMDDOther["@value"] === "true"}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            {data.DMDDOther["@value"] === "true" ? (
                              <FormInput
                                name="DMDDOtherDetails"
                                error={errors.DMDDOtherDetails}
                                value={data.DMDDOtherDetails["@value"]}
                                onChange={handleChange}
                                identical={compare("DMDDOtherDetails")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            ) : null}
                          </div>
                        </div>
                      </div>
                      {errors.DMDD !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.DMDD}
                            </div>
                          </div>
                        </div>
                      ) : null}
                      <FormInput
                        name="DMDDFrequency"
                        label="Frequency:"
                        error={errors.DMDDFrequency}
                        value={data.DMDDFrequency["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("DMDDFrequency")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="DMDDIntensity"
                        label="Intensity:"
                        error={errors.DMDDIntensity}
                        value={data.DMDDIntensity["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("DMDDIntensity")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="DMDDDuration"
                        label="Duration:"
                        error={errors.DMDDDuration}
                        value={data.DMDDDuration["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("DMDDDuration")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </>
                  )}
                </div>
                <div className="border rounded mb-2 px-2 pt-2">
                  <FormCheckbox
                    name="ConductDisorder"
                    label="Symptoms of Conduct Disorder"
                    divClassName="ms-3 fw-bold"
                    checked={data.ConductDisorder["@value"] === "true"}
                    onChange={handleConductDisorderChange}
                    disabled={formDisabled}
                  />
                  {data.ConductDisorder["@value"] === "true" && (
                    <>
                      <div className="container">
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="Aggression"
                              label="Aggression to people and/or animals"
                              divClassName="ms-3"
                              checked={data.Aggression["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="DestructionOfProperty"
                              label="Destruction of Property"
                              divClassName="ms-3"
                              checked={
                                data.DestructionOfProperty["@value"] === "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="Theft"
                              label="Deceitfulness or theft"
                              divClassName="ms-3"
                              checked={data.Theft["@value"] === "true"}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-4">
                            <FormCheckbox
                              name="SeriousViolations"
                              label="Serious violations of rules"
                              divClassName="ms-3"
                              checked={
                                data.SeriousViolations["@value"] === "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col-4">
                            <FormCheckbox
                              name="OtherConductDisorder"
                              label="Other:"
                              divClassName="ms-3"
                              checked={
                                data.OtherConductDisorder["@value"] === "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            {data.OtherConductDisorder["@value"] === "true" ? (
                              <FormInput
                                name="OtherConductDisorderDetails"
                                error={errors.OtherConductDisorderDetails}
                                value={
                                  data.OtherConductDisorderDetails["@value"]
                                }
                                onChange={handleChange}
                                identical={compare(
                                  "OtherConductDisorderDetails"
                                )}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            ) : null}
                          </div>
                        </div>
                      </div>
                      {errors.ConductDisorder !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.ConductDisorder}
                            </div>
                          </div>
                        </div>
                      ) : null}
                      <FormInput
                        name="ConductDisorderFrequency"
                        label="Frequency:"
                        error={errors.ConductDisorderFrequency}
                        value={data.ConductDisorderFrequency["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("ConductDisorderFrequency")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="ConductDisorderIntensity"
                        label="Intensity:"
                        error={errors.ConductDisorderIntensity}
                        value={
                          data.ConductDisorderIntensity !== undefined
                            ? data.ConductDisorderIntensity["@value"]
                            : ""
                        }
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("ConductDisorderIntensity")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="ConductDisorderDuration"
                        label="Duration:"
                        error={errors.ConductDisorderDuration}
                        value={data.ConductDisorderDuration["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("ConductDisorderDuration")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </>
                  )}
                </div>
                <div className="border rounded mb-2 px-2 pt-2">
                  <FormCheckbox
                    name="ODD"
                    label="Symptoms of Oppositional Defiance Disorder (ODD)"
                    divClassName="ms-3 fw-bold"
                    checked={data.ODD["@value"] === "true"}
                    onChange={handleODDChange}
                    disabled={formDisabled}
                  />
                  {data.ODD["@value"] === "true" && (
                    <>
                      <div className="container">
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="IrritableODD"
                              label="Angry/irritable mood"
                              divClassName="ms-3"
                              checked={data.IrritableODD["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="Argumentative"
                              label="Argumentative/defiant"
                              divClassName="ms-3"
                              checked={data.Argumentative["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="Vindictive"
                              label="Vindictive"
                              divClassName="ms-3"
                              checked={data.Vindictive["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-4">
                            <FormCheckbox
                              name="OtherODD"
                              label="Other:"
                              divClassName="ms-3"
                              checked={data.OtherODD["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                            {data.OtherODD["@value"] === "true" ? (
                              <FormInput
                                name="OtherODDDetails"
                                error={errors.OtherODDDetails}
                                value={data.OtherODDDetails["@value"]}
                                onChange={handleChange}
                                identical={compare("OtherODDDetails")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            ) : null}
                          </div>
                        </div>
                      </div>
                      {errors.ODD !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.ODD}
                            </div>
                          </div>
                        </div>
                      ) : null}
                      <FormInput
                        name="ODDFrequency"
                        label="Frequency:"
                        error={errors.ODDFrequency}
                        value={data.ODDFrequency["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("ODDFrequency")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="ODDIntensity"
                        label="Intensity:"
                        error={errors.ODDIntensity}
                        value={data.ODDIntensity["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("ODDIntensity")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="ODDDuration"
                        label="Duration:"
                        error={errors.ODDDuration}
                        value={data.ODDDuration["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("ODDDuration")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </>
                  )}
                </div>
                <div className="border rounded mb-2 px-2 pt-2">
                  <FormCheckbox
                    name="OCD"
                    label="Symptoms of Obsessive-Compulsive Disorder (OCD)"
                    divClassName="ms-3 fw-bold"
                    checked={data.OCD["@value"] === "true"}
                    onChange={handleOCDChange}
                    disabled={formDisabled}
                  />
                  {data.OCD["@value"] === "true" && (
                    <>
                      <div className="container">
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="OCDPresence"
                              label="Presence of obsessions, compulsions, or both"
                              divClassName="ms-3"
                              checked={data.OCDPresence["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="Obsessions"
                              label="Obsessions (recurrent and persistent thoughts, urges, or images)"
                              divClassName="ms-3"
                              checked={data.Obsessions["@value"] === "true"}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="TimeConsuming"
                              label="Obsessions or compulsions are time consuming and cause distress/impairment"
                              divClassName="ms-3"
                              checked={data.TimeConsuming["@value"] === "true"}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="Compulsions"
                              label="Compulsions/repetitive behaviors (hand washing, ordering, checking,) or mental acts (praying, counting, repeating words silently)"
                              divClassName="ms-3"
                              checked={data.Compulsions["@value"] === "true"}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="ReduceAnxiety"
                              label="Behaviors and mental acts done to prevent or reduce anxiety or distress"
                              divClassName="ms-3"
                              checked={data.ReduceAnxiety["@value"] === "true"}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="SuppressUrges"
                              label="Attempts to ignore or suppress thoughts, urges or images with other thought or action (compulsion)"
                              divClassName="ms-3"
                              checked={data.SuppressUrges["@value"] === "true"}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-4">
                            <FormCheckbox
                              name="OCDOther"
                              label="Other:"
                              divClassName="ms-3"
                              checked={data.OCDOther["@value"] === "true"}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            {data.OCDOther["@value"] === "true" ? (
                              <FormInput
                                name="OCDOtherDetails"
                                error={errors.OCDOtherDetails}
                                value={data.OCDOtherDetails["@value"]}
                                onChange={handleChange}
                                identical={compare("OCDOtherDetails")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            ) : null}
                          </div>
                        </div>
                      </div>
                      {errors.OCD !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.OCD}
                            </div>
                          </div>
                        </div>
                      ) : null}
                      <FormInput
                        name="OCDFrequency"
                        label="Frequency:"
                        error={errors.OCDFrequency}
                        value={data.OCDFrequency["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("OCDFrequency")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="OCDIntensity"
                        label="Intensity:"
                        error={errors.OCDIntensity}
                        value={data.OCDIntensity["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("OCDIntensity")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="OCDDuration"
                        label="Duration:"
                        error={errors.OCDDuration}
                        value={data.OCDDuration["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("OCDDuration")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </>
                  )}
                </div>
                <div className="border rounded mb-2 px-2 pt-2">
                  <FormCheckbox
                    name="PanicDisorder"
                    label="Symptoms of Panic Disorder"
                    divClassName="ms-3 fw-bold"
                    checked={data.PanicDisorder["@value"] === "true"}
                    onChange={handlePanicDisorderChange}
                    disabled={formDisabled}
                  />
                  {data.PanicDisorder["@value"] === "true" && (
                    <>
                      <div className="container">
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="PoundingHeart"
                              label="Palpitations, pounding heart, accelerated heart rate"
                              divClassName="ms-3"
                              checked={data.PoundingHeart["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="PanicDisorderSweating"
                              label="Sweating"
                              divClassName="ms-3"
                              checked={
                                data.PanicDisorderSweating["@value"] === "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="Trembling"
                              label="Trembling/shaking"
                              divClassName="ms-3"
                              checked={data.Trembling["@value"] === "true"}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="ShortnessOfBreath"
                              label="Shortness of breath"
                              divClassName="ms-3"
                              checked={
                                data.ShortnessOfBreath["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="Choking"
                              label="Feelings of choking"
                              divClassName="ms-3"
                              checked={data.Choking["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="ChestPain"
                              label="Chest pain/discomfort"
                              divClassName="ms-3"
                              checked={data.ChestPain["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="PanicDisorderNausea"
                              label="Nausea/abdominal discomfort"
                              divClassName="ms-3"
                              checked={
                                data.PanicDisorderNausea["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="Dizzy"
                              label="Dizzy/faint"
                              divClassName="ms-3"
                              checked={data.Dizzy["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="Chills"
                              label="Chills"
                              divClassName="ms-3"
                              checked={data.Chills["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="Derealization"
                              label="Derealization or depersonalization"
                              divClassName="ms-3"
                              checked={data.Derealization["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="FearOfCrazy"
                              label="Fear of losing control/going crazy"
                              divClassName="ms-3"
                              checked={data.FearOfCrazy["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="FearOfDying"
                              label="Fear of dying"
                              divClassName="ms-3"
                              checked={data.FearOfDying["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-4">
                            <FormCheckbox
                              name="PanicDisorderOther"
                              label="Other:"
                              divClassName="ms-3"
                              checked={
                                data.PanicDisorderOther["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                            {data.PanicDisorderOther["@value"] === "true" ? (
                              <FormInput
                                name="PanicDisorderOtherDetails"
                                error={errors.PanicDisorderOtherDetails}
                                value={data.PanicDisorderOtherDetails["@value"]}
                                onChange={handleChange}
                                identical={compare("PanicDisorderOtherDetails")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            ) : null}
                          </div>
                        </div>
                      </div>
                      {errors.PanicDisorder !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.PanicDisorder}
                            </div>
                          </div>
                        </div>
                      ) : null}
                      <FormInput
                        name="PanicDisorderFrequency"
                        label="Frequency:"
                        error={errors.PanicDisorderFrequency}
                        value={data.PanicDisorderFrequency["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("PanicDisorderFrequency")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="PanicDisorderIntensity"
                        label="Intensity:"
                        error={errors.PanicDisorderIntensity}
                        value={data.PanicDisorderIntensity["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("PanicDisorderIntensity")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="PanicDisorderDuration"
                        label="Duration:"
                        error={errors.PanicDisorderDuration}
                        value={data.PanicDisorderDuration["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("PanicDisorderDuration")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </>
                  )}
                </div>
                <div className="border rounded mb-2 px-2 pt-2">
                  <FormCheckbox
                    name="Phobias"
                    label="Symptoms of Phobias"
                    divClassName="ms-3 fw-bold"
                    checked={data.Phobias["@value"] === "true"}
                    onChange={handlePhobiasChange}
                    disabled={formDisabled}
                  />
                  {data.Phobias["@value"] === "true" && (
                    <>
                      <div className="container">
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="PhobiasSpecificFear"
                              label="Marked fear/anxiety about a specific object or situation"
                              divClassName="ms-3"
                              checked={
                                data.PhobiasSpecificFear["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="ImmediateFear"
                              label="Object or situation provokes immediate fear or anxiety"
                              divClassName="ms-3"
                              checked={data.ImmediateFear["@value"] === "true"}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="PhobiasAvoidance"
                              label="Avoids objective/situation"
                              divClassName="ms-3"
                              checked={
                                data.PhobiasAvoidance["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-4">
                            <FormCheckbox
                              name="FearExaggerated"
                              label="Fear and anxiety is out of proportion"
                              divClassName="ms-3"
                              checked={
                                data.FearExaggerated["@value"] === "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col-4">
                            <FormCheckbox
                              name="PhobiasOther"
                              label="Other:"
                              divClassName="ms-3"
                              checked={data.PhobiasOther["@value"] === "true"}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            {data.PhobiasOther["@value"] === "true" ? (
                              <FormInput
                                name="PhobiasOtherDetails"
                                error={errors.PhobiasOtherDetails}
                                value={data.PhobiasOtherDetails["@value"]}
                                onChange={handleChange}
                                identical={compare("PhobiasOtherDetails")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            ) : null}
                          </div>
                        </div>
                      </div>
                      {errors.Phobias !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.Phobias}
                            </div>
                          </div>
                        </div>
                      ) : null}
                      <FormInput
                        name="PhobiasFrequency"
                        label="Frequency:"
                        error={errors.PhobiasFrequency}
                        value={data.PhobiasFrequency["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("PhobiasFrequency")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="PhobiasIntensity"
                        label="Intensity:"
                        error={errors.PhobiasIntensity}
                        value={data.PhobiasIntensity["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("PhobiasIntensity")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="PhobiasDuration"
                        label="Duration:"
                        error={errors.PhobiasDuration}
                        value={data.PhobiasDuration["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("PhobiasDuration")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      Diagnosis to consider: Specific Phobia, Social Anxiety
                      Disorder (Social Phobia), Agoraphobia
                    </>
                  )}
                </div>
                <div className="border rounded mb-2 px-2 pt-2">
                  <FormCheckbox
                    name="DI"
                    label="Developmental Impairments"
                    divClassName="ms-3 fw-bold"
                    checked={data.DI["@value"] === "true"}
                    onChange={handleDIChange}
                    disabled={formDisabled}
                  />
                  {data.DI["@value"] === "true" && (
                    <>
                      <div className="container">
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="DILowIQ"
                              label="Low intelligence/IQ"
                              divClassName="ms-3"
                              checked={data.DILowIQ["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="DISensoryImpairments"
                              label="Sensory impairments"
                              divClassName="ms-3"
                              checked={
                                data.DISensoryImpairments["@value"] === "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="DISpeechDelays"
                              label="Language/speech delays"
                              divClassName="ms-3"
                              checked={data.DISpeechDelays["@value"] === "true"}
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="DIMotorCoordinationDelays"
                              label="Motor coordination delays"
                              divClassName="ms-3"
                              checked={
                                data.DIMotorCoordinationDelays["@value"] ===
                                "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="DILearningDisorder"
                              label="Specific learning disorder"
                              divClassName="ms-3"
                              checked={
                                data.DILearningDisorder["@value"] === "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="DISpatialDelays"
                              label="Visio-spatial skill delays"
                              divClassName="ms-3"
                              checked={
                                data.DISpatialDelays["@value"] === "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-4">
                            <FormCheckbox
                              name="DevelopmentalImpairmentsOther"
                              label="Other:"
                              divClassName="ms-3"
                              checked={
                                data.DevelopmentalImpairmentsOther["@value"] ===
                                "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            {data.DevelopmentalImpairmentsOther["@value"] ===
                            "true" ? (
                              <FormInput
                                name="DevelopmentalImpairmentsOtherDetails"
                                error={
                                  errors.DevelopmentalImpairmentsOtherDetails
                                }
                                value={
                                  data.DevelopmentalImpairmentsOtherDetails[
                                    "@value"
                                  ]
                                }
                                onChange={handleChange}
                                identical={compare(
                                  "DevelopmentalImpairmentsOtherDetails"
                                )}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            ) : null}
                          </div>
                        </div>
                      </div>
                      {errors.DI !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.DI}
                            </div>
                          </div>
                        </div>
                      ) : null}
                      {data.DILowIQ["@value"] === "true" ||
                      data.DISensoryImpairments["@value"] === "true" ||
                      data.DISpeechDelays["@value"] === "true" ||
                      data.DIMotorCoordinationDelays["@value"] === "true" ||
                      data.DILearningDisorder["@value"] === "true" ||
                      data.DISpatialDelays["@value"] === "true" ? (
                        <FormInput
                          name="DevelopmentalImpairmentsDetails"
                          label="Please describe:"
                          error={errors.DevelopmentalImpairmentsDetails}
                          value={data.DevelopmentalImpairmentsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("DevelopmentalImpairmentsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </>
                  )}
                </div>
                <div className="border rounded mb-2 px-2 pt-2">
                  <FormCheckbox
                    name="OtherSymptoms"
                    label="Other Symptoms"
                    divClassName="ms-3 fw-bold"
                    checked={data.OtherSymptoms["@value"] === "true"}
                    onChange={handleOtherSymptomsChange}
                    disabled={formDisabled}
                  />
                  {data.OtherSymptoms["@value"] === "true" && (
                    <>
                      <FormInput
                        name="OtherSymptomsDetails"
                        label="Please describe:"
                        error={errors.OtherSymptomsDetails}
                        value={data.OtherSymptomsDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("OtherSymptomsDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="OtherSymptomsFrequency"
                        label="Frequency:"
                        error={errors.OtherSymptomsFrequency}
                        value={data.OtherSymptomsFrequency["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("OtherSymptomsFrequency")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="OtherSymptomsIntensity"
                        label="Intensity:"
                        error={errors.OtherSymptomsIntensity}
                        value={data.OtherSymptomsIntensity["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("OtherSymptomsIntensity")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="OtherSymptomsDuration"
                        label="Duration:"
                        error={errors.OtherSymptomsDuration}
                        value={data.OtherSymptomsDuration["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("OtherSymptomsDuration")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </>
                  )}
                </div>
              </ExpandDiv>
              <ExpandDiv
                id="ClinicalNarrative"
                expandStatus={expandStatus}
                className="mt-2"
                title="Clinical Narrative Summary"
              >
                Include etiology of presenting problem and maintenance of
                problem; mental health history; alcohol/drug history; severity
                of problem; where problem occurs (functioning at home, at work,
                in community); onset of problem (acute vs. chronic); client
                motivation for treatment; whether problem is known to be
                responsive to treatment.
                <FormTextArea
                  name="ClinicalNarrativeSummary"
                  error={errors.ClinicalNarrativeSummary}
                  value={data.ClinicalNarrativeSummary["@value"]}
                  onChange={handleChange}
                  identical={compare("ClinicalNarrativeSummary")}
                  commentsHandler={handleComments}
                  rows="5"
                />
              </ExpandDiv>
              <ExpandDiv
                id="Diagnosis"
                expandStatus={expandStatus}
                className="mt-2"
                title="Diagnosis"
              >
                <FieldTable
                  name="DiagnosisDetails"
                  commentsHandler={handleComments}
                >
                  <div className="container border mb-2">
                    <div className="row border-bottom">
                      <div className="col-3 border-end py-2">ICD-10 Code</div>
                      <div className="col py-2">Diagnosis</div>
                    </div>
                    {data.DiagnosisDetails["@value"] &&
                      JSON.parse(data.DiagnosisDetails["@value"]).map(
                        (item, index) => (
                          <div key={item._id} className="row border-bottom">
                            <div className="col-3 border-end py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <i
                                    className="fas fa-save me-2 d-print-none"
                                    onClick={() => {
                                      handleSaveRow(index, "DiagnosisDetails");
                                    }}
                                  ></i>
                                  <i
                                    className="far fa-trash-alt me-2 d-print-none"
                                    onClick={() => {
                                      handleDeleteRow(
                                        index,
                                        "DiagnosisDetails"
                                      );
                                    }}
                                  ></i>
                                  <FormInput
                                    name="Code"
                                    value={item.Code}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "DiagnosisDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(index, "DiagnosisDetails");
                                    }}
                                  ></i>
                                  {item.Code}
                                </>
                              )}
                            </div>
                            <div className="col py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <FormTextArea
                                  name="Diagnosis"
                                  value={item.Diagnosis}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "DiagnosisDetails"
                                    );
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Diagnosis
                              )}
                            </div>
                          </div>
                        )
                      )}
                  </div>
                  {!formDisabled ? (
                    <button
                      className="btn btn-primary mb-2"
                      onClick={() => {
                        handleAddRow("DiagnosisDetails", { IsEditable: true });
                      }}
                    >
                      Add Diagnosis
                    </button>
                  ) : null}
                  {errors.DiagnosisDetails !== undefined && (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.DiagnosisDetails}
                        </div>
                      </div>
                    </div>
                  )}
                </FieldTable>
                {data.PSDType["@value"] !==
                  "Psychiatric Diagnostic Evaluation" && (
                  <FormRadioButton
                    name="SeriousDisturbance"
                    label="Serious Emotional Disturbance (SED):"
                    error={errors.SeriousDisturbance}
                    value={data.SeriousDisturbance["@value"]}
                    options={yesNoNAOptions}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
              </ExpandDiv>
              <ExpandDiv
                id="TreatmentRecommendations"
                expandStatus={expandStatus}
                className="mt-2"
                title="Treatment Recommendations/Needs"
              >
                {data.PSDType["@value"] !==
                  "Psychiatric Diagnostic Evaluation" && (
                  <>
                    <b>Additional Assessments/Evaluation Needs:</b>
                    <FormCheckbox
                      name="NoAdditionalNeeds"
                      label="No additional assessment/evaluation needed"
                      divClassName="ms-3 fw-bold"
                      checked={data.NoAdditionalNeeds["@value"] === "true"}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                    {data.NoAdditionalNeeds["@value"] !== "true" ? (
                      <>
                        <FieldTable
                          name="AdditionalNeeds"
                          commentsHandler={handleComments}
                        >
                          <div className="container border mb-2">
                            <div className="row border-bottom">
                              <div className="col-4 border-end py-2">
                                Additional Assessment/Evalutation Need
                              </div>
                              <div className="col py-2">Explain</div>
                            </div>
                            {data.AdditionalNeeds["@value"] &&
                              JSON.parse(data.AdditionalNeeds["@value"]).map(
                                (item, index) => (
                                  <div
                                    key={item._id}
                                    className="row border-bottom"
                                  >
                                    <div className="col-4 border-end py-2 d-flex">
                                      {item.IsEditable === undefined ||
                                      item.IsEditable ? (
                                        <>
                                          <i
                                            className="fas fa-save me-2 d-print-none"
                                            onClick={() => {
                                              handleSaveRow(
                                                index,
                                                "AdditionalNeeds"
                                              );
                                            }}
                                          ></i>
                                          <i
                                            className="far fa-trash-alt me-2 d-print-none"
                                            onClick={() => {
                                              handleDeleteRow(
                                                index,
                                                "AdditionalNeeds"
                                              );
                                            }}
                                          ></i>
                                          <FormSelect
                                            name="Need"
                                            value={item.Need}
                                            options={assessmentNeedOptions}
                                            onChange={(e) => {
                                              handleRowChange(
                                                e,
                                                index,
                                                "AdditionalNeeds"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        </>
                                      ) : (
                                        <>
                                          <i
                                            className="fas fa-pen me-2 d-print-none"
                                            onClick={() => {
                                              handleEditRow(
                                                index,
                                                "AdditionalNeeds"
                                              );
                                            }}
                                          ></i>
                                          {item.Need}
                                        </>
                                      )}
                                    </div>
                                    <div className="col py-2">
                                      {item.IsEditable === undefined ||
                                      item.IsEditable ? (
                                        <FormInput
                                          name="Explain"
                                          value={item.Explain}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "AdditionalNeeds"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      ) : (
                                        item.Explain
                                      )}
                                    </div>
                                  </div>
                                )
                              )}
                          </div>
                          {!formDisabled ? (
                            <button
                              className="btn btn-primary mb-2"
                              onClick={() => {
                                handleAddRow("AdditionalNeeds", {
                                  IsEditable: true,
                                });
                              }}
                            >
                              Add Need
                            </button>
                          ) : null}
                          {errors.AdditionalNeeds !== undefined && (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.AdditionalNeeds}
                                </div>
                              </div>
                            </div>
                          )}
                        </FieldTable>
                        <br />
                      </>
                    ) : null}
                  </>
                )}
                <b>Treatment Recommendations</b> (
                <a
                  href="https://safy365.sharepoint.com/:w:/r/sites/ComplianceandQualityImprovement/Shared%20Documents/General/Forms/Kentucky/Treatment%20Foster%20Care%20Forms/Treatment%20Recommendations%20Guide.docx?d=wd3d41d2a04ec405690e3b79129778015&csf=1&web=1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Treatment Recommendation Guide
                </a>
                ):
                <FieldTable
                  name="TreatmentRecommendations"
                  commentsHandler={handleComments}
                >
                  <div className="container border mb-2">
                    <div className="row border-bottom">
                      <div className="col border-end py-2">Recommendation</div>
                      <div className="col border-end py-2">Status</div>
                      <div className="col py-2">Reason</div>
                    </div>
                    {data.TreatmentRecommendations["@value"] &&
                      JSON.parse(data.TreatmentRecommendations["@value"]).map(
                        (item, index) => (
                          <div key={item._id} className="row border-bottom">
                            <div className="col border-end py-2 d-flex">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <i
                                    className="fas fa-save me-2 d-print-none"
                                    onClick={() => {
                                      handleSaveRow(
                                        index,
                                        "TreatmentRecommendations"
                                      );
                                    }}
                                  ></i>
                                  <i
                                    className="far fa-trash-alt me-2 d-print-none"
                                    onClick={() => {
                                      handleDeleteRow(
                                        index,
                                        "TreatmentRecommendations"
                                      );
                                    }}
                                  ></i>
                                  <FormSelect
                                    name="Recommendation"
                                    value={item.Recommendation}
                                    options={treatmentRecommendationOptions}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "TreatmentRecommendations"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(
                                        index,
                                        "TreatmentRecommendations"
                                      );
                                    }}
                                  ></i>
                                  {item.Recommendation}
                                </>
                              )}
                            </div>
                            <div className="col border-end py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <FormRadioButton
                                    name={`Deferred-${index}`}
                                    label="SAFY will be providing:"
                                    value={item.Deferred}
                                    options={yesNoOptions}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        value: e.currentTarget.value,
                                      };
                                      handleRowChange(
                                        e,
                                        index,
                                        "TreatmentRecommendations"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                  <FormRadioButton
                                    name={`Referred-${index}`}
                                    label="SAFY will be referring to another agency/individual:"
                                    value={item.Referred}
                                    options={yesNoOptions}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        value: e.currentTarget.value,
                                      };
                                      handleRowChange(
                                        e,
                                        index,
                                        "TreatmentRecommendations"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                  <FormRadioButton
                                    name={`Declined-${index}`}
                                    label="Declined by Client:"
                                    value={item.Declined}
                                    options={yesNoOptions}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        value: e.currentTarget.value,
                                      };
                                      handleRowChange(
                                        e,
                                        index,
                                        "TreatmentRecommendations"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                </>
                              ) : (
                                getTreatmentRecommendationStatus(item)
                              )}
                            </div>
                            <div className="col py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <FormInput
                                  name="Explain"
                                  value={item.Explain}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "TreatmentRecommendations"
                                    );
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Explain
                              )}
                            </div>
                          </div>
                        )
                      )}
                  </div>
                  {!formDisabled ? (
                    <button
                      className="btn btn-primary mb-2"
                      onClick={() => {
                        handleAddRow("TreatmentRecommendations", {
                          IsEditable: true,
                        });
                      }}
                    >
                      Add Recommendation
                    </button>
                  ) : null}
                  {errors.TreatmentRecommendations !== undefined && (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.TreatmentRecommendations}
                        </div>
                      </div>
                    </div>
                  )}
                </FieldTable>
                {data.PSDType["@value"] !==
                  "Psychiatric Diagnostic Evaluation" && (
                  <>
                    <FormRadioButton
                      name="DiagnosisEducation"
                      label="Education regarding diagnosis provided to client/family:"
                      error={errors.DiagnosisEducation}
                      value={data.DiagnosisEducation["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormRadioButton
                      name="RecommendationEducation"
                      label="Education regarding recommendations provided to client/family:"
                      error={errors.RecommendationEducation}
                      value={data.RecommendationEducation["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormRadioButton
                      name="ClientAgrees"
                      label="Client in agreement with recommendations:"
                      error={errors.ClientAgrees}
                      value={data.ClientAgrees["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormRadioButton
                      name="FamilyAgress"
                      label="Family in agreement with recommendations:"
                      error={errors.FamilyAgress}
                      value={data.FamilyAgress["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </>
                )}
              </ExpandDiv>
              {showNarrative === "Show" ? (
                <div className="clinicalNarrative bg-white border p-2">
                  <label>
                    <b>Clinical Narrative Summary:</b>
                  </label>
                  <FormTextArea
                    name="ClinicalNarrativeSummary"
                    error={errors.ClinicalNarrativeSummary}
                    value={data.ClinicalNarrativeSummary["@value"]}
                    onChange={handleChange}
                    identical={compare("ClinicalNarrativeSummary")}
                    commentsHandler={handleComments}
                    rows="5"
                    placeholder="Include information on who the ongoing clinician will be, the date of their care planning session, and any other comments necessary for the clinical narrative summary..."
                  />
                </div>
              ) : null}
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default KYHFPSD;
